import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithCustomToken,
  sendEmailVerification,
  sendPasswordResetEmail,
  verifyBeforeUpdateEmail,
  reload
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  doc,
  deleteDoc,
  onSnapshot,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import {
  Plane, Package, Search, User, LogOut, MapPin,
  Calendar, Weight, Box, Trash2, ChevronRight, ChevronDown, ChevronUp, RefreshCw,
  AlertCircle, Globe, ShieldCheck, ArrowRight, LogIn,
  CheckCircle2, X, MessageSquare, Send, UserCircle2,
  BarChart3, Users, Mail, RefreshCcw, AlertTriangle,
  Sparkles, Info, DollarSign, EyeOff, Eye, Phone,
  Instagram, Facebook, MessageCircle, Lock, FileText,
  KeyRound, Ban, ShieldAlert, UserX, BadgeCheck,
  Accessibility, HeartHandshake, PersonStanding, Settings,
  Video, Download
} from 'lucide-react';

// ==========================================
// Firebase Configuration
// ==========================================
const getFirebaseConfig = () => {
  try {
    if (typeof window !== 'undefined' && window['__firebase_config']) {
      return JSON.parse(window['__firebase_config']);
    }
  } catch (e) {
    console.warn("Could not parse environment config, using default.");
  }
  return {
    apiKey: "AIzaSyDXW9hSq_yR6CQ0MwyT_dyrlMEOc8_j13U",
    authDomain: "flycarrier-1d477.firebaseapp.com",
    projectId: "flycarrier-1d477",
    storageBucket: "flycarrier-1d477.firebasestorage.app",
    messagingSenderId: "22382525558",
    appId: "1:22382525558:web:a5315465ea374f1740b357",
    measurementId: "G-GZC1R93P43"
  };
};

const firebaseConfig = getFirebaseConfig();
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Turns a display name into a safe Firebase Storage path segment (keeps
// Persian/Unicode letters — Storage paths are UTF-8 — but strips slashes
// and other characters that would otherwise create sub-folders or break
// the path).
const sanitizeForStoragePath = (name) => {
  const cleaned = (name || 'user').trim().replace(/[\/\\#\[\]?*:]+/g, '_').slice(0, 60);
  return cleaned || 'user';
};

// ==========================================
// Constants & Data — English + Persian
// ==========================================
// ==========================================
// 🖼️ Brand logo (TNAi / TORABINEJAD Ai) — embedded so the app is self-contained
// ==========================================
const LOGO_SRC = "data:image/png;base64,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";

const LANGUAGES = {
  'en': 'English',
  'fa': 'فارسی'
};

const COUNTRY_TO_LANG = {
  'IR': 'fa', 'AF': 'fa'
};

// Pricing units per package type
const PACKAGE_PRICING = {
  documents:    { unit: 'perPage', defaultUSD: 10 },
  fullSuitcase: { unit: 'perKg',   defaultUSD: 20 },
  pet:         { unit: 'perKg',   defaultUSD: 25 },
  food:        { unit: 'perKg',   defaultUSD: 8 },
  electronics: { unit: 'perKg',   defaultUSD: 15 },
  medicine:    { unit: 'perBox',  defaultUSD: 20 },
  cosmetics:   { unit: 'perKg',   defaultUSD: 12 },
  dishware:    { unit: 'perKg',   defaultUSD: 10 },
  toys:        { unit: 'perKg',   defaultUSD: 8 },
  clothes:     { unit: 'perKg',   defaultUSD: 7 },
  tobacco:     { unit: 'perBox',  defaultUSD: 15 },
  personalItems: { unit: 'perKg', defaultUSD: 10 },
  custom:      { unit: 'perItem', defaultUSD: 15 },
  companion:   { unit: 'perTrip', defaultUSD: 80 }
};

const COUNTRIES_AND_CITIES = {
  "Afghanistan": ["Kabul"],
  "Albania": ["Tirana"],
  "Algeria": ["Algiers", "Oran"],
  "Andorra": ["Andorra la Vella"],
  "Angola": ["Luanda"],
  "Antigua and Barbuda": ["Saint John's"],
  "Argentina": ["Buenos Aires", "Córdoba", "Rosario"],
  "Armenia": ["Yerevan"],
  "Australia": ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide"],
  "Austria": ["Vienna", "Salzburg", "Graz"],
  "Azerbaijan": ["Baku", "Ganja"],
  "Bahamas": ["Nassau"],
  "Bahrain": ["Manama"],
  "Bangladesh": ["Dhaka", "Chittagong", "Sylhet"],
  "Barbados": ["Bridgetown"],
  "Belarus": ["Minsk", "Gomel"],
  "Belgium": ["Brussels", "Antwerp", "Bruges"],
  "Belize": ["Belmopan"],
  "Benin": ["Cotonou", "Porto-Novo"],
  "Bhutan": ["Thimphu"],
  "Bolivia": ["La Paz", "Santa Cruz de la Sierra"],
  "Bosnia and Herzegovina": ["Sarajevo", "Banja Luka"],
  "Botswana": ["Gaborone", "Maun"],
  "Brazil": ["São Paulo", "Rio de Janeiro", "Brasília", "Salvador", "Recife"],
  "Brunei": ["Bandar Seri Begawan"],
  "Bulgaria": ["Sofia", "Plovdiv"],
  "Burkina Faso": ["Ouagadougou"],
  "Burundi": ["Bujumbura", "Gitega"],
  "Cabo Verde": ["Praia"],
  "Cambodia": ["Phnom Penh", "Siem Reap"],
  "Cameroon": ["Yaoundé", "Douala"],
  "Canada": ["Toronto", "Vancouver", "Montreal", "Calgary", "Ottawa", "Halifax"],
  "Central African Republic": ["Bangui"],
  "Chad": ["N'Djamena"],
  "Chile": ["Santiago", "Valparaíso", "Concepción"],
  "China": ["Beijing", "Shanghai", "Guangzhou", "Shenzhen", "Chengdu", "Xi'an", "Hangzhou"],
  "Colombia": ["Bogotá", "Medellín", "Cali", "Cartagena"],
  "Comoros": ["Moroni"],
  "Congo (Congo-Brazzaville)": ["Brazzaville", "Pointe-Noire"],
  "Costa Rica": ["San José", "Liberia"],
  "Croatia": ["Zagreb", "Split", "Dubrovnik"],
  "Cuba": ["Havana", "Santiago de Cuba"],
  "Cyprus": ["Nicosia", "Limassol"],
  "Czechia": ["Prague", "Brno"],
  "Democratic Republic of the Congo": ["Kinshasa", "Lubumbashi"],
  "Denmark": ["Copenhagen", "Aarhus", "Billund"],
  "Djibouti": ["Djibouti City"],
  "Dominica": ["Roseau"],
  "Dominican Republic": ["Santo Domingo", "Punta Cana", "Santiago"],
  "Ecuador": ["Quito", "Guayaquil", "Cuenca"],
  "Egypt": ["Cairo", "Alexandria", "Sharm El Sheikh", "Luxor"],
  "El Salvador": ["San Salvador"],
  "Equatorial Guinea": ["Malabo", "Bata"],
  "Eritrea": ["Asmara"],
  "Estonia": ["Tallinn", "Tartu"],
  "Eswatini": ["Mbabane", "Manzini"],
  "Ethiopia": ["Addis Ababa", "Dire Dawa"],
  "Fiji": ["Suva", "Nadi"],
  "Finland": ["Helsinki", "Tampere"],
  "France": ["Paris", "Lyon", "Marseille", "Nice", "Toulouse", "Bordeaux"],
  "Gabon": ["Libreville"],
  "Gambia": ["Banjul"],
  "Georgia": ["Tbilisi", "Batumi"],
  "Germany": ["Berlin", "Frankfurt", "Munich", "Hamburg", "Cologne", "Düsseldorf", "Stuttgart"],
  "Ghana": ["Accra", "Kumasi"],
  "Greece": ["Athens", "Thessaloniki", "Heraklion"],
  "Grenada": ["St. George's"],
  "Guatemala": ["Guatemala City"],
  "Guinea": ["Conakry"],
  "Guinea-Bissau": ["Bissau"],
  "Guyana": ["Georgetown"],
  "Haiti": ["Port-au-Prince"],
  "Honduras": ["Tegucigalpa", "San Pedro Sula"],
  "Hungary": ["Budapest", "Debrecen"],
  "Iceland": ["Reykjavik"],
  "India": ["Delhi", "Mumbai", "Bangalore", "Kolkata", "Chennai", "Hyderabad", "Pune"],
  "Indonesia": ["Jakarta", "Bali (Denpasar)", "Surabaya", "Bandung", "Medan"],
  "Iran": ["Tehran", "Mashhad", "Isfahan", "Shiraz", "Tabriz"],
  "Iraq": ["Baghdad", "Erbil", "Basra"],
  "Ireland": ["Dublin", "Cork"],
  "Israel": ["Tel Aviv", "Jerusalem", "Haifa"],
  "Italy": ["Rome", "Milan", "Naples", "Turin", "Venice", "Florence"],
  "Jamaica": ["Kingston", "Montego Bay"],
  "Japan": ["Tokyo", "Osaka", "Kyoto", "Nagoya", "Fukuoka", "Sapporo"],
  "Jordan": ["Amman", "Aqaba"],
  "Kazakhstan": ["Nur-Sultan", "Almaty"],
  "Kenya": ["Nairobi", "Mombasa"],
  "Kiribati": ["Tarawa"],
  "Kuwait": ["Kuwait City"],
  "Kyrgyzstan": ["Bishkek", "Osh"],
  "Laos": ["Vientiane", "Luang Prabang"],
  "Latvia": ["Riga", "Daugavpils"],
  "Lebanon": ["Beirut"],
  "Lesotho": ["Maseru"],
  "Liberia": ["Monrovia"],
  "Libya": ["Tripoli", "Benghazi"],
  "Liechtenstein": ["Vaduz"],
  "Lithuania": ["Vilnius", "Kaunas"],
  "Luxembourg": ["Luxembourg City"],
  "Madagascar": ["Antananarivo"],
  "Malawi": ["Lilongwe", "Blantyre"],
  "Malaysia": ["Kuala Lumpur", "Penang", "Johor Bahru", "Kota Kinabalu"],
  "Maldives": ["Malé"],
  "Mali": ["Bamako"],
  "Malta": ["Valletta"],
  "Marshall Islands": ["Majuro"],
  "Mauritania": ["Nouakchott"],
  "Mauritius": ["Port Louis"],
  "Mexico": ["Mexico City", "Cancún", "Guadalajara", "Monterrey"],
  "Micronesia": ["Palikir"],
  "Moldova": ["Chișinău"],
  "Monaco": ["Monaco"],
  "Mongolia": ["Ulaanbaatar"],
  "Montenegro": ["Podgorica", "Kotor"],
  "Morocco": ["Casablanca", "Rabat", "Marrakesh", "Tangier"],
  "Mozambique": ["Maputo"],
  "Myanmar": ["Yangon", "Mandalay"],
  "Namibia": ["Windhoek"],
  "Nauru": ["Yaren"],
  "Nepal": ["Kathmandu", "Pokhara"],
  "Netherlands": ["Amsterdam", "Rotterdam", "The Hague", "Eindhoven"],
  "New Zealand": ["Auckland", "Wellington", "Christchurch"],
  "Nicaragua": ["Managua"],
  "Niger": ["Niamey"],
  "Nigeria": ["Lagos", "Abuja", "Port Harcourt"],
  "North Korea": ["Pyongyang"],
  "North Macedonia": ["Skopje"],
  "Norway": ["Oslo", "Bergen", "Stavanger"],
  "Oman": ["Muscat", "Salalah"],
  "Pakistan": ["Karachi", "Lahore", "Islamabad", "Peshawar"],
  "Palau": ["Ngerulmud"],
  "Palestine": ["Ramallah", "Gaza"],
  "Panama": ["Panama City"],
  "Papua New Guinea": ["Port Moresby"],
  "Paraguay": ["Asunción"],
  "Peru": ["Lima", "Cusco", "Arequipa"],
  "Philippines": ["Manila", "Cebu", "Davao"],
  "Poland": ["Warsaw", "Kraków", "Gdańsk", "Wrocław"],
  "Portugal": ["Lisbon", "Porto", "Faro"],
  "Qatar": ["Doha"],
  "Romania": ["Bucharest", "Cluj-Napoca", "Timișoara"],
  "Russia": ["Moscow", "Saint Petersburg", "Sochi", "Novosibirsk", "Yekaterinburg"],
  "Rwanda": ["Kigali"],
  "Saint Kitts and Nevis": ["Basseterre"],
  "Saint Lucia": ["Castries"],
  "Saint Vincent and the Grenadines": ["Kingstown"],
  "Samoa": ["Apia"],
  "San Marino": ["San Marino"],
  "Sao Tome and Principe": ["São Tomé"],
  "Saudi Arabia": ["Riyadh", "Jeddah", "Mecca", "Medina", "Dammam"],
  "Senegal": ["Dakar"],
  "Serbia": ["Belgrade", "Novi Sad"],
  "Seychelles": ["Victoria"],
  "Sierra Leone": ["Freetown"],
  "Singapore": ["Singapore"],
  "Slovakia": ["Bratislava", "Košice"],
  "Slovenia": ["Ljubljana", "Maribor"],
  "Solomon Islands": ["Honiara"],
  "Somalia": ["Mogadishu"],
  "South Africa": ["Johannesburg", "Cape Town", "Durban", "Pretoria"],
  "South Korea": ["Seoul", "Busan", "Incheon", "Daegu"],
  "South Sudan": ["Juba"],
  "Spain": ["Madrid", "Barcelona", "Valencia", "Seville", "Bilbao", "Málaga"],
  "Sri Lanka": ["Colombo", "Kandy"],
  "Sudan": ["Khartoum"],
  "Suriname": ["Paramaribo"],
  "Sweden": ["Stockholm", "Gothenburg", "Malmö"],
  "Switzerland": ["Zurich", "Geneva", "Basel", "Bern"],
  "Syria": ["Damascus", "Aleppo"],
  "Tajikistan": ["Dushanbe"],
  "Tanzania": ["Dar es Salaam", "Zanzibar", "Arusha"],
  "Thailand": ["Bangkok", "Phuket", "Chiang Mai", "Pattaya"],
  "Timor-Leste": ["Dili"],
  "Togo": ["Lomé"],
  "Tonga": ["Nukuʻalofa"],
  "Trinidad and Tobago": ["Port of Spain"],
  "Tunisia": ["Tunis", "Sousse"],
  "Turkey": ["Istanbul", "Ankara", "Izmir", "Antalya", "Adana"],
  "Turkmenistan": ["Ashgabat"],
  "Tuvalu": ["Funafuti"],
  "Uganda": ["Kampala"],
  "Ukraine": ["Kyiv", "Lviv", "Odesa"],
  "United Arab Emirates": ["Dubai", "Abu Dhabi", "Sharjah"],
  "United Kingdom": ["London", "Manchester", "Birmingham", "Glasgow", "Edinburgh"],
  "United States": ["New York", "Los Angeles", "Chicago", "Houston", "Miami", "Atlanta", "Boston", "Seattle", "San Francisco", "Dallas"],
  "Uruguay": ["Montevideo"],
  "Uzbekistan": ["Tashkent", "Samarkand"],
  "Vanuatu": ["Port Vila"],
  "Vatican City": ["Vatican City"],
  "Venezuela": ["Caracas", "Maracaibo"],
  "Vietnam": ["Hanoi", "Ho Chi Minh City", "Da Nang", "Hai Phong"],
  "Yemen": ["Sana'a", "Aden"],
  "Zambia": ["Lusaka"],
  "Zimbabwe": ["Harare", "Bulawayo"]
};

// Package types now INCLUDE the travel companion as a single item.
// Labels are resolved via translation using labelKey, so Persian shows Persian.
const PACKAGE_TYPES = [
  { id: "pet",         labelKey: "pkgPet" },
  { id: "documents",   labelKey: "pkgDocuments" },
  { id: "fullSuitcase", labelKey: "pkgFullSuitcase", hasQuantity: true },
  { id: "food",        labelKey: "pkgFood" },
  { id: "electronics", labelKey: "pkgElectronics" },
  { id: "medicine",    labelKey: "pkgMedicine" },
  { id: "cosmetics",   labelKey: "pkgCosmetics" },
  { id: "dishware",    labelKey: "pkgDishware" },
  { id: "toys",        labelKey: "pkgToys" },
  { id: "clothes",     labelKey: "pkgClothes" },
  { id: "tobacco",     labelKey: "pkgTobacco" },
  { id: "personalItems", labelKey: "pkgPersonalItems" },
  { id: "custom",      labelKey: "pkgCustom", isCustom: true },
  { id: "companion",   labelKey: "pkgCompanion", isCompanion: true }
];

// ==========================================
// TRANSLATIONS
// ==========================================
const TRANSLATIONS = {
  en: {
    appTitle: "FlyCarrier",
    login: "Login",
    signup: "Sign Up",
    loginBtn: "Login / Sign Up",
    welcome: "Welcome Back",
    welcomeSub: "Login to manage your shipments or trips",
    joinSub: "Join the community of senders and travelers",
    email: "Email Address",
    password: "Password",
    haveAccount: "Already have an account?",
    noAccount: "Don't have an account?",
    authFailed: "Authentication failed. Please check your credentials.",
    roleSender: "Sender",
    roleTraveler: "Traveler",
    chooseRole: "I want to be a...",
    originCountry: "Origin Country",
    originCity: "Origin City",
    destCountry: "Destination Country",
    destCity: "Destination City",
    selectCountry: "Select Country...",
    selectCity: "Select City...",
    next: "Next Step",
    back: "Go Back",
    saveProfile: "Save & Continue",
    editProfile: "Edit Profile",
    createProfile: "Complete Your Profile",
    cancel: "Cancel",
    pauseListing: "Pause Listing",
    resumeListing: "Resume Listing",
    pauseListingHint: "Temporarily hide your listing from other users. You can resume anytime.",
    listingPausedBadge: "Paused",
    listingPausedBanner: "Your listing is paused and hidden from other users.",
    confirmPauseTitle: "Pause your listing?",
    confirmPauseDesc: "Other users won't be able to find or match with you until you resume it.",
    confirmResumeTitle: "Resume your listing?",
    confirmResumeDesc: "Your listing will become visible to other users again.",
    deleteProfileAction: "Delete Listing",
    confirmDeleteProfileTitle: "Delete your listing?",
    confirmDeleteProfileDesc: "Your trip details will be removed and your listing will no longer be shown to other users. Your account, login, and messages stay intact — you can create a new listing anytime.",
    packageDetails: "Package Details",
    flightDetails: "Flight & Capacity",
    whatSending: "Item Type",
    weight: "Weight (kg)",
    weightOptional: "Weight (kg) — optional",
    quantityOptional: "Quantity — optional",
    weightEachOptional: "Weight each (kg) — optional",
    flightDate: "Departure Date",
    flightTime: "Departure Time",
    flightNumber: "Flight Number",
    hasTicketQuestion: "Have you already bought your ticket?",
    hasTicketYes: "Yes, I have my ticket",
    hasTicketNo: "Not yet",
    approxDate: "Approximate Date",
    approxDateHint: "You don't have a ticket yet — just tell us the month you expect to fly.",
    acceptedItems: "Items I can carry",
    contactInfo: "Contact Details",
    fullName: "Full Name",
    yourPhone: "Mobile Number",
    recipientPhone: "Recipient's Mobile",
    recipientFullName: "Recipient's Full Name",
    dashboard: "Dashboard",
    findMatches: "Available Matches",
    searchBtn: "Search Matches",
    searching: "Searching...",
    noMatches: "No matches found. Try adjusting your criteria.",
    showPhone: "Reveal Contact",
    sendMessage: "Send Message",
    messages: "Messages",
    inbox: "Inbox",
    typeMessage: "Type your message...",
    send: "Send",
    edit: "Edit",
    footerRights: "All rights reserved.",
    footerCredit: "Crafted by",
    footerTelegramBot: "Open Telegram Bot",
    footerInstagram: "Open Instagram Page",
    landingTitle: "Global Shipping, Simplified.",
    landingSubtitle: "Connect with travelers to send parcels faster and cheaper, or earn money on your next trip.",
    getStarted: "Start Your Journey",
    features: "Why FlyCarrier?",
    forSendersTitle: "Send Anywhere",
    forSendersDesc: "Skip the courier queues. Send documents and gifts with verified travelers at a fraction of the cost.",
    forTravelersTitle: "Earn While Flying",
    forTravelersDesc: "Turn your extra luggage space into cash. Subsidize your travel costs effortlessly.",
    secureTitle: "Trust & Safety",
    secureDesc: "Verified profiles and a secure handshake process ensure peace of mind for every shipment.",
    adminPanel: "Admin Panel",
    onlineUsers: "Online Users",
    totalUsers: "Total Users",
    newSignups: "New Signups",
    visitStatsTitle: "Site Visits",
    memberStatsTitle: "Members",
    statToday: "Today",
    statWeek: "This Week",
    statMonth: "This Month",
    statTotal: "All Time",
    publishToFooter: "Publish to footer",
    publishedToFooter: "Published — visible in footer",
    unpublish: "Unpublish",
    publishVisitsHint: "Show these visit numbers publicly in the site footer.",
    publishMembersHint: "Show the total member count publicly in the site footer.",
    statsPublishedNote: "Now visible to everyone in the footer.",
    statsUnpublishedNote: "Hidden from the public footer.",
    footerVisitsLabel: "Visits",
    footerMembersLabel: "Members",
    refreshStats: "Refresh",
    mainAdminOnly: "Main admin only",
    filterDate: "Filter by Date",
    startDate: "Start Date",
    endDate: "End Date",
    apply: "Apply",
    verifyTitle: "Verify Your Email",
    verifyDesc: "We've sent a verification link to your email address. Please click on the link to activate your account.",
    verifyBtn: "I have verified my email",
    emailVerifySettingTitle: "Email Verification on Signup",
    emailVerifySettingDesc: "When enabled, new users must verify their email address before they can use their account (current default flow). When disabled, users can start using the platform right after signing up — no email verification step.",
    emailVerifyStatusOn: "Enabled — verification required",
    emailVerifyStatusOff: "Disabled — verification not required",
    resendBtn: "Resend Verification Email",
    emailSent: "Email sent!",
    notVerifiedYet: "Email not verified yet. Please click the link in your email.",
    logout: "Logout",
    spamNotice: "Didn't receive the email? Please check your Spam or Junk folder. Sometimes verification emails end up there!",
    checkVerification: "Check Verification Status",
    optional: "Optional",
    socialMedia: "Social Media & Messaging (Optional)",
    socialDesc: "Share these so others can reach you on the apps you already use.",
    whatsapp: "WhatsApp Number",
    telegram: "Telegram Username or Phone",
    signal: "Signal Number",
    instagram: "Instagram Username",
    facebook: "Facebook Profile / Username",
    hidePhoneLabel: "Hide my phone number from public profile",
    hidePhoneHint: "When enabled, your phone is not shown on matches. People can still reach you via in-app messages.",
    phoneHidden: "Phone hidden by user",
    contactViaMessage: "Message to get contact",
    priceSection: "Suggested Price (Optional)",
    priceSectionDesc: "Set the price you're willing to offer or accept. This helps both sides agree faster.",
    yourOffer: "Your Offer",
    suggestedRate: "Your Rate",
    currency: "Currency",
    pricePlaceholder: "e.g. 25",
    perKg: "per kg",
    perPage: "per page",
    perBox: "per box",
    perItem: "per item",
    perTrip: "per trip",
    travelerRates: "Set your rate per item type",
    suggestedDefault: "Suggested baseline: ~",
    priceOffer: "Offer",
    priceRate: "Rate",
    termsTitle: "Terms & Safety Notice",
    termsIntro: "Before you sign up, please read this carefully. By creating an account on FlyCarrier you confirm that you have read, understood and agree to the following:",
    termsList: [
      "FlyCarrier is only a connection platform between senders and travelers. We are NOT a party to any agreement, transaction or shipment between users.",
      "We strongly recommend using only travelers who have been identity-verified by FlyCarrier (those with the blue verification badge). For unverified travelers you significantly increase your risk of fraud.",
      "Fraud risk is real. There are known scams in peer-to-peer shipping: scammers may take your package and never deliver it; some users ask for the shipping fee upfront and then block you; others post fake flight numbers or false names.",
      "Before sending anything, YOU are responsible for personally verifying the other person: ask for their full name, a government-issued photo ID, and their flight booking (PNR / e-ticket showing the flight number, date and passenger name). Cross-check the flight number on the airline's official website.",
      "Never transfer money in advance. Hand over the package and the agreed payment in person, at the airport or another safe public place, after you have verified the traveler's identity and ticket.",
      "You are responsible for following all customs laws and regulations in the origin and destination countries. Do not send or accept anything illegal, restricted, or dangerous (drugs, weapons, large amounts of cash, hazardous materials, etc.). Carrying someone else's package without inspecting it is at your own risk.",
      "For travel companion arrangements: FlyCarrier does not employ, train, license, or supervise companions. A companion is not a medical professional or a certified caregiver. If the passenger has medical needs, arrange proper professional care and inform the airline in advance. You accept full responsibility for any arrangement.",
      "FlyCarrier is not liable for any loss, damage, theft, delay, scam, financial loss, customs issue, legal trouble, injury or any other harm arising from your use of the platform or your interactions with other users.",
      "You use this platform entirely at your own risk."
    ],
    termsAccept: "I have read and I accept the Terms & Safety Notice above.",
    termsRequired: "You must accept the Terms & Safety Notice to create an account.",
    geoDetectInfo: "Language auto-selected based on your region. You can change it anytime from the top menu.",
    forgotPassword: "Forgot password?",
    forgotTitle: "Reset Your Password",
    forgotDesc: "Enter the email associated with your account. We'll send you a secure link to reset your password.",
    sendResetLink: "Send Reset Link",
    resetSent: "Reset link sent! Check your inbox (and Spam folder).",
    backToLogin: "Back to Login",
    changePassword: "Change Password",
    changePasswordHint: "We'll email you a secure password reset link.",
    resetLinkSent: "Password reset link sent to your email. Please check your inbox.",
    bannedTitle: "Account Suspended",
    bannedMsg: "Your account has been suspended by an administrator. If you believe this is a mistake, please contact support.",
    manageUsers: "Manage Users",
    searchUsersPlaceholder: "Search by email or name...",
    statusActive: "Active",
    statusBanned: "Banned",
    statusOnline: "Online",
    banAction: "Ban",
    unbanAction: "Unban",
    deleteAction: "Delete",
    confirmBanTitle: "Ban this user?",
    confirmBanDesc: "They will be blocked from using the platform until you unban them.",
    confirmDeleteTitle: "Delete this user's profile?",
    confirmDeleteDesc: "Their profile data will be removed from the database. Note: The user's authentication account remains and must be removed manually for full deletion.",
    confirmAction: "Confirm",
    noUsersFound: "No users found.",
    joined: "Joined",
    viewDetails: "View details",
    hideDetails: "Hide details",
    detailsProfileType: "Profile type",
    detailsNoProfile: "This user has not completed their profile yet.",
    detailsRoute: "Route",
    detailsContact: "Contact",
    detailsRecipient: "Recipient",
    detailsPackage: "Package to send",
    detailsFlight: "Flight",
    detailsAcceptedItems: "Accepted items",
    detailsRates: "Rates per item",
    detailsCompanionOffer: "Companion service offered",
    detailsCompanionNeed: "Needs companion",
    detailsSocial: "Social / messaging",
    detailsPhoneHidden: "Phone hidden from public",
    detailsNone: "None",
    detailsWeight: "Weight",
    detailsPreferredDate: "Preferred date",
    detailsFlightNumber: "Flight number",
    sortNewest: "Newest first",
    sortOldest: "Oldest first",
    sortLabel: "Sort by signup",
    verified: "Verified",
    verifyAction: "Verify",
    unverifyAction: "Remove Verification",
    verifiedBadgeTooltip: "Verified user — identity confirmed by FlyCarrier admins",
    confirmVerifyTitle: "Verify this user?",
    confirmVerifyDesc: "This user will receive a blue verification badge visible to everyone. Only verify after you have personally confirmed their identity.",
    confirmUnverifyTitle: "Remove verification?",
    confirmUnverifyDesc: "The blue verification badge will be removed from this user's profile.",
    preferredDate: "Preferred Delivery Date",
    preferredDateHint: "When would you like the package delivered? (optional, helps travelers find a match)",
    dateFilter: "Date Range",
    dateFilterHint: "Filter matches by date. Past dates are always excluded.",
    fromDate: "From",
    toDate: "To",
    anyDate: "Any date",
    pastDatesExcluded: "Past dates automatically excluded",
    clearDates: "Clear dates",
    contactAdmin: "Contact Admin",
    contactAdminDesc: "Send a message to one of our admins for support or to report an issue",
    selectAdmin: "Select an admin",
    noAdminsAvailable: "No admins available right now.",
    promoteToAdmin: "Promote to Sub-Admin",
    revokeAdmin: "Revoke Admin Access",
    managePermissions: "Manage Permissions",
    adminRoleLabel: "Admin Role",
    mainAdminBadge: "Main Admin",
    subAdminBadge: "Sub-Admin",
    permCanBan: "Can ban / unban users",
    permCanDelete: "Can delete user profiles",
    permCanVerify: "Can verify / unverify users",
    permsHeading: "What can this Sub-Admin do?",
    permsDescription: "Select the actions this sub-admin is allowed to perform. They will never be able to promote other admins or modify the Main Admin's account.",
    savePermissions: "Save Permissions",
    confirmPromoteTitle: "Promote to Sub-Admin?",
    confirmPromoteDesc: "This user will gain administrative privileges based on the permissions you grant.",
    confirmRevokeTitle: "Revoke admin access?",
    confirmRevokeDesc: "This user will lose all admin privileges and become a regular user again.",
    notAuthorized: "You do not have permission for this action.",
    perItemSpecs: "Per-item weight & rate",
    perItemSpecsDesc: "Set weight (optional) and your rate for each item type you accept.",
    accountSecurity: "Account Security",
    currentEmail: "Current Email (Username)",
    newEmail: "New Email Address",
    updateEmail: "Update Email",
    updateEmailDesc: "A verification link will be sent to the new email address. The change takes effect only after you click the link in that email.",
    emailUpdateSent: "Verification email sent! Open your new email inbox and click the link to complete the change.",
    emailUpdateError: "Could not update email. Please try again.",
    reauthRequired: "For security reasons, please sign out and sign back in, then try changing your email again.",
    sameEmailError: "The new email must be different from your current one.",
    invalidEmailError: "Please enter a valid email address.",
    displayName: "Display Name",
    displayNameHint: "This is the name other users see on your profile, in matches, and in messages.",
    updateName: "Update Name",
    nameUpdated: "Display name updated successfully.",
    requestVerification: "Request Identity Verification",
    verificationNavLabel: "Identity Verification (Optional)",
    verificationNavHint: "* You haven't verified your identity yet. Click to get started. (Recommended if you're a traveler.)",
    verificationBadge: "Get Blue Verification Badge",
    verificationOptional: "Optional — but highly recommended to build trust with senders",
    verificationFormTitle: "Identity Verification Request",
    verificationFormDesc: "Upload a passport photo, a short passport video and a selfie video, then your phone number and full name. Our admin reviews everything right here — no need to leave the site.",
    verificationStepLabel: "Step {n} of 5",
    verificationStep1Title: "Passport photo",
    verificationStep1Desc: "Upload a clear photo or scan of your passport's info page. All details (name, number, photo, dates) must be sharp and readable.",
    verificationStep2Title: "Passport video",
    verificationStep2Desc: "Upload a short (~10 second) video clearly showing your passport's info page — steady hands, good lighting, all details readable.",
    verificationStep3Title: "Selfie video",
    verificationStep3Desc: "Hold your passport's info page just under your chin, so that both your face and every detail on the passport page are sharp and inside the same frame (see the picture below). While recording, say clearly:",
    verificationSelfieSampleText: "\"I am [your full legal name], and on {date} I am requesting identity verification on the FlyCarrier platform.\"",
    verificationStep4Title: "Phone number",
    verificationStep4Desc: "Send a real mobile number where we can reach you.",
    verificationStep5Title: "Full legal name",
    verificationStep5Desc: "As shown on your passport.",
    verificationChoosePhoto: "Choose photo",
    verificationChooseVideo: "Choose video",
    verificationChangeFile: "Change file",
    verificationFileRequired: "Please choose a file to continue.",
    verificationInvalidPhoto: "Please choose an image file.",
    verificationInvalidVideo: "Please choose a video file.",
    verificationUploading: "Uploading your documents…",
    downloadFile: "Download",
    deleteFile: "Delete from storage",
    confirmDeleteVerificationFile: "Permanently delete this file from Firebase Storage? This cannot be undone.",
    verificationFullName: "Full Legal Name (as on passport)",
    submitVerification: "Submit Verification Request",
    verificationSubmitted: "Your verification request has been submitted. Our admin will review it and message you here as soon as a decision is made.",
    verificationPending: "Verification Pending",
    verificationPendingDesc: "Your request is under review. We'll notify you here as soon as a decision is made.",
    verificationApproved: "You are verified! ✓",
    verificationRejected: "Verification request was not approved.",
    viewVerificationRequests: "Verification Requests",
    verificationRequestsTab: "Verification Requests",
    verificationStatus: "Status",
    retry: "Retry",
    statusPending: "Pending",
    statusApproved: "Approved",
    statusRejected: "Rejected",
    approveRequest: "Approve & Verify",
    rejectRequest: "Reject",
    messageApplicant: "Message Applicant",
    adminNote: "Admin Note",
    adminNoteHint: "Optional note recorded with this decision (not shown to user).",
    noVerificationRequests: "No verification requests yet.",
    senderSafetyTitle: "Important Safety Notice",
    senderSafetyBody: "For your safety, we strongly recommend choosing travelers with the blue verification badge. Be aware that scammers may contact you from outside this platform and impersonate verified travelers to gain your trust. Always conduct all initial conversations inside the FlyCarrier in-app chat so you can confirm the identity and the badge of the person you are dealing with. Any issues that arise from arrangements made outside of this platform are entirely your own responsibility.",
    dismiss: "Dismiss",
    fillRequiredFields: "Please complete the required fields.",
    saveFailed: "Could not save. Please try again.",
    accountSettings: "Account Settings",
    accountSettingsDesc: "Manage your login email, password and display name",
    accountSettingsBtn: "Account Settings",
    telegramAccountTitle: "Telegram Account",
    telegramAccountDesc: "You joined via the Telegram bot, so there is no email or password to manage here. Your account is linked to your Telegram profile.",
    whatSendingMulti: "What are you sending? (select one or more)",
    senderPickAtLeastOne: "Please select at least one item to send.",
    senderPerItemSpecs: "Weight & price per item",
    senderPerItemSpecsDesc: "Set an optional weight and your offered price for each item. Weight is optional and only used to match travelers who can carry it.",
    // Package labels
    pkgPet: "Pet",
    pkgDocuments: "Documents",
    pkgFullSuitcase: "Full Suitcase",
    pkgFood: "Food",
    pkgElectronics: "Electronics",
    pkgMedicine: "Medicine",
    pkgCosmetics: "Cosmetics",
    pkgDishware: "Dishware",
    pkgToys: "Toys",
    pkgClothes: "Clothes",
    pkgTobacco: "Tobacco",
    pkgPersonalItems: "Personal Items",
    pkgCustom: "Other (add your own)",
    customItemLabel: "Describe this item",
    customItemPlaceholder: "e.g. used laptop charger",
    customItemRequired: "Please describe this item.",
    pkgCompanion: "Travel Companion",
    // Travel Companion (as an item)
    companionService: "Travel Companion",
    companionItemDesc: "You can accompany and look after another passenger during the trip.",
    companionSenderItemDesc: "Find a traveler to accompany a passenger during the trip.",
    companionTypeQuestion: "Passenger mobility",
    companionTravelerAcceptTitle: "Which passengers can you accompany?",
    companionSenderNeedTitle: "What is your passenger's mobility?",
    companionWheelchair: "Wheelchair passenger",
    companionWalking: "Walking passenger (mobile)",
    companionWheelchairShort: "Wheelchair",
    companionWalkingShort: "Walking",
    companionAcceptWheelchair: "I can accompany a wheelchair passenger",
    companionAcceptWalking: "I can accompany a walking (mobile) passenger",
    companionNeedWheelchair: "My passenger uses a wheelchair",
    companionNeedWalking: "My passenger can walk (mobile)",
    companionPickAtLeastOne: "Select at least one passenger type you can accompany.",
    companionSenderPickOne: "Select your passenger's mobility.",
    companionOffered: "Companion available",
    companionForWheelchair: "Accompanies wheelchair passengers",
    companionForWalking: "Accompanies walking passengers",
    companionForBoth: "Accompanies wheelchair & walking passengers",
    companionNeeded: "Needs companion",
    companionNeededWheelchair: "Needs companion (wheelchair)",
    companionNeededWalking: "Needs companion (walking)",
    companionMatchNote: "This traveler offers travel companion service.",
    companionSafetyNote: "Companions are volunteers from the community, not medical staff or certified caregivers. Always inform the airline about special assistance needs and arrange professional medical care where required.",
    companionRate: "Companion fee (per trip)",
    // Telegram members
    telegramTab: "Telegram Members",
    telegramMembersTitle: "Telegram Members",
    telegramMembersDesc: "Users who joined via the Telegram bot. No email account is created for them.",
    telegramBadge: "Telegram",
    hasProfile: "Profile created",
    noProfile: "No profile yet",
    telegramId: "Telegram ID",
    telegramUsername: "Username",
    noTelegramMembers: "No Telegram members yet.",
    viaTelegram: "via Telegram",
    signupSourceWeb: "Web",
    signupSourceTelegram: "Telegram"
  },
  fa: {
    appTitle: "FlyCarrier",
    login: "ورود به حساب",
    signup: "ثبت نام",
    loginBtn: "ورود / عضویت",
    welcome: "خوش آمدید",
    welcomeSub: "برای مدیریت سفرها یا بسته‌ها وارد شوید",
    joinSub: "به جامعه جهانی مسافران و فرستندگان بپیوندید",
    email: "آدرس ایمیل",
    password: "رمز عبور",
    haveAccount: "قبلاً ثبت نام کرده‌اید؟",
    noAccount: "هنوز حساب ندارید؟",
    authFailed: "اطلاعات ورود نادرست است",
    roleSender: "فرستنده",
    roleTraveler: "مسافر",
    chooseRole: "قصد دارید چه کاری انجام دهید؟",
    originCountry: "کشور مبدأ",
    originCity: "شهر مبدأ",
    destCountry: "کشور مقصد",
    destCity: "شهر مقصد",
    selectCountry: "انتخاب کشور...",
    selectCity: "انتخاب شهر...",
    next: "مرحله بعد",
    back: "بازگشت",
    saveProfile: "ذخیره و ادامه",
    editProfile: "ویرایش پروفایل",
    createProfile: "تکمیل پروفایل کاربری",
    cancel: "انصراف",
    pauseListing: "توقف موقت آگهی",
    resumeListing: "فعال‌سازی مجدد آگهی",
    pauseListingHint: "آگهی شما به‌طور موقت از دید سایر کاربران مخفی می‌شود. هر زمان که خواستید می‌توانید دوباره فعالش کنید.",
    listingPausedBadge: "متوقف شده",
    listingPausedBanner: "آگهی شما در حال حاضر متوقف شده و برای سایر کاربران نمایش داده نمی‌شود.",
    confirmPauseTitle: "آگهی شما متوقف شود؟",
    confirmPauseDesc: "تا زمانی که دوباره فعالش نکنید، سایر کاربران نمی‌توانند شما را پیدا کرده یا با شما مچ شوند.",
    confirmResumeTitle: "آگهی شما دوباره فعال شود؟",
    confirmResumeDesc: "آگهی شما دوباره برای سایر کاربران قابل مشاهده خواهد بود.",
    deleteProfileAction: "حذف آگهی",
    confirmDeleteProfileTitle: "آگهی شما حذف شود؟",
    confirmDeleteProfileDesc: "اطلاعات سفر شما پاک می‌شود و آگهی‌تان دیگر برای سایر کاربران نمایش داده نخواهد شد. حساب کاربری، ورود و پیام‌های شما دست‌نخورده باقی می‌مانند و هر زمان می‌توانید آگهی جدیدی بسازید.",
    packageDetails: "مشخصات مرسوله",
    flightDetails: "اطلاعات پرواز و ظرفیت",
    whatSending: "نوع کالا",
    weight: "وزن (کیلوگرم)",
    weightOptional: "وزن (کیلوگرم) — اختیاری",
    quantityOptional: "تعداد — اختیاری",
    weightEachOptional: "وزن هر کدام (کیلوگرم) — اختیاری",
    flightDate: "تاریخ پرواز",
    flightTime: "ساعت پرواز",
    flightNumber: "شماره پرواز",
    hasTicketQuestion: "آیا بلیط پرواز خود را تهیه کرده‌اید؟",
    hasTicketYes: "بله، بلیط تهیه کرده‌ام",
    hasTicketNo: "هنوز بلیط تهیه نکرده‌ام",
    approxDate: "تاریخ حدودی پرواز",
    approxDateHint: "چون هنوز بلیط تهیه نکرده‌اید، کافیست ماه و سال حدودی پرواز خود را مشخص کنید.",
    acceptedItems: "کالاهای قابل حمل",
    contactInfo: "اطلاعات تماس",
    fullName: "نام و نام خانوادگی",
    yourPhone: "شماره موبایل شما",
    recipientPhone: "شماره موبایل گیرنده",
    recipientFullName: "نام کامل گیرنده",
    dashboard: "پیشخوان",
    findMatches: "پیشنهادهای موجود",
    searchBtn: "جستجوی هوشمند",
    searching: "در حال جستجو...",
    noMatches: "موردی یافت نشد. لطفاً معیارهای جستجو را تغییر دهید.",
    showPhone: "نمایش تماس",
    sendMessage: "ارسال پیام",
    messages: "پیام‌ها",
    inbox: "صندوق دریافت",
    typeMessage: "پیام خود را بنویسید...",
    send: "ارسال",
    edit: "ویرایش",
    footerRights: "تمامی حقوق محفوظ است.",
    footerCredit: "طراحی و توسعه توسط",
    footerTelegramBot: "ورود به ربات تلگرامی",
    footerInstagram: "ورود به صفحه اینستاگرام",
    landingTitle: "ارسال جهانی، ساده و سریع",
    landingSubtitle: "با مسافران هم‌مسیر شوید تا بسته‌هایتان را ارزان‌تر بفرستید یا از فضای خالی چمدانتان درآمد کسب کنید.",
    getStarted: "همین حالا شروع کنید",
    features: "چرا FlyCarrier؟",
    forSendersTitle: "ارسال به هر کجا",
    forSendersDesc: "بدون صف‌های طولانی پست، مدارک و هدایا را با مسافران تایید شده و با هزینه‌ای کمتر ارسال کنید.",
    forTravelersTitle: "کسب درآمد از سفر",
    forTravelersDesc: "فضای خالی چمدان خود را به پول نقد تبدیل کنید و بخشی از هزینه‌های سفرتان را جبران نمایید.",
    secureTitle: "امنیت و اعتماد",
    secureDesc: "پروفایل‌های تایید شده و فرآیند امن برای آسودگی خاطر شما در هر مرحله.",
    adminPanel: "پنل مدیریت",
    onlineUsers: "کاربران آنلاین",
    totalUsers: "کل کاربران",
    newSignups: "ثبت‌نام‌های جدید",
    visitStatsTitle: "بازدید سایت",
    memberStatsTitle: "اعضا",
    statToday: "امروز",
    statWeek: "این هفته",
    statMonth: "این ماه",
    statTotal: "کل",
    publishToFooter: "انتشار در فوتر",
    publishedToFooter: "منتشر شد — در فوتر نمایش داده می‌شود",
    unpublish: "لغو انتشار",
    publishVisitsHint: "نمایش عمومی آمار بازدید در فوتر سایت.",
    publishMembersHint: "نمایش عمومی تعداد کل اعضا در فوتر سایت.",
    statsPublishedNote: "اکنون برای همه در فوتر قابل مشاهده است.",
    statsUnpublishedNote: "از فوتر عمومی پنهان شد.",
    footerVisitsLabel: "بازدید",
    footerMembersLabel: "اعضا",
    refreshStats: "بروزرسانی",
    mainAdminOnly: "فقط مدیر اصلی",
    filterDate: "فیلتر تاریخ",
    startDate: "تاریخ شروع",
    endDate: "تاریخ پایان",
    apply: "اعمال فیلتر",
    verifyTitle: "تأیید آدرس ایمیل",
    verifyDesc: "یک لینک فعال‌سازی به ایمیل شما ارسال شد. لطفاً روی لینک کلیک کنید تا حساب شما فعال شود.",
    verifyBtn: "ایمیل را تأیید کردم",
    emailVerifySettingTitle: "تأیید ایمیل هنگام ثبت‌نام",
    emailVerifySettingDesc: "در صورت فعال بودن، کاربران جدید باید پیش از استفاده از حساب کاربری، ایمیل خود را تأیید کنند (روال فعلی). در صورت غیرفعال بودن، کاربران بلافاصله پس از ثبت‌نام می‌توانند از پلتفرم استفاده کنند و نیازی به تأیید ایمیل نیست.",
    emailVerifyStatusOn: "فعال — تأیید ایمیل الزامی است",
    emailVerifyStatusOff: "غیرفعال — تأیید ایمیل الزامی نیست",
    resendBtn: "ارسال مجدد ایمیل تأیید",
    emailSent: "ایمیل ارسال شد!",
    notVerifiedYet: "ایمیل هنوز تأیید نشده است. لطفاً روی لینک ارسال شده کلیک کنید.",
    logout: "خروج",
    spamNotice: "ایمیلی دریافت نکردید؟ لطفاً پوشه اسپم (Spam) یا هرزنامه (Junk) ایمیل خود را بررسی کنید. گاهی اوقات ایمیل تأیید در این پوشه‌ها قرار می‌گیرد!",
    checkVerification: "بررسی وضعیت تأیید",
    optional: "اختیاری",
    socialMedia: "شبکه‌های اجتماعی و پیام‌رسان‌ها (اختیاری)",
    socialDesc: "این موارد را وارد کنید تا دیگران بتوانند از طریق پیام‌رسان‌های مورد علاقه‌شان با شما در ارتباط باشند.",
    whatsapp: "شماره واتساپ",
    telegram: "نام کاربری یا شماره تلگرام",
    signal: "شماره سیگنال",
    instagram: "آی‌دی اینستاگرام",
    facebook: "پروفایل / نام کاربری فیسبوک",
    hidePhoneLabel: "شماره تماس من از پروفایل عمومی مخفی شود",
    hidePhoneHint: "در صورت فعال بودن، شماره موبایل شما برای دیگران قابل مشاهده نخواهد بود. دیگران فقط می‌توانند از طریق چت داخل سایت با شما در ارتباط باشند.",
    phoneHidden: "شماره تماس توسط کاربر مخفی شده",
    contactViaMessage: "برای دریافت تماس پیام دهید",
    priceSection: "مبلغ پیشنهادی (اختیاری)",
    priceSectionDesc: "مبلغی که حاضر هستید بپردازید یا دریافت کنید را مشخص کنید. این کار توافق را سریع‌تر می‌کند.",
    yourOffer: "پیشنهاد شما",
    suggestedRate: "تعرفه شما",
    currency: "ارز",
    pricePlaceholder: "مثلاً 25",
    perKg: "به ازای هر کیلوگرم",
    perPage: "به ازای هر صفحه",
    perBox: "به ازای هر باکس",
    perItem: "به ازای هر مورد",
    perTrip: "برای کل سفر",
    travelerRates: "تعرفه خود را برای هر نوع کالا تعیین کنید",
    suggestedDefault: "مبلغ پایه پیشنهادی: حدود ",
    priceOffer: "پیشنهاد",
    priceRate: "تعرفه",
    termsTitle: "قوانین و هشدارهای امنیتی",
    termsIntro: "قبل از ثبت‌نام لطفاً موارد زیر را با دقت بخوانید. با ساخت حساب کاربری در FlyCarrier تأیید می‌کنید که این موارد را خوانده، درک کرده و پذیرفته‌اید:",
    termsList: [
      "FlyCarrier صرفاً یک پلتفرم ارتباطی بین فرستندگان و مسافران است. ما هیچ نقشی در توافق، معامله یا ارسال بسته بین کاربران نداریم.",
      "اکیداً توصیه می‌شود فقط از مسافرانی استفاده کنید که توسط FlyCarrier احراز هویت شده‌اند و نشان آبی تأیید را دارند. در صورت استفاده از مسافران احراز هویت نشده، ریسک کلاهبرداری به‌طور قابل‌توجهی افزایش می‌یابد.",
      "خطر کلاهبرداری واقعی است. در پلتفرم‌های ارسال بسته با مسافر، کلاهبرداری‌های شناخته‌شده‌ای وجود دارد: ممکن است فردی بسته شما را تحویل بگیرد و هرگز به مقصد نرساند؛ برخی هزینه ارسال را در ابتدا درخواست می‌کنند و پس از دریافت پول، شما را بلاک می‌کنند؛ برخی دیگر شماره پرواز جعلی یا نام و هویت ساختگی ارائه می‌دهند.",
      "قبل از ارسال هر چیزی، مسئولیت بررسی شخصی طرف مقابل با خودِ شماست: نام و نام خانوادگی کامل، کارت شناسایی معتبر دولتی (گذرنامه/کارت ملی) و رزرو پرواز (PNR یا بلیط الکترونیکی شامل شماره پرواز، تاریخ و نام مسافر) را از او بخواهید. شماره پرواز را در سایت رسمی شرکت هواپیمایی استعلام کنید.",
      "هرگز قبل از تحویل حضوری، هیچ مبلغی واریز نکنید. تحویل بسته و پرداخت مبلغ توافقی باید به صورت حضوری و در محل امن (ترجیحاً فرودگاه) و پس از تأیید هویت و بلیط مسافر انجام شود.",
      "مسئولیت رعایت کلیه قوانین گمرکی کشورهای مبدأ و مقصد بر عهده خود شماست. ارسال یا قبول کالاهای غیرقانونی، ممنوعه یا خطرناک (مواد مخدر، اسلحه، مقادیر زیاد پول نقد، مواد قابل اشتعال و ...) ممنوع است. حمل بسته بدون بازرسی محتویات آن کاملاً به ریسک خودتان است.",
      "درباره خدمت مسافر همراه: FlyCarrier هیچ همراهی را استخدام، آموزش، تأیید صلاحیت یا نظارت نمی‌کند. همراه یک متخصص پزشکی یا مراقب دارای گواهی نیست. اگر مسافر نیازهای پزشکی دارد، مراقبت حرفه‌ای مناسب ترتیب دهید و از قبل به شرکت هواپیمایی اطلاع دهید. مسئولیت کامل هر ترتیبی بر عهده خود شماست.",
      "FlyCarrier هیچ مسئولیتی در قبال گم شدن، آسیب، سرقت، تأخیر، کلاهبرداری، ضرر مالی، مشکلات گمرکی، گرفتاری‌های قانونی، صدمات شخصی یا هرگونه ضرر دیگر ناشی از استفاده از این پلتفرم یا تعامل با سایر کاربران ندارد.",
      "استفاده از این پلتفرم کاملاً به مسئولیت خودِ شما انجام می‌شود."
    ],
    termsAccept: "متن قوانین و هشدارهای امنیتی بالا را خوانده‌ام و می‌پذیرم.",
    termsRequired: "برای ایجاد حساب کاربری باید قوانین و هشدارهای امنیتی را بپذیرید.",
    geoDetectInfo: "زبان به صورت خودکار بر اساس منطقه شما انتخاب شد. هر زمان می‌توانید از منوی بالا تغییر دهید.",
    forgotPassword: "رمز عبور را فراموش کرده‌اید؟",
    forgotTitle: "بازیابی رمز عبور",
    forgotDesc: "ایمیل حساب کاربری خود را وارد کنید. لینک امن بازیابی رمز عبور برایتان ارسال خواهد شد.",
    sendResetLink: "ارسال لینک بازیابی",
    resetSent: "لینک بازیابی ارسال شد! لطفاً صندوق ورودی (و پوشه اسپم) را بررسی کنید.",
    backToLogin: "بازگشت به صفحه ورود",
    changePassword: "تغییر رمز عبور",
    changePasswordHint: "یک لینک امن بازیابی رمز عبور به ایمیل شما ارسال می‌شود.",
    resetLinkSent: "لینک تغییر رمز عبور به ایمیل شما ارسال شد. لطفاً صندوق ورودی را بررسی کنید.",
    bannedTitle: "حساب کاربری شما مسدود شده",
    bannedMsg: "حساب کاربری شما توسط مدیر سایت مسدود شده است. در صورتی که فکر می‌کنید این اقدام اشتباه بوده، لطفاً با پشتیبانی تماس بگیرید.",
    manageUsers: "مدیریت کاربران",
    searchUsersPlaceholder: "جستجو بر اساس ایمیل یا نام...",
    statusActive: "فعال",
    statusBanned: "مسدود",
    statusOnline: "آنلاین",
    banAction: "مسدود کردن",
    unbanAction: "رفع مسدودی",
    deleteAction: "حذف",
    confirmBanTitle: "این کاربر مسدود شود؟",
    confirmBanDesc: "کاربر تا زمانی که توسط شما رفع مسدودی نشود، نمی‌تواند از پلتفرم استفاده کند.",
    confirmDeleteTitle: "پروفایل این کاربر حذف شود؟",
    confirmDeleteDesc: "اطلاعات پروفایل کاربر از پایگاه داده حذف می‌شود. توجه: حساب احراز هویت کاربر باقی می‌ماند و برای حذف کامل باید به صورت دستی حذف شود.",
    confirmAction: "تأیید",
    noUsersFound: "کاربری یافت نشد.",
    joined: "تاریخ ثبت‌نام",
    viewDetails: "مشاهده جزئیات",
    hideDetails: "بستن جزئیات",
    detailsProfileType: "نوع پروفایل",
    detailsNoProfile: "این کاربر هنوز پروفایل خود را کامل نکرده است.",
    detailsRoute: "مسیر",
    detailsContact: "اطلاعات تماس",
    detailsRecipient: "گیرنده",
    detailsPackage: "بار برای ارسال",
    detailsFlight: "پرواز",
    detailsAcceptedItems: "اقلام پذیرفته‌شده",
    detailsRates: "نرخ هر قلم",
    detailsCompanionOffer: "ارائه خدمت مسافر همراه",
    detailsCompanionNeed: "نیازمند مسافر همراه",
    detailsSocial: "شبکه‌های اجتماعی / پیام‌رسان",
    detailsPhoneHidden: "شماره تلفن از دید عموم پنهان است",
    detailsNone: "ندارد",
    detailsWeight: "وزن",
    detailsPreferredDate: "تاریخ ترجیحی",
    detailsFlightNumber: "شماره پرواز",
    sortNewest: "جدیدترین",
    sortOldest: "قدیمی‌ترین",
    sortLabel: "مرتب‌سازی بر اساس ثبت‌نام",
    verified: "احراز هویت شده",
    verifyAction: "احراز هویت",
    unverifyAction: "حذف احراز هویت",
    verifiedBadgeTooltip: "کاربر احراز هویت شده — هویت توسط مدیران FlyCarrier تأیید شده است",
    confirmVerifyTitle: "این کاربر احراز هویت شود؟",
    confirmVerifyDesc: "این کاربر یک نشان آبی احراز هویت دریافت می‌کند که برای همه کاربران سایت قابل مشاهده خواهد بود. فقط پس از تأیید شخصی هویت کاربر این کار را انجام دهید.",
    confirmUnverifyTitle: "احراز هویت حذف شود؟",
    confirmUnverifyDesc: "نشان آبی احراز هویت از پروفایل این کاربر حذف خواهد شد.",
    preferredDate: "تاریخ ترجیحی تحویل",
    preferredDateHint: "چه زمانی می‌خواهید بسته به دست گیرنده برسد؟ (اختیاری - به مسافران کمک می‌کند شما را پیدا کنند)",
    dateFilter: "بازه تاریخ",
    dateFilterHint: "فیلتر نتایج بر اساس تاریخ. تاریخ‌های گذشته همیشه حذف می‌شوند.",
    fromDate: "از تاریخ",
    toDate: "تا تاریخ",
    anyDate: "هر تاریخی",
    pastDatesExcluded: "تاریخ‌های گذشته به‌طور خودکار حذف می‌شوند",
    clearDates: "پاک کردن تاریخ‌ها",
    contactAdmin: "ارتباط با ادمین",
    contactAdminDesc: "برای پشتیبانی یا گزارش مشکل به یکی از ادمین‌ها پیام بدهید",
    selectAdmin: "انتخاب ادمین",
    noAdminsAvailable: "هیچ ادمینی در حال حاضر در دسترس نیست.",
    promoteToAdmin: "ارتقاء به ادمین فرعی",
    revokeAdmin: "لغو دسترسی ادمین",
    managePermissions: "مدیریت دسترسی‌ها",
    adminRoleLabel: "نقش ادمین",
    mainAdminBadge: "ادمین اصلی",
    subAdminBadge: "ادمین فرعی",
    permCanBan: "اجازه مسدود کردن / رفع مسدودی کاربران",
    permCanDelete: "اجازه حذف پروفایل کاربران",
    permCanVerify: "اجازه احراز هویت / لغو احراز هویت کاربران",
    permsHeading: "این ادمین فرعی چه کارهایی می‌تواند انجام دهد؟",
    permsDescription: "اقداماتی که ادمین فرعی اجازه انجامشان را دارد را انتخاب کنید. ادمین‌های فرعی هرگز نمی‌توانند ادمین‌های دیگر بسازند یا حساب ادمین اصلی را تغییر دهند.",
    savePermissions: "ذخیره دسترسی‌ها",
    confirmPromoteTitle: "ارتقاء به ادمین فرعی؟",
    confirmPromoteDesc: "این کاربر دسترسی‌های مدیریتی بر اساس مجوزهایی که اعطا می‌کنید را به دست می‌آورد.",
    confirmRevokeTitle: "لغو دسترسی ادمین؟",
    confirmRevokeDesc: "این کاربر تمام دسترسی‌های مدیریتی را از دست می‌دهد و دوباره به کاربر عادی تبدیل می‌شود.",
    notAuthorized: "شما اجازه انجام این اقدام را ندارید.",
    perItemSpecs: "وزن و تعرفه برای هر کالا",
    perItemSpecsDesc: "وزن (اختیاری) و تعرفه خود را برای هر نوع کالایی که می‌پذیرید به‌طور جداگانه تعیین کنید.",
    accountSecurity: "امنیت حساب کاربری",
    currentEmail: "ایمیل فعلی (نام کاربری)",
    newEmail: "آدرس ایمیل جدید",
    updateEmail: "به‌روزرسانی ایمیل",
    updateEmailDesc: "یک لینک تأیید به آدرس ایمیل جدید ارسال می‌شود. تغییر فقط پس از کلیک شما روی لینک در آن ایمیل اعمال خواهد شد.",
    emailUpdateSent: "ایمیل تأیید ارسال شد! ایمیل جدید خود را باز کنید و روی لینک کلیک کنید تا تغییر تکمیل شود.",
    emailUpdateError: "به‌روزرسانی ایمیل با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
    reauthRequired: "به دلایل امنیتی، لطفاً خارج شوید و دوباره وارد شوید، سپس تغییر ایمیل را امتحان کنید.",
    sameEmailError: "ایمیل جدید باید با ایمیل فعلی شما متفاوت باشد.",
    invalidEmailError: "لطفاً یک آدرس ایمیل معتبر وارد کنید.",
    displayName: "نام نمایشی",
    displayNameHint: "این نامی است که سایر کاربران در پروفایل شما، نتایج جستجو و پیام‌ها می‌بینند.",
    updateName: "به‌روزرسانی نام",
    nameUpdated: "نام نمایشی با موفقیت به‌روزرسانی شد.",
    requestVerification: "درخواست احراز هویت",
    verificationNavLabel: "احراز هویت اختیاری",
    verificationNavHint: "* شما هنوز احراز هویت نشده‌اید. برای شروع کلیک کنید. (اگر مسافر هستید، توصیه می‌شود انجام دهید.)",
    verificationBadge: "دریافت نشان آبی احراز هویت",
    verificationOptional: "اختیاری — اما به‌شدت توصیه می‌شود برای جلب اعتماد فرستندگان",
    verificationFormTitle: "درخواست احراز هویت",
    verificationFormDesc: "یک عکس پاسپورت، یک ویدیوی کوتاه از پاسپورت و یک ویدیوی سلفی، سپس شماره تلفن و نام کامل خود را بارگذاری کنید. ادمین ما همه‌چیز را همین‌جا بررسی می‌کند — نیازی به خروج از سایت نیست.",
    verificationStepLabel: "مرحله {n} از ۵",
    verificationStep1Title: "عکس پاسپورت",
    verificationStep1Desc: "یک عکس یا اسکن واضح از صفحه‌ی مشخصات پاسپورتتان بارگذاری کنید. تمام جزئیات (نام، شماره، عکس، تاریخ‌ها) باید کاملاً خوانا و واضح باشند.",
    verificationStep2Title: "ویدیوی پاسپورت",
    verificationStep2Desc: "یک ویدیوی کوتاه (حدود ۱۰ ثانیه) بارگذاری کنید که صفحه‌ی مشخصات پاسپورتتان را کاملاً واضح نشان دهد — دست ثابت، نور کافی، همه‌ی جزئیات خوانا.",
    verificationStep3Title: "ویدیوی سلفی",
    verificationStep3Desc: "صفحه‌ی مشخصات پاسپورتتان را درست زیر چانه‌تان بگیرید، طوری که هم صورت شما و هم تمام جزئیات پاسپورت واضح و در یک کادر باشند (مطابق عکس زیر). در حین فیلم‌برداری، واضح بگویید:",
    verificationSelfieSampleText: "«من [نام و نام خانوادگی کامل خودتان] هستم و در تاریخ {date} درخواست احراز هویت در پلتفرم فلای‌کریر را دارم.»",
    verificationStep4Title: "شماره تلفن",
    verificationStep4Desc: "یک شماره موبایل واقعی که بتوانیم با آن تماس بگیریم بفرستید.",
    verificationStep5Title: "نام کامل قانونی",
    verificationStep5Desc: "مطابق گذرنامه.",
    verificationChoosePhoto: "انتخاب عکس",
    verificationChooseVideo: "انتخاب ویدیو",
    verificationChangeFile: "تغییر فایل",
    verificationFileRequired: "لطفاً یک فایل انتخاب کنید تا ادامه دهید.",
    verificationInvalidPhoto: "لطفاً یک فایل تصویری انتخاب کنید.",
    verificationInvalidVideo: "لطفاً یک فایل ویدیویی انتخاب کنید.",
    verificationUploading: "در حال بارگذاری مدارک شما…",
    downloadFile: "دانلود",
    deleteFile: "حذف از فایربیس",
    confirmDeleteVerificationFile: "این فایل برای همیشه از Firebase Storage حذف شود؟ این عمل قابل بازگشت نیست.",
    verificationFullName: "نام کامل (مطابق با گذرنامه)",
    submitVerification: "ثبت درخواست احراز هویت",
    verificationSubmitted: "درخواست احراز هویت شما ثبت شد. ادمین ما بررسی می‌کند و به‌محض تصمیم‌گیری، همین‌جا به شما پیام می‌دهد.",
    verificationPending: "در انتظار احراز هویت",
    verificationPendingDesc: "درخواست شما در حال بررسی است. به‌محض تصمیم‌گیری، همین‌جا به شما اطلاع می‌دهیم.",
    verificationApproved: "شما احراز هویت شده‌اید! ✓",
    verificationRejected: "درخواست احراز هویت پذیرفته نشد.",
    viewVerificationRequests: "درخواست‌های احراز هویت",
    verificationRequestsTab: "درخواست‌های احراز هویت",
    verificationStatus: "وضعیت",
    retry: "تلاش مجدد",
    statusPending: "در انتظار",
    statusApproved: "تأیید شده",
    statusRejected: "رد شده",
    approveRequest: "تأیید و احراز",
    rejectRequest: "رد",
    messageApplicant: "ارسال پیام به متقاضی",
    adminNote: "یادداشت مدیر",
    adminNoteHint: "یادداشت اختیاری که با این تصمیم ثبت می‌شود (به کاربر نمایش داده نمی‌شود).",
    noVerificationRequests: "هنوز درخواست احراز هویتی ثبت نشده است.",
    senderSafetyTitle: "هشدار مهم ایمنی",
    senderSafetyBody: "برای امنیت بیشتر، اکیداً توصیه می‌شود فقط مسافرانی را انتخاب کنید که نشان آبی احراز هویت دارند. آگاه باشید که افراد سودجو ممکن است از خارج از این پلتفرم با شما تماس بگیرند و خود را به جای مسافران احراز هویت‌شده جا بزنند تا اعتماد شما را جلب کنند. تمام مکالمات اولیه را در چت داخل سایت FlyCarrier انجام دهید تا بتوانید هویت و وجود نشان آبی فرد را تأیید کنید. هرگونه مشکل ناشی از ترتیب‌دهی خارج از این پلتفرم، کاملاً به عهده خود شماست.",
    dismiss: "بستن",
    fillRequiredFields: "لطفاً فیلدهای الزامی را کامل کنید.",
    saveFailed: "ذخیره ناموفق بود. لطفاً دوباره تلاش کنید.",
    accountSettings: "تنظیمات حساب کاربری",
    accountSettingsDesc: "مدیریت ایمیل ورود، رمز عبور و نام نمایشی",
    accountSettingsBtn: "تنظیمات حساب",
    telegramAccountTitle: "حساب تلگرامی",
    telegramAccountDesc: "شما از طریق ربات تلگرام عضو شده‌اید، بنابراین ایمیل یا رمز عبوری برای مدیریت وجود ندارد. حساب شما به پروفایل تلگرامتان متصل است.",
    whatSendingMulti: "چه چیزهایی می‌فرستید؟ (یک یا چند مورد)",
    senderPickAtLeastOne: "لطفاً حداقل یک کالا برای ارسال انتخاب کنید.",
    senderPerItemSpecs: "وزن و قیمت هر کالا",
    senderPerItemSpecsDesc: "برای هر کالا وزن (اختیاری) و قیمت پیشنهادی خود را تعیین کنید. وزن اختیاری است و فقط برای یافتن مسافرانی که توان حمل آن را دارند استفاده می‌شود.",
    // برچسب کالاها
    pkgPet: "حیوان خانگی",
    pkgDocuments: "مدارک",
    pkgFullSuitcase: "چمدان کامل",
    pkgFood: "مواد غذایی",
    pkgElectronics: "لوازم الکترونیکی",
    pkgMedicine: "دارو",
    pkgCosmetics: "لوازم آرایشی",
    pkgDishware: "ظروف",
    pkgToys: "اسباب‌بازی",
    pkgClothes: "پوشاک",
    pkgTobacco: "دخانیات",
    pkgPersonalItems: "لوازم شخصی",
    pkgCustom: "موارد دلخواه دیگر (خودتان اضافه کنید)",
    customItemLabel: "این مورد را توضیح دهید",
    customItemPlaceholder: "مثلاً شارژر لپ‌تاپ دست دوم",
    customItemRequired: "لطفاً این مورد را توضیح دهید.",
    pkgCompanion: "مسافر همراه",
    // مسافر همراه (به‌عنوان یک آیتم)
    companionService: "مسافر همراه",
    companionItemDesc: "می‌توانید در طول سفر همراه یک مسافر دیگر باشید و از او مراقبت کنید.",
    companionSenderItemDesc: "یک مسافر پیدا کنید که در طول سفر همراه مسافر شما باشد.",
    companionTypeQuestion: "وضعیت حرکتی مسافر",
    companionTravelerAcceptTitle: "می‌توانید همراه چه نوع مسافرانی باشید؟",
    companionSenderNeedTitle: "وضعیت حرکتی مسافر شما چگونه است؟",
    companionWheelchair: "مسافر با ویلچر",
    companionWalking: "مسافر راه‌رونده (متحرک)",
    companionWheelchairShort: "ویلچر",
    companionWalkingShort: "راه‌رونده",
    companionAcceptWheelchair: "می‌توانم همراه مسافر با ویلچر باشم",
    companionAcceptWalking: "می‌توانم همراه مسافر راه‌رونده (متحرک) باشم",
    companionNeedWheelchair: "مسافر من از ویلچر استفاده می‌کند",
    companionNeedWalking: "مسافر من می‌تواند راه برود (متحرک)",
    companionPickAtLeastOne: "حداقل یک نوع مسافر که می‌توانید همراهش باشید را انتخاب کنید.",
    companionSenderPickOne: "وضعیت حرکتی مسافر خود را انتخاب کنید.",
    companionOffered: "مسافر همراه موجود است",
    companionForWheelchair: "همراهی مسافران با ویلچر",
    companionForWalking: "همراهی مسافران راه‌رونده",
    companionForBoth: "همراهی مسافران ویلچری و راه‌رونده",
    companionNeeded: "نیازمند مسافر همراه",
    companionNeededWheelchair: "نیازمند همراه (ویلچر)",
    companionNeededWalking: "نیازمند همراه (راه‌رونده)",
    companionMatchNote: "این مسافر خدمت مسافر همراه ارائه می‌دهد.",
    companionSafetyNote: "همراهان داوطلبانی از جامعه کاربران هستند، نه کادر پزشکی یا مراقبان دارای گواهی. همیشه نیازهای کمکی خاص را به شرکت هواپیمایی اطلاع دهید و در صورت لزوم مراقبت پزشکی حرفه‌ای ترتیب دهید.",
    companionRate: "دستمزد همراهی (برای کل سفر)",
    // اعضای تلگرام
    telegramTab: "اعضای تلگرام",
    telegramMembersTitle: "اعضای تلگرام",
    telegramMembersDesc: "کاربرانی که از طریق ربات تلگرام عضو شده‌اند. برای آن‌ها حساب ایمیلی ساخته نمی‌شود.",
    telegramBadge: "تلگرام",
    hasProfile: "پروفایل ساخته شده",
    noProfile: "بدون پروفایل",
    telegramId: "آی‌دی تلگرام",
    telegramUsername: "نام کاربری",
    noTelegramMembers: "هنوز عضوی از طریق تلگرام ثبت نشده است.",
    viaTelegram: "از طریق تلگرام",
    signupSourceWeb: "وب",
    signupSourceTelegram: "تلگرام"
  }
};

const getTranslation = (lang, key) => {
  if (TRANSLATIONS[lang] && TRANSLATIONS[lang][key] !== undefined) return TRANSLATIONS[lang][key];
  return TRANSLATIONS['en'][key] || key;
};

// Helper: resolve a package type's label in the current language. For the
// "custom" (Other — add your own) type, prefers the free-text label the
// user typed (stored in itemSpecs.custom.label) over the generic name.
const pkgTypeLabel = (lang, id, itemSpecs) => {
  if (id === 'custom') {
    const customLabel = itemSpecs?.custom?.label;
    if (customLabel) return customLabel;
  }
  const pt = PACKAGE_TYPES.find(p => p.id === id);
  if (!pt) return id;
  return getTranslation(lang, pt.labelKey);
};

// ==========================================
// Language Detection via IP
// ==========================================
const detectLanguageFromIP = async () => {
  try {
    const userOverride = typeof localStorage !== 'undefined' && localStorage.getItem('fc_lang_user_set');
    if (userOverride) return null;
    if (typeof localStorage !== 'undefined') {
      const cached = localStorage.getItem('fc_geo_cache');
      if (cached) {
        try {
          const { lang, ts } = JSON.parse(cached);
          if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000 && lang) return lang;
        } catch (e) { /* ignore */ }
      }
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const data = await res.json();
    const cc = (data.country_code || data.country || '').toUpperCase();
    const detected = COUNTRY_TO_LANG[cc] || 'en';
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('fc_geo_cache', JSON.stringify({ lang: detected, ts: Date.now(), country: cc }));
    }
    return detected;
  } catch (e) {
    console.warn('Geo detection failed, falling back to English:', e?.message);
    return null;
  }
};

// ==========================================
// Glassmorphism Style Helpers — Dark Navy Theme
// ==========================================
const glass = "bg-white/[0.06] backdrop-blur-2xl border border-white/[0.10] shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)]";
const glassCard = "bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]";
const glassInput = "bg-white/[0.06] backdrop-blur-md border border-white/[0.12] focus:border-violet-400/60 focus:ring-2 focus:ring-violet-400/25 focus:bg-white/[0.10] outline-none transition-all duration-300 text-white placeholder-slate-500";
const btnPrimary = "bg-gradient-to-r from-violet-500 via-indigo-500 to-blue-500 hover:from-violet-600 hover:via-indigo-600 hover:to-blue-600 text-white font-bold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-300 hover:-translate-y-0.5 active:scale-95";

// ==========================================
// LanguageToggle
// ==========================================
function LanguageToggle({ lang, setLang, compact }) {
  return (
    <div className={`inline-flex items-center gap-1 rounded-full p-1 border border-white/[0.10] bg-white/[0.05] ${compact ? '' : ''}`}>
      {Object.entries(LANGUAGES).map(([code, name]) => {
        const active = lang === code;
        return (
          <button
            key={code}
            onClick={() => setLang(code)}
            className={`px-3 py-1.5 rounded-full text-sm font-bold transition-all duration-300 ${active
              ? 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-md shadow-indigo-500/30'
              : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.06]'}`}
            aria-pressed={active}
            title={name}
          >
            {name}
          </button>
        );
      })}
    </div>
  );
}

// ==========================================
// BrandLogo — the real TNAi / TORABINEJAD Ai logo, embedded as base64 (self-contained)
// ==========================================
function BrandLogo({ className = "", height = 40 }) {
  return (
    <img
      src={LOGO_SRC}
      alt="TORABINEJAD Ai"
      className={className}
      style={{ height, width: 'auto', objectFit: 'contain' }}
      loading="lazy"
    />
  );
}

// ==========================================
// Companion helpers (shared)
// ==========================================
// Traveler companion acceptance is stored as companionAccept: { wheelchair: bool, walking: bool }
// Sender companion need is stored as companionNeed: 'wheelchair' | 'walking' (single choice)
const companionAcceptSummary = (lang, accept) => {
  if (!accept) return '';
  const w = accept.wheelchair, k = accept.walking;
  if (w && k) return getTranslation(lang, 'companionForBoth');
  if (w) return getTranslation(lang, 'companionForWheelchair');
  if (k) return getTranslation(lang, 'companionForWalking');
  return '';
};

const companionNeedLabel = (lang, need) => {
  if (need === 'wheelchair') return getTranslation(lang, 'companionNeededWheelchair');
  if (need === 'walking') return getTranslation(lang, 'companionNeededWalking');
  return getTranslation(lang, 'companionNeeded');
};

// Does a traveler's acceptance cover a sender's need?
const companionMatches = (travelerAccept, senderNeed) => {
  if (!travelerAccept || !senderNeed) return false;
  if (senderNeed === 'wheelchair') return travelerAccept.wheelchair === true;
  if (senderNeed === 'walking') return travelerAccept.walking === true;
  return false;
};

// ==========================================
// Main Component
// ==========================================
export default function AirLogisticsApp() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [view, setView] = useState('loading');
  const [userProfile, setUserProfile] = useState(null);
  const [lang, setLangState] = useState('en');
  const [geoAutoApplied, setGeoAutoApplied] = useState(false);
  const [chatReceiver, setChatReceiver] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  // Admin-controlled: whether email verification is required at signup. Defaults to OFF.
  const [requireEmailVerification, setRequireEmailVerification] = useState(false);
  const requireEmailVerificationRef = useRef(false);
  useEffect(() => { requireEmailVerificationRef.current = requireEmailVerification; }, [requireEmailVerification]);
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'app'), (snap) => {
      setRequireEmailVerification(snap.exists() && snap.data().requireEmailVerification === true);
    }, () => { /* ignore, keep default */ });
    return () => unsubscribe();
  }, []);

  const setLang = (newLang) => {
    setLangState(newLang);
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('fc_lang_user_set', newLang);
      }
    } catch (e) { /* ignore */ }
  };

  const t = (key) => getTranslation(lang, key);
  const dir = lang === 'fa' ? 'rtl' : 'ltr';
  // Effective "email OK" gate: passes when verification isn't required, or when it is and the user has verified.
  const emailOk = !!user && (!requireEmailVerification || user.emailVerified);

  const isMainAdmin = user && user.email === 'administrator@fly-carrier.com';
  const isSubAdmin = user && userProfile?.isAdmin === true && !isMainAdmin;
  const isAdmin = isMainAdmin || isSubAdmin;
  const adminPermissions = isMainAdmin
    ? { canBan: true, canDelete: true, canVerify: true, canPromoteAdmins: true }
    : isSubAdmin
      ? {
          canBan: userProfile?.adminPermissions?.canBan === true,
          canDelete: userProfile?.adminPermissions?.canDelete === true,
          canVerify: userProfile?.adminPermissions?.canVerify === true,
          canPromoteAdmins: false
        }
      : { canBan: false, canDelete: false, canVerify: false, canPromoteAdmins: false };

  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('fc_lang_user_set');
        if (saved && LANGUAGES[saved]) {
          setLangState(saved);
          return;
        }
      }
    } catch (e) { /* ignore */ }
    detectLanguageFromIP().then((detected) => {
      if (detected && LANGUAGES[detected] && detected !== 'en') {
        setLangState(detected);
        setGeoAutoApplied(true);
        setTimeout(() => setGeoAutoApplied(false), 5000);
      }
    });
  }, []);

  useEffect(() => {
    const logVisit = async () => {
      try {
        const today = new Date();
        const key = `d_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const sessionKey = `fc_visit_${key}`;
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(sessionKey)) return;
        await setDoc(doc(db, 'stats', 'visits'), {
          [key]: increment(1),
          total: increment(1),
          lastVisitAt: serverTimestamp()
        }, { merge: true });
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(sessionKey, '1');
      } catch (e) {
        console.warn('Visit logging skipped:', e?.code || e?.message || e);
      }
    };
    logVisit();
  }, []);

  useEffect(() => {
    document.title = "FlyCarrier — Send Packages with Travelers Worldwide | ارسال بار با مسافر | www.fly-carrier.com";
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'FlyCarrier connects senders with travelers worldwide for peer-to-peer shipping and a travel companion service for wheelchair or walking passengers. ارسال بسته و بار با مسافر هوایی، خدمت مسافر همراه، ارزان‌تر از پست و باربری بین‌المللی.');
    setMeta('keywords', [
      'FlyCarrier','peer to peer shipping','send package with traveler','travel companion',
      'wheelchair passenger assistance','flight companion service','kolbar','kolbar Canada','kolbar USA',
      'send package to Iran','فلای کریر','ارسال بار با مسافر','مسافر همراه','همراه مسافر با ویلچر','کولبر','کولبر کانادا','کولبر آمریکا','ارسال بار به ایران'
    ].join(', '));
    setMeta('author', 'FlyCarrier - TORABINEJAD AI');
    setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('theme-color', '#7c3aed');
    setMeta('og:title', 'FlyCarrier — Send Packages & Travel Companion Worldwide | ارسال بار با مسافر', true);
    setMeta('og:description', 'Connect with verified travelers to send packages internationally, or find a travel companion. ارسال بار با مسافر و خدمت مسافر همراه.', true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', 'https://www.fly-carrier.com', true);
    setMeta('og:site_name', 'FlyCarrier', true);
    setMeta('og:locale', dir === 'rtl' ? 'fa_IR' : 'en_US', true);
    setMeta('twitter:card', 'summary_large_image');
    try {
      document.documentElement.lang = lang;
      document.documentElement.dir = dir;
    } catch (e) { /* ignore */ }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://www.fly-carrier.com');
  }, [lang]);

  useEffect(() => {
    const initAuth = async () => {
      const initialToken = typeof window !== 'undefined' ? window['__initial_auth_token'] : undefined;
      if (initialToken) {
        await signInWithCustomToken(auth, initialToken);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setView('landing');
        setAuthLoading(false);
        setUserProfile(null);
      } else {
        if (requireEmailVerificationRef.current && !currentUser.emailVerified) {
          setView('verify-email');
          setAuthLoading(false);
          return;
        }
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), { lastSeen: serverTimestamp() });
        } catch (e) { }
        setAuthLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    if (requireEmailVerification && !user.emailVerified) { setView('verify-email'); return; }
    setDataLoading(true);
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserProfile(data);
        if (data.banned === true) {
          setView('banned');
          setDataLoading(false);
          return;
        }
        if (data.role) {
          if (view === 'loading' || view === 'auth' || view === 'create-profile' || view === 'verify-email' || view === 'banned') setView('dashboard');
        } else {
          setView('create-profile');
        }
      } else {
        setUserProfile(null);
        setView('create-profile');
      }
      setDataLoading(false);
    }, (error) => {
      console.error("Profile error:", error);
      setDataLoading(false);
      if (view === 'auth' || view === 'loading') setView('create-profile');
    });
    return () => unsubscribe();
  }, [user, user?.emailVerified, requireEmailVerification]);

  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    const q = query(collection(db, "messages"), where("receiverId", "==", user.uid), where("read", "==", false));
    const unsubscribe = onSnapshot(q, (snapshot) => { setUnreadCount(snapshot.size); });
    return () => unsubscribe();
  }, [user]);

  const handleLoginClick = () => setView('auth');
  const handleLogoClick = () => {
    if (!user) setView('landing');
    else if (requireEmailVerification && !user.emailVerified) setView('verify-email');
    else if (userProfile && userProfile.role) setView('dashboard');
    else setView('create-profile');
  };

  const openChat = (receiver) => {
    if (!receiver) { setChatReceiver(null); setView('messages'); return; }
    const normalized = {
      id: receiver.id || receiver.uid,
      uid: receiver.uid || receiver.id,
      name: receiver.name || receiver.fullName || receiver.email || 'User',
      email: receiver.email || '',
      verified: receiver.verified === true,
    };
    setChatReceiver(normalized);
    setView('messages');
  };

  const handleChangePassword = async () => {
    if (!user?.email) return;
    try {
      await sendPasswordResetEmail(auth, user.email);
      alert(t('resetLinkSent'));
    } catch (err) {
      console.error('Password reset failed:', err);
      alert('Could not send reset email. Please try again later.');
    }
  };

  if (authLoading || (user && dataLoading && view === 'loading')) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#0a1530] via-[#0d1e44] to-[#0a1228] font-bold gap-6">
        <div className="w-20 h-20 border-4 border-indigo-400/20 border-t-indigo-400 rounded-full animate-spin"></div>
        <div className="text-slate-500 text-sm tracking-[0.3em] uppercase font-light">Loading FlyCarrier...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1530] via-[#0d1e44] to-[#0a1228] font-sans text-white flex flex-col transition-all duration-500 relative overflow-hidden" dir={dir}>
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-indigo-500/15 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute top-1/4 -right-20 w-[400px] h-[400px] bg-blue-500/12 rounded-full blur-[80px] animate-pulse" style={{animationDelay: '1.5s'}}></div>
        <div className="absolute -bottom-32 left-1/3 w-[450px] h-[450px] bg-violet-500/10 rounded-full blur-[90px] animate-pulse" style={{animationDelay: '3s'}}></div>
        <div className="absolute top-2/3 left-10 w-[350px] h-[350px] bg-sky-500/8 rounded-full blur-[80px] animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      <Navbar
        user={user} isAdmin={isAdmin}
        onLogout={() => { signOut(auth); setView('landing'); }}
        lang={lang} setLang={setLang} t={t}
        onLoginClick={handleLoginClick} onLogoClick={handleLogoClick}
        onMessagesClick={() => { setChatReceiver(null); setView('messages'); }}
        onAdminClick={() => setView('admin')} currentView={view} unreadCount={unreadCount}
        onAccountClick={() => setView('account')}
        showAccountBtn={!!(emailOk && userProfile)}
        profile={userProfile}
        onVerifyClick={() => setShowVerificationForm(true)}
      />

      {geoAutoApplied && lang !== 'en' && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-4 duration-500 px-4 w-full max-w-md">
          <div className={`${glass} px-5 py-3 rounded-full flex items-center gap-3 text-sm text-slate-200`}>
            <Globe className="h-4 w-4 text-emerald-300 flex-shrink-0" />
            <span className="flex-1">{t('geoDetectInfo')}</span>
            <button onClick={() => setGeoAutoApplied(false)} className="text-slate-500 hover:text-slate-100"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <div className="w-full flex-grow flex flex-col relative z-10">
        {view === 'landing' && <LandingPage t={t} onGetStarted={handleLoginClick} dir={dir} />}
        {view !== 'landing' && (
          <div className={`max-w-6xl mx-auto p-4 w-full flex-grow ${user ? 'pt-44 sm:pt-44' : 'pt-28 sm:pt-28'}`}>
            {view === 'auth' && !user && <AuthScreen t={t} dir={dir} requireEmailVerification={requireEmailVerification} />}
            {view === 'auth' && user && (
              <div className="text-center py-20 flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-violet-400 border-t-transparent rounded-full animate-spin mb-6"></div>
                <p className="text-slate-500 font-medium">Redirecting securely...</p>
              </div>
            )}
            {view === 'verify-email' && user && (
              <VerifyEmailScreen user={user} t={t} onVerified={() => { window.location.reload(); }} />
            )}
            {view === 'banned' && user && (
              <BannedScreen user={user} t={t} onLogout={() => { signOut(auth); setView('landing'); }} />
            )}
            {view === 'create-profile' && emailOk && (
              <ProfileForm user={user} existingData={userProfile} onComplete={() => setView('dashboard')} t={t} dir={dir} lang={lang} />
            )}
            {view === 'dashboard' && userProfile && emailOk && (
              <Dashboard user={user} profile={userProfile} onEdit={() => setView('edit-profile')} onMessage={openChat} onChangePassword={handleChangePassword} onAccountSettings={() => setView('account')} t={t} dir={dir} lang={lang} onOpenVerification={() => setShowVerificationForm(true)} />
            )}
            {view === 'account' && emailOk && (
              <AccountSettings user={user} profile={userProfile} onBack={() => setView(userProfile?.role ? 'dashboard' : 'create-profile')} t={t} dir={dir} />
            )}
            {view === 'edit-profile' && userProfile && (
              <ProfileForm user={user} existingData={userProfile} onComplete={() => setView('dashboard')} onCancel={() => setView('dashboard')} t={t} dir={dir} lang={lang} />
            )}
            {view === 'messages' && userProfile && (
              <MessagesView currentUser={user} currentProfile={userProfile} initialReceiver={chatReceiver} t={t} />
            )}
            {view === 'admin' && isAdmin && (<AdminPanel t={t} lang={lang} currentUserEmail={user?.email} isMainAdmin={isMainAdmin} adminPermissions={adminPermissions} onMessage={openChat} />)}
          </div>
        )}
      </div>
      <Footer t={t} dir={dir} />
      {showVerificationForm && user && userProfile && (
        <VerificationRequestForm
          user={user}
          profile={userProfile}
          onClose={() => setShowVerificationForm(false)}
          onSubmitted={() => { setShowVerificationForm(false); alert(t('verificationSubmitted')); }}
          t={t}
          dir={dir}
          lang={lang}
        />
      )}
    </div>
  );
}

// ==========================================
// Navbar
// ==========================================
function Navbar({ user, isAdmin, onLogout, lang, setLang, t, onLoginClick, onLogoClick, onMessagesClick, onAdminClick, unreadCount, onAccountClick, showAccountBtn, profile, onVerifyClick }) {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.08]" style={{background: 'rgba(10,18,40,0.78)', backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)'}}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="h-16 sm:h-20 flex items-center justify-between gap-2">
          <button onClick={onLogoClick} className="flex items-center gap-2 sm:gap-3 text-white hover:opacity-90 transition-all group min-w-0">
            <div className="bg-gradient-to-br from-indigo-400 to-blue-500 text-white p-2 sm:p-2.5 rounded-2xl shadow-lg shadow-indigo-500/40 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
              <Plane className="h-5 w-5 sm:h-6 sm:w-6 transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-extrabold text-base sm:text-xl tracking-tight bg-gradient-to-r from-indigo-200 via-blue-200 to-sky-200 bg-clip-text text-transparent truncate">{t('appTitle')}</span>
              <span className="text-[10px] text-slate-500 tracking-widest font-light hidden sm:block">www.fly-carrier.com</span>
            </div>
          </button>

          <div className="hidden sm:flex items-center gap-3">
            <LanguageToggle lang={lang} setLang={setLang} />
            {user ? (
              <div className="flex items-center gap-1 pl-2 border-l border-white/[0.10]">
                {isAdmin && (
                  <button onClick={onAdminClick} className="p-2.5 text-slate-400 hover:text-amber-400 hover:bg-amber-400/10 rounded-full transition-all" title="Admin Panel">
                    <BarChart3 className="h-5 w-5" />
                  </button>
                )}
                <button onClick={onMessagesClick} className="p-2.5 text-slate-400 hover:text-indigo-300 hover:bg-indigo-400/15 rounded-full transition-all relative" title={t('messages')}>
                  <MessageSquare className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 bg-rose-500 text-white text-[10px] font-bold h-4 w-4 flex items-center justify-center rounded-full animate-pulse shadow-lg shadow-rose-500/50">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {showAccountBtn && (
                  <button onClick={onAccountClick} className="p-2.5 text-slate-400 hover:text-violet-300 hover:bg-violet-400/15 rounded-full transition-all" title={t('accountSettings')}>
                    <Settings className="h-5 w-5" />
                  </button>
                )}
                <button onClick={onLogout} className="p-2.5 text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 rounded-full transition-all" title={t('logout')}>
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <button onClick={onLoginClick} className={`${btnPrimary} text-sm px-6 py-2.5 rounded-full flex items-center gap-2`}>
                <LogIn className="h-4 w-4" />
                <span>{t('loginBtn')}</span>
              </button>
            )}
          </div>

          <div className="flex sm:hidden items-center gap-1 flex-shrink-0">
            {user ? (
              <>
                {isAdmin && (
                  <button onClick={onAdminClick} className="p-2 text-slate-400 hover:text-amber-400 active:bg-amber-400/10 rounded-full transition-all" title="Admin" aria-label="Admin Panel">
                    <BarChart3 className="h-5 w-5" />
                  </button>
                )}
                <button onClick={onMessagesClick} className="p-2 text-slate-400 hover:text-indigo-300 active:bg-indigo-400/15 rounded-full transition-all relative" title={t('messages')} aria-label={t('messages')}>
                  <MessageSquare className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 bg-rose-500 text-white text-[9px] font-bold h-4 w-4 flex items-center justify-center rounded-full animate-pulse shadow-lg shadow-rose-500/50">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {showAccountBtn && (
                  <button onClick={onAccountClick} className="p-2 text-slate-400 hover:text-violet-300 active:bg-violet-400/15 rounded-full transition-all" title={t('accountSettings')} aria-label={t('accountSettings')}>
                    <Settings className="h-5 w-5" />
                  </button>
                )}
                <button onClick={onLogout} className="p-2 text-slate-400 hover:text-rose-400 active:bg-rose-400/10 rounded-full transition-all" title={t('logout')} aria-label={t('logout')}>
                  <LogOut className="h-5 w-5" />
                </button>
              </>
            ) : (
              <button onClick={onLoginClick} className={`${btnPrimary} text-xs px-4 py-2 rounded-full flex items-center gap-1.5`} aria-label={t('loginBtn')}>
                <LogIn className="h-4 w-4" />
                <span>{t('login')}</span>
              </button>
            )}
          </div>
        </div>

        <div className="sm:hidden flex justify-center pb-2 -mt-1">
          <LanguageToggle lang={lang} setLang={setLang} compact />
        </div>

        {user && (
          <div className="flex justify-center pb-3">
            {profile?.verified === true ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-sky-500/15 text-sky-300 border border-sky-400/30">
                <BadgeCheck className="h-3.5 w-3.5" fill="currentColor" stroke="#0c1330" strokeWidth={2.5} /> {t('verificationApproved')}
              </span>
            ) : profile?.verificationStatus === 'pending' ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-400/30">
                <BadgeCheck className="h-3.5 w-3.5" /> {t('verificationPending')}
              </span>
            ) : (
              <button onClick={onVerifyClick} className="inline-flex flex-col items-center gap-0.5 px-4 py-2 rounded-2xl bg-sky-500/10 hover:bg-sky-500/20 border border-sky-400/30 transition-all text-center max-w-full">
                <span className="text-sky-300 text-xs font-extrabold flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5" /> {t('verificationNavLabel')}</span>
                <span className="text-sky-100/70 text-[11px] leading-snug">{t('verificationNavHint')}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

// ==========================================
// MessagesView — in-app chat (index-safe)
// ==========================================
function MessagesView({ currentUser, currentProfile, initialReceiver, t }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(initialReceiver || null);
  const [verifiedMap, setVerifiedMap] = useState({});
  const messagesEndRef = useRef(null);
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  const sortByTime = (arr) => [...arr].sort((a, b) => {
    const ta = a.timestamp?.seconds || (a.timestamp?.toMillis ? a.timestamp.toMillis() / 1000 : 0) || 0;
    const tb = b.timestamp?.seconds || (b.timestamp?.toMillis ? b.timestamp.toMillis() / 1000 : 0) || 0;
    return ta - tb;
  });

  useEffect(() => {
    const idsToCheck = new Set();
    conversations.forEach(c => {
      const cid = c.id || c.uid;
      if (cid && verifiedMap[cid] === undefined) idsToCheck.add(cid);
    });
    const aid = activeChat?.id || activeChat?.uid;
    if (aid && verifiedMap[aid] === undefined) idsToCheck.add(aid);
    if (idsToCheck.size === 0) return;
    const fetchAll = async () => {
      const updates = {};
      await Promise.all(Array.from(idsToCheck).map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          updates[uid] = snap.exists() ? snap.data().verified === true : false;
        } catch (e) { updates[uid] = false; }
      }));
      setVerifiedMap(prev => ({ ...prev, ...updates }));
    };
    fetchAll();
  }, [conversations, activeChat]);

  useEffect(() => {
    if (!activeChat || !currentUser) return;
    const otherId = activeChat.id || activeChat.uid;
    if (!otherId) return;
    const markRead = async () => {
      try {
        const qy = query(
          collection(db, "messages"),
          where("participants", "array-contains", currentUser.uid),
          where("senderId", "==", otherId),
          where("read", "==", false)
        );
        const snapshot = await getDocs(qy);
        snapshot.docs.forEach(async (docSnap) => { await updateDoc(doc(db, "messages", docSnap.id), { read: true }); });
      } catch (e) {
        console.warn('markRead failed (safe to ignore):', e?.message);
      }
    };
    markRead();
  }, [activeChat, currentUser, messages]);

  useEffect(() => {
    if (!currentUser) return;
    const buildConversations = (msgs) => {
      const ordered = sortByTime(msgs).reverse();
      const contactsMap = new Map();
      ordered.forEach(msg => {
        const otherId = msg.senderId === currentUser.uid ? msg.receiverId : msg.senderId;
        const otherName = msg.senderId === currentUser.uid ? msg.receiverName : msg.senderName;
        if (!contactsMap.has(otherId)) contactsMap.set(otherId, { id: otherId, name: otherName, lastMsg: msg });
      });
      setConversations(Array.from(contactsMap.values()));
    };
    let unsubscribe = () => {};
    try {
      const qy = query(
        collection(db, "messages"),
        where("participants", "array-contains", currentUser.uid),
        orderBy("timestamp", "desc")
      );
      unsubscribe = onSnapshot(qy, (snapshot) => {
        buildConversations(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err) => {
        console.warn('Conversation listener error, retrying without orderBy:', err?.message);
        const qy2 = query(collection(db, "messages"), where("participants", "array-contains", currentUser.uid));
        unsubscribe = onSnapshot(qy2, (snap2) => {
          buildConversations(snap2.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (e2) => console.error('Conversation fallback failed:', e2?.message));
      });
    } catch (e) {
      console.error('Failed to attach conversation listener:', e);
    }
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !activeChat) return;
    const otherId = activeChat.id || activeChat.uid;
    if (!otherId) return;
    const applyMsgs = (all) => {
      const chatMsgs = all.filter(m => (m.senderId === currentUser.uid && m.receiverId === otherId) || (m.senderId === otherId && m.receiverId === currentUser.uid));
      setMessages(sortByTime(chatMsgs));
      setTimeout(scrollToBottom, 100);
    };
    let unsubscribe = () => {};
    try {
      const qy = query(
        collection(db, "messages"),
        where("participants", "array-contains", currentUser.uid),
        orderBy("timestamp", "asc")
      );
      unsubscribe = onSnapshot(qy, (snapshot) => {
        applyMsgs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err) => {
        console.warn('Message listener error, retrying without orderBy:', err?.message);
        const qy2 = query(collection(db, "messages"), where("participants", "array-contains", currentUser.uid));
        unsubscribe = onSnapshot(qy2, (snap2) => {
          applyMsgs(snap2.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (e2) => console.error('Message fallback failed:', e2?.message));
      });
    } catch (e) {
      console.error('Failed to attach message listener:', e);
    }
    return () => unsubscribe();
  }, [currentUser, activeChat]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChat) return;
    const receiverId = activeChat.id || activeChat.uid;
    if (!receiverId) {
      alert('Cannot send message: recipient information is missing. Please go back and try again.');
      return;
    }
    const msgContent = newMessage;
    setNewMessage("");
    const tempMessage = { id: "temp_" + Date.now(), senderId: currentUser.uid, receiverId: receiverId, content: msgContent, timestamp: { seconds: Date.now() / 1000 }, read: false, isTemp: true };
    setMessages(prev => [...prev, tempMessage]);
    setTimeout(scrollToBottom, 50);
    try {
      await addDoc(collection(db, "messages"), {
        senderId: currentUser.uid,
        senderName: currentProfile.fullName || currentUser.email,
        receiverId: receiverId,
        receiverName: activeChat.name || activeChat.fullName || activeChat.email || "User",
        content: msgContent,
        timestamp: serverTimestamp(),
        read: false,
        participants: [currentUser.uid, receiverId]
      });
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message: " + (error?.message || error?.code || 'unknown error'));
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
    }
  };

  return (
    <div className={`${glass} rounded-3xl overflow-hidden h-[70vh] md:h-[600px] flex flex-col md:flex-row`}>
      <div className={`w-full md:w-1/3 border-r border-white/[0.10] flex flex-col min-h-0 ${activeChat ? 'hidden md:flex' : 'flex'}`} style={{background: 'rgba(255,255,255,0.03)'}}>
        <div className="p-4 border-b border-white/[0.10]"><h2 className="text-lg font-bold text-white">{t('inbox')}</h2></div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {conversations.length === 0 ? <div className="p-8 text-center text-slate-500 text-sm">No conversations yet.</div> :
            conversations.map(contact => (
              <div key={contact.id} onClick={() => setActiveChat(contact)} className={`p-4 border-b border-white/[0.06] cursor-pointer hover:bg-white/[0.06] transition-colors ${activeChat?.id === contact.id ? 'bg-white/[0.10] border-l-2 border-l-violet-500' : ''}`}>
                <div className="font-bold text-slate-100 text-sm flex items-center gap-1.5">
                  {contact.name || "Unknown"}
                  {verifiedMap[contact.id] && <VerifiedBadge size="sm" title={t('verifiedBadgeTooltip')} />}
                </div>
                <div className={`text-xs truncate mt-1 ${contact.lastMsg.senderId !== currentUser.uid && !contact.lastMsg.read ? 'font-bold text-slate-200' : 'text-slate-500'}`}>
                  {contact.lastMsg.senderId === currentUser.uid && "You: "}{contact.lastMsg.content}
                </div>
              </div>
            ))
          }
        </div>
      </div>
      <div className={`w-full md:w-2/3 flex flex-col min-h-0 ${!activeChat ? 'hidden md:flex' : 'flex'}`}>
        {activeChat ? (
          <>
            <div className="p-4 border-b border-white/[0.10] flex items-center gap-3 z-10" style={{background: 'rgba(255,255,255,0.04)'}}>
              <button onClick={() => setActiveChat(null)} className="md:hidden text-slate-500"><ChevronRight className="h-6 w-6 rotate-180" /></button>
              <div className="w-10 h-10 bg-violet-500/25 rounded-full flex items-center justify-center text-violet-400"><User className="h-6 w-6" /></div>
              <div className="font-bold text-slate-100 flex items-center gap-1.5">
                {activeChat.name || activeChat.email}
                {verifiedMap[activeChat.id] && <VerifiedBadge size="md" title={t('verifiedBadgeTooltip')} />}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4" style={{background: 'rgba(0,0,0,0.12)'}}>
              {messages.map(msg => {
                const isMe = msg.senderId === currentUser.uid;
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] p-3.5 rounded-2xl text-sm shadow-md ${isMe ? 'bg-gradient-to-r from-violet-500 to-blue-500 text-white rounded-tr-sm' : 'bg-white/[0.10] backdrop-blur-md border border-white/[0.10] text-slate-100 rounded-tl-sm'} ${msg.isTemp ? 'opacity-60' : ''}`}>
                      {msg.content}
                      <div className={`text-[10px] mt-1 text-right ${isMe ? 'text-slate-500' : 'text-slate-500'}`}>
                        {msg.read && isMe && <span className="flex items-center justify-end gap-1"><CheckCircle2 className="h-3 w-3"/> Read</span>}
                        {msg.isTemp && <span className="text-[9px]">Sending...</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <form onSubmit={handleSend} className="p-4 border-t border-white/[0.10] flex gap-2" style={{background: 'rgba(255,255,255,0.04)'}}>
              <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder={t('typeMessage')} className={`flex-1 p-3 ${glassInput} rounded-xl text-slate-100 placeholder-slate-500`} />
              <button type="submit" disabled={!newMessage.trim()} className={`p-3 ${btnPrimary} rounded-xl disabled:opacity-30`}><Send className="h-5 w-5" /></button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-500 p-8">
            <MessageSquare className="h-16 w-16 mb-4 opacity-30" />
            <p>Select a conversation to start chatting.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// LandingPage
// ==========================================
function LandingPage({ t, onGetStarted, dir }) {
  return (
    <div className="w-full overflow-hidden">
      <div className="relative pt-32 pb-8 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className={`inline-flex items-center justify-center p-5 ${glass} rounded-3xl mb-8`}>
            <Plane className="h-14 w-14 text-violet-400" />
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight tracking-tight bg-gradient-to-r from-white via-indigo-200 to-blue-300 bg-clip-text text-transparent drop-shadow-2xl">
            {t('landingTitle')}
          </h1>
          <p className="text-lg md:text-2xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed font-light">
            {t('landingSubtitle')}
          </p>
          <button onClick={onGetStarted} className={`group ${btnPrimary} text-xl px-12 py-5 rounded-full flex items-center gap-3 mx-auto ring-4 ring-indigo-400/25`}>
            <Sparkles className="h-6 w-6" />
            {t('getStarted')}
            <ArrowRight className={`h-6 w-6 transition-transform group-hover:translate-x-1 ${dir === 'rtl' ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
          </button>
        </div>
      </div>

      <div className="sr-only" aria-hidden="false">
        <h2>FlyCarrier - Peer-to-Peer International Shipping & Travel Companion Platform | ارسال بار با مسافر و مسافر همراه</h2>
        <p>Send packages with travelers worldwide, or find a travel companion for wheelchair or walking passengers. ارسال بسته و بار با مسافر هوایی و خدمت مسافر همراه.</p>
        <p>کولبر و مسافر برای ارسال بار به ایران و از ایران به سراسر جهان. کولبر کانادا، آمریکا، استرالیا، آلمان، انگلیس، دبی و همه شهرهایی که ایرانیان در آن حضور دارند.</p>
      </div>

      <div className="pt-4 pb-16 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Package, title: t('forSendersTitle'), desc: t('forSendersDesc'), gradient: "from-amber-400 to-orange-500" },
              { icon: Plane, title: t('forTravelersTitle'), desc: t('forTravelersDesc'), gradient: "from-indigo-500 to-blue-500" },
              { icon: HeartHandshake, title: t('companionService'), desc: t('companionItemDesc'), gradient: "from-teal-400 to-cyan-500" },
              { icon: ShieldCheck, title: t('secureTitle'), desc: t('secureDesc'), gradient: "from-emerald-400 to-teal-500" }
            ].map((feature, idx) => (
              <div key={idx} className={`group ${glass} p-7 rounded-3xl hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-500`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-gradient-to-br ${feature.gradient} group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-300 leading-relaxed text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Footer
// ==========================================
function Footer({ t, dir }) {
  const [publicStats, setPublicStats] = useState({ showVisits: false, showMembers: false, visits: null, members: null });
  useEffect(() => {
    let unsub = () => {};
    try {
      unsub = onSnapshot(doc(db, 'stats', 'publicSettings'), (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setPublicStats({
            showVisits: d.showVisits === true,
            showMembers: d.showMembers === true,
            visits: d.visitsSnapshot || null,
            members: d.membersSnapshot || null
          });
        }
      }, () => {});
    } catch (e) { /* ignore */ }
    return () => unsub();
  }, []);

  const showVisits = publicStats.showVisits && publicStats.visits;
  const showMembers = publicStats.showMembers && publicStats.members;
  const fmt = (n) => (Number(n) || 0).toLocaleString();

  return (
    <footer className={`border-t border-white/[0.08] py-8 mt-auto relative z-10`} style={{background: 'rgba(10,18,40,0.5)', backdropFilter: 'blur(12px)'}}>
      {(showVisits || showMembers) && (
        <div className="max-w-6xl mx-auto px-4 mb-6">
          <div className="flex flex-wrap justify-center gap-3">
            {showMembers && (
              <div className="rounded-2xl bg-white/[0.05] border border-violet-400/20 px-5 py-3 flex items-center gap-3">
                <div className="p-2 rounded-xl bg-violet-500/15 text-violet-300"><Users className="h-5 w-5" /></div>
                <div>
                  <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{t('footerMembersLabel')}</div>
                  <div className="text-xl font-black text-white leading-tight">{fmt(publicStats.members.total)}</div>
                </div>
              </div>
            )}
            {showVisits && (
              <div className="rounded-2xl bg-white/[0.05] border border-sky-400/20 px-5 py-3 flex items-center gap-4">
                <div className="p-2 rounded-xl bg-sky-500/15 text-sky-300"><Eye className="h-5 w-5" /></div>
                <div className="flex items-center gap-4">
                  <div><div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{t('statToday')}</div><div className="text-lg font-black text-white leading-tight">{fmt(publicStats.visits.today)}</div></div>
                  <div className="w-px h-8 bg-white/[0.10]" />
                  <div><div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{t('statWeek')}</div><div className="text-lg font-black text-white leading-tight">{fmt(publicStats.visits.week)}</div></div>
                  <div className="w-px h-8 bg-white/[0.10]" />
                  <div><div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{t('statMonth')}</div><div className="text-lg font-black text-white leading-tight">{fmt(publicStats.visits.month)}</div></div>
                  <div className="w-px h-8 bg-white/[0.10]" />
                  <div><div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{t('footerVisitsLabel')} · {t('statTotal')}</div><div className="text-lg font-black text-white leading-tight">{fmt(publicStats.visits.total)}</div></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 px-4 text-center md:text-left">
        <div className="flex items-center gap-2 text-slate-400">
          <Plane className="h-5 w-5 text-indigo-400" />
          <span className="font-bold text-slate-200 tracking-tight">FlyCarrier</span>
          <span className="text-slate-500 text-xs">— www.fly-carrier.com</span>
        </div>
        <div className="flex flex-col items-center gap-3 order-first md:order-none">
          <BrandLogo height={56} className="opacity-95 drop-shadow-[0_2px_10px_rgba(99,102,241,0.25)]" />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href="https://t.me/flycarriercombot" target="_blank" rel="noopener noreferrer" title={t('footerTelegramBot')} aria-label={t('footerTelegramBot')} className="h-10 px-4 rounded-xl bg-sky-500/15 border border-sky-400/30 hover:bg-sky-500/25 flex items-center justify-center gap-2 text-sky-300 font-bold text-sm transition-all hover:scale-105">
              <Send className="h-4 w-4 flex-shrink-0" /> <span>{t('footerTelegramBot')}</span>
            </a>
            <a href="https://instagram.com/fly.carrier" target="_blank" rel="noopener noreferrer" title={t('footerInstagram')} aria-label={t('footerInstagram')} className="h-10 px-4 rounded-xl bg-pink-500/15 border border-pink-400/30 hover:bg-pink-500/25 flex items-center justify-center gap-2 text-pink-300 font-bold text-sm transition-all hover:scale-105">
              <Instagram className="h-4 w-4 flex-shrink-0" /> <span>{t('footerInstagram')}</span>
            </a>
          </div>
        </div>
        <div className={`${dir === 'rtl' ? 'md:text-left' : 'md:text-right'}`}>
          <p className="text-slate-300 text-sm font-medium">© {new Date().getFullYear()} {t('appTitle')}. {t('footerRights')}</p>
          <p className="text-slate-500 text-xs mt-1">
            {t('footerCredit')} <span className="font-semibold text-indigo-400 hover:text-indigo-300 cursor-pointer transition-colors">TORABINEJAD AI</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

// ==========================================
// VerifyEmailScreen / BannedScreen / VerifiedBadge / InfoItem
// ==========================================
function VerifyEmailScreen({ user, t, onVerified }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const checkVerification = async () => {
    setLoading(true);
    try { await reload(user); if (user.emailVerified) { onVerified(); } else { setMsg(t('notVerifiedYet')); } } catch (e) { setMsg("Error checking status."); } finally { setLoading(false); }
  };
  const resendEmail = async () => {
    setLoading(true);
    try { await sendEmailVerification(user); setMsg(t('emailSent') + " ✓"); } catch (e) { setMsg("Error sending email. Try again later."); } finally { setLoading(false); }
  };
  return (
    <div className="max-w-lg mx-auto space-y-6 animate-in zoom-in-95 duration-500">
      <div className={`${glass} p-10 rounded-3xl text-center`}>
        <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-amber-500/30">
          <Mail className="h-10 w-10 text-slate-100" />
        </div>
        <h2 className="text-3xl font-extrabold text-white mb-4">{t('verifyTitle')}</h2>
        <p className="text-slate-500 mb-3 leading-relaxed text-lg">{t('verifyDesc')}</p>
        <p className="text-violet-400 font-bold text-lg mb-8 break-all">{user.email}</p>
        {msg && (<div className={`mb-6 p-4 rounded-2xl text-sm font-bold ${msg.includes('✓') || msg.includes('sent') || msg.includes('ارسال') ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/20' : 'bg-rose-500/20 text-rose-300 border border-rose-400/20'}`}>{msg}</div>)}
        <button onClick={checkVerification} disabled={loading} className={`w-full ${btnPrimary} py-4 rounded-2xl flex items-center justify-center gap-2 text-lg mb-4`}>
          {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><RefreshCcw className="h-5 w-5" />{t('checkVerification')}</>}
        </button>
        <button onClick={resendEmail} disabled={loading} className="text-violet-400 text-sm font-bold hover:text-slate-200 hover:underline transition-colors">{t('resendBtn')}</button>
      </div>
      <div className="relative overflow-hidden rounded-2xl" style={{background: 'rgba(251,191,36,0.12)', border: '1.5px solid rgba(251,191,36,0.3)'}}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 animate-pulse"></div>
        <div className="p-6 flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-amber-400/20 rounded-xl flex items-center justify-center mt-0.5"><AlertTriangle className="h-7 w-7 text-amber-400" /></div>
          <div className="flex-1"><h4 className="text-amber-300 font-extrabold text-base mb-2 flex items-center gap-2"><Info className="h-4 w-4" />Important Notice</h4><p className="text-amber-100/80 leading-relaxed text-[15px]">{t('spamNotice')}</p></div>
        </div>
      </div>
      <div className="text-center pt-2"><button onClick={() => signOut(getAuth())} className="text-slate-500 text-sm hover:text-rose-400 flex items-center justify-center gap-1.5 mx-auto transition-colors"><LogOut className="h-4 w-4" /> {t('logout')}</button></div>
    </div>
  );
}

function VerifiedBadge({ size = 'md', title }) {
  const px = { sm: 'h-3 w-3', md: 'h-4 w-4', lg: 'h-5 w-5', xl: 'h-6 w-6' }[size] || 'h-4 w-4';
  return (
    <span className="inline-flex items-center justify-center flex-shrink-0" title={title || 'Verified'} aria-label="Verified user">
      <BadgeCheck className={`${px} text-sky-400 drop-shadow-[0_0_4px_rgba(56,189,248,0.55)]`} strokeWidth={2.5} fill="currentColor" stroke="#0c1330" />
    </span>
  );
}

function BannedScreen({ user, t, onLogout }) {
  return (
    <div className="max-w-lg mx-auto space-y-6 animate-in zoom-in-95 duration-500">
      <div className={`${glass} p-10 rounded-3xl text-center border-2 border-rose-400/30`}>
        <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-rose-500/30">
          <Ban className="h-10 w-10 text-slate-100" />
        </div>
        <h2 className="text-3xl font-extrabold text-white mb-4">{t('bannedTitle')}</h2>
        <p className="text-slate-500 mb-3 leading-relaxed text-base">{t('bannedMsg')}</p>
        <p className="text-slate-500 text-sm mb-8 break-all">{user.email}</p>
        <button onClick={onLogout} className="w-full py-3.5 rounded-2xl bg-white/[0.08] hover:bg-white/[0.14] text-slate-100 font-bold flex items-center justify-center gap-2 transition-all">
          <LogOut className="h-5 w-5" /> {t('logout')}
        </button>
      </div>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-slate-200">
      {Icon && <Icon className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <span className="text-slate-500 text-xs font-bold uppercase tracking-wider mr-1.5">{label}:</span>
        <span className="text-slate-100/90 font-medium break-words">{value}</span>
      </div>
    </div>
  );
}

// ==========================================
// AccountSettings — standalone page for email, password & display name
// Separate from ProfileForm. Reached via its own button in Navbar + Dashboard.
// Hidden entirely for Telegram users (they have no email/password).
// ==========================================
function AccountSettings({ user, profile, onBack, t, dir }) {
  const isTelegramUser = profile?.signupSource === 'telegram';

  const [displayName, setDisplayName] = useState(profile?.fullName || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg, setNameMsg] = useState('');

  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [emailErr, setEmailErr] = useState('');

  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  const handleNameUpdate = async () => {
    setNameMsg('');
    const trimmed = (displayName || '').trim();
    if (!trimmed) return;
    setNameLoading(true);
    try {
      await setDoc(doc(db, 'users', user.uid), { fullName: trimmed, updatedAt: serverTimestamp() }, { merge: true });
      setNameMsg(t('nameUpdated'));
    } catch (err) { setNameMsg(err?.message || t('saveFailed')); }
    finally { setNameLoading(false); }
  };

  const handleEmailUpdate = async () => {
    setEmailMsg(''); setEmailErr('');
    const trimmed = (newEmail || '').trim();
    if (!trimmed) { setEmailErr(t('invalidEmailError')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setEmailErr(t('invalidEmailError')); return; }
    if (trimmed.toLowerCase() === (user?.email || '').toLowerCase()) { setEmailErr(t('sameEmailError')); return; }
    setEmailLoading(true);
    try {
      await verifyBeforeUpdateEmail(user, trimmed);
      setEmailMsg(t('emailUpdateSent'));
      setNewEmail('');
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') setEmailErr(t('reauthRequired'));
      else if (err.code === 'auth/invalid-email') setEmailErr(t('invalidEmailError'));
      else setEmailErr(err.message || t('emailUpdateError'));
    } finally { setEmailLoading(false); }
  };

  const handlePasswordReset = async () => {
    setPwMsg('');
    if (!user?.email) return;
    setPwLoading(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      setPwMsg(t('resetLinkSent'));
    } catch (err) { setPwMsg(err.message || t('saveFailed')); }
    finally { setPwLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in duration-500" dir={dir}>
      <div className={`${glass} rounded-3xl p-8 sm:p-10`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-extrabold text-white">{t('accountSettings')}</h2>
              <p className="text-slate-500 text-sm">{t('accountSettingsDesc')}</p>
            </div>
          </div>
          <button onClick={onBack} className="px-4 py-2.5 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] text-slate-100 font-bold text-sm flex items-center gap-2 transition-all">
            <ChevronRight className={`h-4 w-4 ${dir === 'rtl' ? '' : 'rotate-180'}`} /> {t('back')}
          </button>
        </div>

        {/* Display Name — available to everyone (including Telegram users) */}
        <div className="rounded-2xl p-5 border-2 border-violet-400/25 bg-violet-500/[0.06] mb-5">
          <div className="flex items-center gap-2 mb-3">
            <UserCircle2 className="h-5 w-5 text-violet-400" />
            <h3 className="text-slate-100 font-extrabold text-base">{t('displayName')}</h3>
          </div>
          <p className="text-slate-500 text-[13px] mb-3 leading-relaxed">{t('displayNameHint')}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <User className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" style={dir === 'rtl' ? { right: '0.75rem' } : { left: '0.75rem' }} />
              <input type="text" value={displayName} onChange={(e) => { setDisplayName(e.target.value); setNameMsg(''); }} placeholder="John Doe" className={`w-full py-2.5 ${dir === 'rtl' ? 'pr-10 pl-3' : 'pl-10 pr-3'} rounded-xl text-slate-100 text-sm ${glassInput}`} />
            </div>
            <button type="button" onClick={handleNameUpdate} disabled={nameLoading || !displayName?.trim()} className="px-4 py-2.5 rounded-xl bg-violet-500/20 hover:bg-violet-500/30 border border-violet-400/40 text-violet-300 font-bold text-sm disabled:opacity-40 transition-all flex items-center justify-center gap-1.5 whitespace-nowrap">
              {nameLoading ? <div className="w-4 h-4 border-2 border-violet-400 border-t-violet-600 rounded-full animate-spin" /> : <><UserCircle2 className="h-3.5 w-3.5" /> {t('updateName')}</>}
            </button>
          </div>
          {nameMsg && (
            <div className="mt-3 rounded-lg bg-emerald-500/12 border border-emerald-400/30 p-3 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <p className="text-emerald-300 text-xs font-bold leading-relaxed">{nameMsg}</p>
            </div>
          )}
        </div>

        {/* Email + Password — only for email-based (web) accounts */}
        {isTelegramUser ? (
          <div className="rounded-2xl p-5 border-2 border-sky-400/25 bg-sky-500/[0.06] flex items-start gap-3">
            <Send className="h-5 w-5 text-sky-300 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sky-200 font-extrabold text-sm mb-1">{t('telegramAccountTitle')}</h3>
              <p className="text-sky-100/75 text-[13px] leading-relaxed">{t('telegramAccountDesc')}</p>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl p-5 border-2 border-violet-400/25 bg-violet-500/[0.06]">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-5 w-5 text-violet-400" />
              <h3 className="text-slate-100 font-extrabold text-base">{t('accountSecurity')}</h3>
            </div>

            <div className="mb-5">
              <label className="block text-slate-500 text-xs font-bold mb-1.5">{t('currentEmail')}</label>
              <p className="text-slate-200 text-sm mb-3 font-mono bg-white/[0.08] rounded-lg px-3 py-2 border border-white/[0.10] break-all">{user?.email || '—'}</p>
              <label className="block text-slate-500 text-xs font-bold mb-1.5">{t('newEmail')}</label>
              <p className="text-slate-500 text-[11px] mb-2 leading-relaxed">{t('updateEmailDesc')}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" style={dir === 'rtl' ? { right: '0.75rem' } : { left: '0.75rem' }} />
                  <input type="email" value={newEmail} onChange={(e) => { setNewEmail(e.target.value); setEmailErr(''); setEmailMsg(''); }} placeholder="new@example.com" autoComplete="off" className={`w-full py-2.5 ${dir === 'rtl' ? 'pr-10 pl-3' : 'pl-10 pr-3'} rounded-xl text-slate-100 text-sm ${glassInput}`} />
                </div>
                <button type="button" onClick={handleEmailUpdate} disabled={emailLoading || !newEmail} className="px-4 py-2.5 rounded-xl bg-violet-500/20 hover:bg-violet-500/30 border border-violet-400/40 text-violet-300 font-bold text-sm disabled:opacity-40 transition-all flex items-center justify-center gap-1.5 whitespace-nowrap">
                  {emailLoading ? <div className="w-4 h-4 border-2 border-violet-400 border-t-violet-600 rounded-full animate-spin" /> : <><Mail className="h-3.5 w-3.5" /> {t('updateEmail')}</>}
                </button>
              </div>
              {emailMsg && (
                <div className="mt-3 rounded-lg bg-emerald-500/12 border border-emerald-400/30 p-3 flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-emerald-300 text-xs font-bold leading-relaxed">{emailMsg}</p>
                </div>
              )}
              {emailErr && (
                <div className="mt-3 rounded-lg bg-rose-500/12 border border-rose-400/30 p-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-rose-400 flex-shrink-0 mt-0.5" />
                  <p className="text-rose-300 text-xs font-bold leading-relaxed">{emailErr}</p>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-white/[0.10]">
              <label className="block text-slate-500 text-xs font-bold mb-1.5">{t('changePassword')}</label>
              <p className="text-slate-500 text-[11px] mb-3 leading-relaxed">{t('changePasswordHint')}</p>
              <button type="button" onClick={handlePasswordReset} disabled={pwLoading} className="px-4 py-2.5 rounded-xl bg-violet-500/20 hover:bg-violet-500/30 border border-violet-400/40 text-violet-300 font-bold text-sm disabled:opacity-40 transition-all flex items-center gap-1.5">
                {pwLoading ? <div className="w-4 h-4 border-2 border-violet-400 border-t-violet-600 rounded-full animate-spin" /> : <><KeyRound className="h-3.5 w-3.5" /> {t('changePassword')}</>}
              </button>
              {pwMsg && (
                <div className="mt-3 rounded-lg bg-emerald-500/12 border border-emerald-400/30 p-3 flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-emerald-300 text-xs font-bold leading-relaxed">{pwMsg}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// ProfileForm — 4 steps
// CHANGES vs original:
//  • Dimensions (length/width/height) fully removed everywhere
//  • Weight is optional (never blocks progression)
//  • Travel companion is now a normal item in PACKAGE_TYPES ("companion")
//    - Traveler: after selecting companion, choose which passengers they accept
//      (wheelchair and/or walking) -> stored in companionAccept {wheelchair, walking}
//    - Sender: after selecting companion, choose passenger mobility (single)
//      -> stored in companionNeed 'wheelchair' | 'walking'
//    - Companion rate uses ratesPerType.companion like any other item
//  • Persian option labels come from translations
//  • Account security (email/password/display name) moved to AccountSettings
// ==========================================
function ProfileForm({ user, existingData, onComplete, onCancel, t, dir, lang }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Scroll back to the top of the form whenever the step changes, so the
  // next/previous step is visible instead of leaving the user scrolled
  // down at the bottom of the (now different) content.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const [role, setRole] = useState(existingData?.role || '');
  const [originCountry, setOriginCountry] = useState(existingData?.originCountry || '');
  const [originCity, setOriginCity] = useState(existingData?.originCity || '');
  const [destCountry, setDestCountry] = useState(existingData?.destCountry || '');
  const [destCity, setDestCity] = useState(existingData?.destCity || '');

  // Sender fields (dimensions removed; weight optional; MULTIPLE items supported)
  // senderItems is an array of package type ids (like traveler's acceptedItems).
  // Per-item weight lives in itemSpecs[id].weight, per-item price in ratesPerType[id].
  // Legacy migration: old profiles stored a single packageType (+ weight) — fold it in.
  const [senderItems, setSenderItems] = useState(() => {
    if (Array.isArray(existingData?.senderItems) && existingData.senderItems.length > 0) return existingData.senderItems;
    if (existingData?.packageType) return [existingData.packageType]; // migrate old single value
    return [];
  });
  const [preferredDate, setPreferredDate] = useState(existingData?.preferredDate || '');
  // Sender companion need — single choice, only relevant when 'companion' is among senderItems
  const [companionNeed, setCompanionNeed] = useState(existingData?.companionNeed || '');

  // Traveler fields
  const [hasTicket, setHasTicket] = useState(existingData?.hasTicket !== undefined ? existingData.hasTicket : !existingData?.approxDate);
  const [flightDate, setFlightDate] = useState(existingData?.flightDate || '');
  const [flightTime, setFlightTime] = useState(existingData?.flightTime || '');
  const [flightNumber, setFlightNumber] = useState(existingData?.flightNumber || '');
  const [approxDate, setApproxDate] = useState(existingData?.approxDate || '');
  const [acceptedItems, setAcceptedItems] = useState(existingData?.acceptedItems || []);
  // itemSpecs carries weight per item (optional), shared by sender & traveler.
  // Legacy migration: old sender profiles kept a top-level weight for their single item.
  const [itemSpecs, setItemSpecs] = useState(() => {
    const base = existingData?.itemSpecs ? { ...existingData.itemSpecs } : {};
    if (existingData?.packageType && existingData?.weight && !base[existingData.packageType]) {
      base[existingData.packageType] = { weight: existingData.weight };
    }
    return base;
  });
  // Traveler companion acceptance — can accept wheelchair and/or walking
  const [companionAccept, setCompanionAccept] = useState(
    existingData?.companionAccept || { wheelchair: false, walking: false }
  );

  // Price fields — per-item rates (shared by sender & traveler)
  const [ratesPerType, setRatesPerType] = useState(existingData?.ratesPerType || {});
  const [rateCurrency, setRateCurrency] = useState(existingData?.rateCurrency || 'USD');

  // Contact fields
  const [fullName, setFullName] = useState(existingData?.fullName || '');
  const [phone, setPhone] = useState(existingData?.phone || '');
  const [phoneHidden, setPhoneHidden] = useState(existingData?.phoneHidden || false);
  const [recipientPhone, setRecipientPhone] = useState(existingData?.recipientPhone || '');
  const [recipientFullName, setRecipientFullName] = useState(existingData?.recipientFullName || '');

  // Social media
  const [whatsapp, setWhatsapp] = useState(existingData?.whatsapp || '');
  const [telegram, setTelegram] = useState(existingData?.telegram || '');
  const [signal, setSignal] = useState(existingData?.signal || '');
  const [instagram, setInstagram] = useState(existingData?.instagram || '');
  const [facebook, setFacebook] = useState(existingData?.facebook || '');

  const countries = Object.keys(COUNTRIES_AND_CITIES);
  const originCities = COUNTRIES_AND_CITIES[originCountry] || [];
  const destCities = COUNTRIES_AND_CITIES[destCountry] || [];
  const totalSteps = 4;

  const toggleItem = (id) => {
    setAcceptedItems((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSenderItem = (id) => {
    setSenderItems((prev) => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (id === 'companion' && prev.includes(id)) setCompanionNeed(''); // deselecting companion clears need
      return next;
    });
  };

  const senderPickedCompanion = senderItems.includes('companion');
  const travelerPickedCompanion = acceptedItems.includes('companion');
  const senderPickedCustom = senderItems.includes('custom');
  const travelerPickedCustom = acceptedItems.includes('custom');
  const customLabelFilled = !!(itemSpecs.custom?.label || '').trim();

  const canProceed = () => {
    if (step === 1) return !!role;
    if (step === 2) return originCountry && originCity && destCountry && destCity;
    if (step === 3) {
      if (role === 'sender') {
        if (senderItems.length === 0) return false;
        // If sender chose companion item, they must pick the passenger mobility
        if (senderPickedCompanion && !companionNeed) return false;
        if (senderPickedCustom && !customLabelFilled) return false;
        return true; // weight is optional now
      }
      // Traveler: must accept at least one item, plus either an exact flight date
      // (if ticket already bought) or an approximate year/month (if not).
      if (acceptedItems.length === 0) return false;
      if (hasTicket ? !flightDate : !approxDate) return false;
      // If traveler accepts companion, they must choose at least one companion type
      if (travelerPickedCompanion && !companionAccept.wheelchair && !companionAccept.walking) return false;
      if (travelerPickedCustom && !customLabelFilled) return false;
      return true;
    }
    if (step === 4) return fullName && (phone || phoneHidden);
    return true;
  };

  const stepErrorMessage = () => {
    if (step === 3) {
      if (role === 'sender' && senderItems.length === 0) return t('senderPickAtLeastOne');
      if (role === 'sender' && senderPickedCompanion && !companionNeed) return t('companionSenderPickOne');
      if (senderPickedCustom || travelerPickedCustom) {
        if (!customLabelFilled) return t('customItemRequired');
      }
      if (role === 'traveler' && travelerPickedCompanion && !companionAccept.wheelchair && !companionAccept.walking) return t('companionPickAtLeastOne');
    }
    return t('fillRequiredFields');
  };

  const handleNext = () => {
    if (!canProceed()) { setError(stepErrorMessage()); return; }
    setError('');
    if (step < totalSteps) setStep(step + 1);
  };
  const handleBack = () => { setError(''); if (step > 1) setStep(step - 1); };

  const handleSave = async () => {
    if (!canProceed()) { setError(stepErrorMessage()); return; }
    setSaving(true);
    setError('');

    const payload = {
      role,
      originCountry, originCity, destCountry, destCity,
      fullName,
      phone: phone || '',
      phoneHidden: !!phoneHidden,
      whatsapp: whatsapp || '',
      telegram: telegram || '',
      signal: signal || '',
      instagram: instagram || '',
      facebook: facebook || '',
      updatedAt: serverTimestamp()
    };

    if (role === 'sender') {
      payload.senderItems = senderItems;
      payload.packageType = '';   // clear legacy single field to avoid stale matches
      payload.weight = '';        // legacy single weight no longer used
      payload.preferredDate = preferredDate || '';
      payload.recipientPhone = recipientPhone || '';
      payload.recipientFullName = recipientFullName || '';
      // Companion need only meaningful when companion is among the items
      payload.companionNeed = senderPickedCompanion ? (companionNeed || '') : '';

      // Per-item weight (optional) and quantity (optional, e.g. full suitcase), only for selected items, never dimensions, skip companion
      const cleanedSpecs = {};
      senderItems.forEach(itemId => {
        if (itemId === 'companion') return;
        const spec = itemSpecs[itemId];
        if (spec && (spec.weight || spec.quantity || spec.label)) {
          const s = {};
          if (spec.weight) s.weight = Number(spec.weight);
          if (spec.quantity) s.quantity = Number(spec.quantity);
          if (spec.label) s.label = spec.label.trim();
          cleanedSpecs[itemId] = s;
        }
      });
      payload.itemSpecs = cleanedSpecs;

      // Per-item price (optional), only for selected items
      const cleanedRates = {};
      senderItems.forEach(itemId => {
        const v = ratesPerType[itemId];
        if (v !== '' && v !== null && v !== undefined && !isNaN(Number(v))) cleanedRates[itemId] = Number(v);
      });
      if (Object.keys(cleanedRates).length > 0) {
        payload.ratesPerType = cleanedRates;
        payload.rateCurrency = rateCurrency;
      }
    } else {
      payload.hasTicket = !!hasTicket;
      payload.flightDate = hasTicket ? flightDate : '';
      payload.flightTime = hasTicket ? (flightTime || '') : '';
      payload.flightNumber = hasTicket ? (flightNumber || '') : '';
      payload.approxDate = hasTicket ? '' : approxDate;
      payload.acceptedItems = acceptedItems;

      // itemSpecs: weight and quantity (optional, e.g. full suitcase), never dimensions
      const cleanedSpecs = {};
      acceptedItems.forEach(itemId => {
        const spec = itemSpecs[itemId];
        if (spec && (spec.weight || spec.quantity || spec.label)) {
          const s = {};
          if (spec.weight) s.weight = Number(spec.weight);
          if (spec.quantity) s.quantity = Number(spec.quantity);
          if (spec.label) s.label = spec.label.trim();
          cleanedSpecs[itemId] = s;
        }
      });
      payload.itemSpecs = cleanedSpecs;

      const cleanedRates = {};
      Object.keys(ratesPerType || {}).forEach(k => {
        const v = ratesPerType[k];
        if (v !== '' && v !== null && !isNaN(Number(v))) cleanedRates[k] = Number(v);
      });
      if (Object.keys(cleanedRates).length > 0) {
        payload.ratesPerType = cleanedRates;
        payload.rateCurrency = rateCurrency;
      }

      // Companion acceptance object; only meaningful when companion is accepted
      payload.companionAccept = travelerPickedCompanion
        ? { wheelchair: companionAccept.wheelchair === true, walking: companionAccept.walking === true }
        : { wheelchair: false, walking: false };
    }

    try {
      await setDoc(doc(db, 'users', user.uid), payload, { merge: true });
      onComplete && onComplete();
    } catch (err) {
      setError(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const StepDot = ({ n }) => (
    <div className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${step >= n ? 'bg-gradient-to-r from-violet-400 to-fuchsia-400' : 'bg-white/10'}`}></div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in duration-500" dir={dir}>
      <div className={`${glass} rounded-3xl p-8 sm:p-10`}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white">{existingData?.role ? t('editProfile') : t('createProfile')}</h2>
          <span className="text-slate-500 text-sm font-bold">{step} / {totalSteps}</span>
        </div>
        <div className="flex gap-2 mb-8 mt-3">
          <StepDot n={1} /><StepDot n={2} /><StepDot n={3} /><StepDot n={4} />
        </div>

        {/* Step 1 — Role */}
        {step === 1 && (
          <div className="space-y-6">
            <h3 className="text-xl font-extrabold text-white mb-2">{t('chooseRole')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button type="button" onClick={() => setRole('sender')} className={`p-6 rounded-2xl border-2 transition-all duration-300 ${role === 'sender' ? 'border-fuchsia-400 bg-fuchsia-500/15 shadow-lg shadow-fuchsia-500/20' : 'border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.08]'}`}>
                <Package className={`h-10 w-10 mx-auto mb-3 ${role === 'sender' ? 'text-violet-500' : 'text-slate-500'}`} />
                <div className="text-slate-100 font-extrabold text-lg">{t('roleSender')}</div>
              </button>
              <button type="button" onClick={() => setRole('traveler')} className={`p-6 rounded-2xl border-2 transition-all duration-300 ${role === 'traveler' ? 'border-violet-400 bg-violet-500/15 shadow-lg shadow-violet-500/20' : 'border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.08]'}`}>
                <Plane className={`h-10 w-10 mx-auto mb-3 ${role === 'traveler' ? 'text-violet-400' : 'text-slate-500'}`} />
                <div className="text-slate-100 font-extrabold text-lg">{t('roleTraveler')}</div>
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Locations */}
        {step === 2 && (
          <div className="space-y-5">
            <h3 className="text-xl font-extrabold text-white mb-2 flex items-center gap-2"><MapPin className="h-5 w-5 text-violet-400" /> {t('originCountry')} → {t('destCountry')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-500 text-sm font-bold mb-2">{t('originCountry')}</label>
                <select value={originCountry} onChange={(e) => { setOriginCountry(e.target.value); setOriginCity(''); }} className={`w-full py-3 px-4 rounded-2xl text-slate-100 ${glassInput}`}>
                  <option value="" className="bg-violet-950">{t('selectCountry')}</option>
                  {countries.map(c => <option key={c} value={c} className="bg-violet-950">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-slate-500 text-sm font-bold mb-2">{t('originCity')}</label>
                <select value={originCity} onChange={(e) => setOriginCity(e.target.value)} disabled={!originCountry} className={`w-full py-3 px-4 rounded-2xl text-slate-100 ${glassInput} disabled:opacity-40`}>
                  <option value="" className="bg-violet-950">{t('selectCity')}</option>
                  {originCities.map(c => <option key={c} value={c} className="bg-violet-950">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-slate-500 text-sm font-bold mb-2">{t('destCountry')}</label>
                <select value={destCountry} onChange={(e) => { setDestCountry(e.target.value); setDestCity(''); }} className={`w-full py-3 px-4 rounded-2xl text-slate-100 ${glassInput}`}>
                  <option value="" className="bg-violet-950">{t('selectCountry')}</option>
                  {countries.map(c => <option key={c} value={c} className="bg-violet-950">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-slate-500 text-sm font-bold mb-2">{t('destCity')}</label>
                <select value={destCity} onChange={(e) => setDestCity(e.target.value)} disabled={!destCountry} className={`w-full py-3 px-4 rounded-2xl text-slate-100 ${glassInput} disabled:opacity-40`}>
                  <option value="" className="bg-violet-950">{t('selectCity')}</option>
                  {destCities.map(c => <option key={c} value={c} className="bg-violet-950">{c}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Sender Details (MULTIPLE items, per-item weight & price) */}
        {step === 3 && role === 'sender' && (
          <div className="space-y-5">
            <h3 className="text-xl font-extrabold text-white mb-2 flex items-center gap-2"><Package className="h-5 w-5 text-violet-500" /> {t('packageDetails')}</h3>

            {/* Multi-select item chips */}
            <div>
              <label className="block text-slate-500 text-sm font-bold mb-3">{t('whatSendingMulti')}</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {PACKAGE_TYPES.map(p => {
                  const active = senderItems.includes(p.id);
                  const isComp = p.isCompanion;
                  return (
                    <button key={p.id} type="button" onClick={() => toggleSenderItem(p.id)} className={`px-3 py-2.5 rounded-xl text-sm font-bold border-2 transition-all flex items-center justify-center gap-1.5 ${active ? (isComp ? 'border-teal-400/60 bg-teal-500/20 text-teal-300 shadow-sm' : 'border-fuchsia-400/60 bg-fuchsia-500/20 text-fuchsia-200 shadow-sm') : 'border-white/[0.10] bg-white/[0.06] text-slate-500 hover:bg-white/[0.08] hover:border-white/[0.14]'}`}>
                      {isComp && <HeartHandshake className="h-3.5 w-3.5" />}
                      {pkgTypeLabel(lang, p.id)}
                    </button>
                  );
                })}
              </div>
              {senderItems.length === 0 && <p className="text-amber-300/80 text-xs mt-3 flex items-center gap-1.5"><Info className="h-3 w-3" /> {t('senderPickAtLeastOne')}</p>}
            </div>

            {/* Companion mobility choice (only when companion is among items) */}
            {senderPickedCompanion && (
              <div className="rounded-2xl p-5 border-2 border-teal-400/30 bg-teal-500/[0.07]">
                <div className="flex items-center gap-2 mb-2">
                  <HeartHandshake className="h-5 w-5 text-teal-300" />
                  <h3 className="text-teal-100 font-extrabold text-base">{t('companionSenderNeedTitle')}</h3>
                </div>
                <p className="text-teal-100/70 text-sm mb-4 leading-relaxed">{t('companionSenderItemDesc')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button type="button" onClick={() => setCompanionNeed('wheelchair')} className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${companionNeed === 'wheelchair' ? 'border-teal-400 bg-teal-500/20 shadow-lg shadow-teal-500/20' : 'border-white/[0.10] bg-white/[0.05] hover:bg-white/[0.08]'}`}>
                    <Accessibility className={`h-6 w-6 flex-shrink-0 ${companionNeed === 'wheelchair' ? 'text-teal-300' : 'text-slate-500'}`} />
                    <span className={`text-sm font-bold text-left ${companionNeed === 'wheelchair' ? 'text-slate-100' : 'text-slate-400'}`}>{t('companionNeedWheelchair')}</span>
                  </button>
                  <button type="button" onClick={() => setCompanionNeed('walking')} className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${companionNeed === 'walking' ? 'border-teal-400 bg-teal-500/20 shadow-lg shadow-teal-500/20' : 'border-white/[0.10] bg-white/[0.05] hover:bg-white/[0.08]'}`}>
                    <PersonStanding className={`h-6 w-6 flex-shrink-0 ${companionNeed === 'walking' ? 'text-teal-300' : 'text-slate-500'}`} />
                    <span className={`text-sm font-bold text-left ${companionNeed === 'walking' ? 'text-slate-100' : 'text-slate-400'}`}>{t('companionNeedWalking')}</span>
                  </button>
                </div>
                {!companionNeed && <p className="text-amber-300/80 text-xs mt-3 flex items-center gap-1.5"><Info className="h-3 w-3" /> {t('companionSenderPickOne')}</p>}
                <div className="mt-3 rounded-xl bg-amber-500/10 border border-amber-400/25 p-3 flex items-start gap-2">
                  <Info className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-100/80 text-[11px] leading-relaxed">{t('companionSafetyNote')}</p>
                </div>
              </div>
            )}

            {/* Per-item weight (optional) + price — dimensions removed, companion has neither weight */}
            {senderItems.length > 0 && (
              <div className="rounded-2xl p-5 mt-2 border-2 border-fuchsia-400/30 bg-fuchsia-500/10">
                <div className="flex items-center gap-2 mb-2">
                  <Box className="h-5 w-5 text-fuchsia-300" />
                  <h3 className="text-slate-100 font-extrabold text-base">{t('senderPerItemSpecs')}</h3>
                </div>
                <p className="text-slate-500 text-sm mb-4 leading-relaxed">{t('senderPerItemSpecsDesc')}</p>
                <div className="mb-4">
                  <label className="block text-slate-500 text-xs font-bold mb-2">{t('currency')}</label>
                  <select value={rateCurrency} onChange={(e) => setRateCurrency(e.target.value)} className={`w-full sm:w-1/3 py-2.5 px-3 rounded-xl ${glassInput}`}>
                    <option value="USD" className="bg-violet-950 text-slate-100">USD ($)</option>
                    <option value="EUR" className="bg-violet-950 text-slate-100">EUR (€)</option>
                    <option value="GBP" className="bg-violet-950 text-slate-100">GBP (£)</option>
                    <option value="CAD" className="bg-violet-950 text-slate-100">CAD (C$)</option>
                    <option value="AUD" className="bg-violet-950 text-slate-100">AUD (A$)</option>
                    <option value="CHF" className="bg-violet-950 text-slate-100">CHF (Fr)</option>
                  </select>
                </div>
                <div className="space-y-3">
                  {senderItems.map(itemId => {
                    const pt = PACKAGE_TYPES.find(p => p.id === itemId);
                    const pricing = PACKAGE_PRICING[itemId];
                    if (!pt) return null;
                    const isComp = pt.isCompanion;
                    const spec = itemSpecs[itemId] || {};
                    const updateSpec = (field, val) => { setItemSpecs({ ...itemSpecs, [itemId]: { ...spec, [field]: val } }); };
                    return (
                      <div key={itemId} className={`rounded-xl p-4 border ${isComp ? 'bg-teal-500/[0.06] border-teal-400/20' : 'bg-white/[0.08] border-white/[0.10]'}`}>
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <span className="font-extrabold text-slate-100 text-sm flex items-center gap-2">
                            {isComp ? <HeartHandshake className="h-4 w-4 text-teal-300" /> : <Box className="h-4 w-4 text-fuchsia-300" />} {pkgTypeLabel(lang, itemId, itemSpecs)}
                          </span>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-emerald-400" />
                            <input type="number" value={ratesPerType[itemId] || ''} onChange={(e) => setRatesPerType({ ...ratesPerType, [itemId]: e.target.value })} className={`w-20 py-1.5 px-2 rounded-lg text-sm ${glassInput}`} placeholder={pricing ? `~${pricing.defaultUSD}` : '0'} />
                            <span className="text-slate-500 text-[10px] font-bold">{pricing ? t(pricing.unit) : t('perItem')}</span>
                          </div>
                        </div>
                        {itemId === 'custom' && (
                          <div className="mb-2">
                            <label className="block text-slate-500 text-[10px] font-bold uppercase mb-1">{t('customItemLabel')}</label>
                            <input type="text" value={spec.label || ''} onChange={(e) => updateSpec('label', e.target.value)} placeholder={t('customItemPlaceholder')} className={`w-full py-2 px-2.5 rounded-lg text-sm ${glassInput}`} />
                          </div>
                        )}
                        {/* Companion has no weight; quantity-based items (e.g. full suitcase) show count then weight each; other items show optional weight only */}
                        {!isComp && pt.hasQuantity && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-slate-500 text-[10px] font-bold uppercase mb-1">{t('quantityOptional')}</label>
                              <input type="number" value={spec.quantity || ''} onChange={(e) => updateSpec('quantity', e.target.value)} placeholder="1" className={`w-full py-2 px-2.5 rounded-lg text-sm ${glassInput}`} />
                            </div>
                            <div>
                              <label className="block text-slate-500 text-[10px] font-bold uppercase mb-1">{t('weightEachOptional')}</label>
                              <input type="number" value={spec.weight || ''} onChange={(e) => updateSpec('weight', e.target.value)} placeholder="kg" className={`w-full py-2 px-2.5 rounded-lg text-sm ${glassInput}`} />
                            </div>
                          </div>
                        )}
                        {!isComp && !pt.hasQuantity && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-slate-500 text-[10px] font-bold uppercase mb-1">{t('weightOptional')}</label>
                              <input type="number" value={spec.weight || ''} onChange={(e) => updateSpec('weight', e.target.value)} placeholder="kg" className={`w-full py-2 px-2.5 rounded-lg text-sm ${glassInput}`} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Preferred Delivery Date */}
            <div className="rounded-2xl p-5 mt-2 border-2 border-sky-400/20 bg-sky-500/[0.05]">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="h-5 w-5 text-sky-300" />
                <h3 className="text-sky-100 font-extrabold text-base">{t('preferredDate')}</h3>
                <span className="text-[10px] uppercase tracking-wider text-sky-300/60 font-bold ml-1">{t('optional')}</span>
              </div>
              <p className="text-sky-100/60 text-xs mb-3 leading-relaxed">{t('preferredDateHint')}</p>
              <input type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className={`w-full sm:w-auto py-3 px-4 rounded-xl text-slate-100 ${glassInput}`} />
            </div>
          </div>
        )}

        {/* Step 3 — Traveler Details */}
        {step === 3 && role === 'traveler' && (
          <div className="space-y-5">
            <h3 className="text-xl font-extrabold text-white mb-2 flex items-center gap-2"><Plane className="h-5 w-5 text-violet-400" /> {t('flightDetails')}</h3>

            {/* Has the traveler already bought a ticket? */}
            <div>
              <label className="block text-slate-500 text-sm font-bold mb-3">{t('hasTicketQuestion')}</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setHasTicket(true)} className={`px-3 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${hasTicket ? 'border-violet-400/60 bg-violet-500/20 text-violet-300 shadow-sm' : 'border-white/[0.10] bg-white/[0.06] text-slate-500 hover:bg-white/[0.08] hover:border-white/[0.14]'}`}>
                  {t('hasTicketYes')}
                </button>
                <button type="button" onClick={() => setHasTicket(false)} className={`px-3 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${!hasTicket ? 'border-violet-400/60 bg-violet-500/20 text-violet-300 shadow-sm' : 'border-white/[0.10] bg-white/[0.06] text-slate-500 hover:bg-white/[0.08] hover:border-white/[0.14]'}`}>
                  {t('hasTicketNo')}
                </button>
              </div>
            </div>

            {hasTicket ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-500 text-xs font-bold mb-2">{t('flightDate')}</label>
                  <input type="date" value={flightDate} onChange={(e) => setFlightDate(e.target.value)} className={`w-full py-3 px-3 rounded-xl text-slate-100 ${glassInput}`} />
                </div>
                <div>
                  <label className="block text-slate-500 text-xs font-bold mb-2">{t('flightTime')}</label>
                  <input type="time" value={flightTime} onChange={(e) => setFlightTime(e.target.value)} className={`w-full py-3 px-3 rounded-xl text-slate-100 ${glassInput}`} />
                </div>
                <div>
                  <label className="block text-slate-500 text-xs font-bold mb-2">{t('flightNumber')}</label>
                  <input type="text" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} className={`w-full py-3 px-3 rounded-xl text-slate-100 ${glassInput}`} placeholder="e.g. EK 203" />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-slate-500 text-xs font-bold mb-2">{t('approxDate')}</label>
                <input type="month" value={approxDate} onChange={(e) => setApproxDate(e.target.value)} min={new Date().toISOString().slice(0, 7)} className={`w-full sm:w-auto py-3 px-3 rounded-xl text-slate-100 ${glassInput}`} />
                <p className="text-slate-500 text-xs mt-2 leading-relaxed">{t('approxDateHint')}</p>
              </div>
            )}

            <div>
              <label className="block text-slate-500 text-sm font-bold mb-3">{t('acceptedItems')}</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {PACKAGE_TYPES.map(p => {
                  const active = acceptedItems.includes(p.id);
                  const isComp = p.isCompanion;
                  return (
                    <button key={p.id} type="button" onClick={() => toggleItem(p.id)} className={`px-3 py-2.5 rounded-xl text-sm font-bold border-2 transition-all flex items-center justify-center gap-1.5 ${active ? (isComp ? 'border-teal-400/60 bg-teal-500/20 text-teal-300 shadow-sm' : 'border-violet-400/60 bg-violet-500/20 text-violet-300 shadow-sm') : 'border-white/[0.10] bg-white/[0.06] text-slate-500 hover:bg-white/[0.08] hover:border-white/[0.14]'}`}>
                      {isComp && <HeartHandshake className="h-3.5 w-3.5" />}
                      {pkgTypeLabel(lang, p.id)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Companion acceptance (only when companion item selected) */}
            {travelerPickedCompanion && (
              <div className="rounded-2xl p-5 border-2 border-teal-400/30 bg-teal-500/[0.07]">
                <div className="flex items-center gap-2 mb-2">
                  <HeartHandshake className="h-5 w-5 text-teal-300" />
                  <h3 className="text-teal-100 font-extrabold text-base">{t('companionTravelerAcceptTitle')}</h3>
                </div>
                <p className="text-teal-100/70 text-sm mb-4 leading-relaxed">{t('companionItemDesc')}</p>
                <div className="space-y-2.5">
                  <label className={`flex items-center gap-3 cursor-pointer select-none p-4 rounded-2xl border-2 transition-all ${companionAccept.wheelchair ? 'border-teal-400 bg-teal-500/15' : 'border-white/[0.10] bg-white/[0.05] hover:bg-white/[0.08]'}`}>
                    <input type="checkbox" checked={companionAccept.wheelchair} onChange={(e) => setCompanionAccept({ ...companionAccept, wheelchair: e.target.checked })} className="w-5 h-5 rounded accent-teal-500 cursor-pointer flex-shrink-0" />
                    <Accessibility className={`h-5 w-5 flex-shrink-0 ${companionAccept.wheelchair ? 'text-teal-300' : 'text-slate-500'}`} />
                    <span className={`text-sm font-bold ${companionAccept.wheelchair ? 'text-slate-100' : 'text-slate-400'}`}>{t('companionAcceptWheelchair')}</span>
                  </label>
                  <label className={`flex items-center gap-3 cursor-pointer select-none p-4 rounded-2xl border-2 transition-all ${companionAccept.walking ? 'border-teal-400 bg-teal-500/15' : 'border-white/[0.10] bg-white/[0.05] hover:bg-white/[0.08]'}`}>
                    <input type="checkbox" checked={companionAccept.walking} onChange={(e) => setCompanionAccept({ ...companionAccept, walking: e.target.checked })} className="w-5 h-5 rounded accent-teal-500 cursor-pointer flex-shrink-0" />
                    <PersonStanding className={`h-5 w-5 flex-shrink-0 ${companionAccept.walking ? 'text-teal-300' : 'text-slate-500'}`} />
                    <span className={`text-sm font-bold ${companionAccept.walking ? 'text-slate-100' : 'text-slate-400'}`}>{t('companionAcceptWalking')}</span>
                  </label>
                </div>
                {!companionAccept.wheelchair && !companionAccept.walking && <p className="text-amber-300/80 text-xs mt-3 flex items-center gap-1.5"><Info className="h-3 w-3" /> {t('companionPickAtLeastOne')}</p>}
                <div className="mt-3 rounded-xl bg-amber-500/10 border border-amber-400/25 p-3 flex items-start gap-2">
                  <Info className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-100/80 text-[11px] leading-relaxed">{t('companionSafetyNote')}</p>
                </div>
              </div>
            )}

            {/* Per-item weight (optional) + rate — dimensions removed */}
            {acceptedItems.length > 0 && (
              <div className="rounded-2xl p-5 mt-2 border-2 border-violet-400/30 bg-violet-500/10">
                <div className="flex items-center gap-2 mb-2">
                  <Box className="h-5 w-5 text-violet-400" />
                  <h3 className="text-slate-100 font-extrabold text-base">{t('perItemSpecs')}</h3>
                </div>
                <p className="text-slate-500 text-sm mb-4 leading-relaxed">{t('perItemSpecsDesc')}</p>
                <div className="mb-4">
                  <label className="block text-slate-500 text-xs font-bold mb-2">{t('currency')}</label>
                  <select value={rateCurrency} onChange={(e) => setRateCurrency(e.target.value)} className={`w-full sm:w-1/3 py-2.5 px-3 rounded-xl ${glassInput}`}>
                    <option value="USD" className="bg-violet-950 text-slate-100">USD ($)</option>
                    <option value="EUR" className="bg-violet-950 text-slate-100">EUR (€)</option>
                    <option value="GBP" className="bg-violet-950 text-slate-100">GBP (£)</option>
                    <option value="CAD" className="bg-violet-950 text-slate-100">CAD (C$)</option>
                    <option value="AUD" className="bg-violet-950 text-slate-100">AUD (A$)</option>
                    <option value="CHF" className="bg-violet-950 text-slate-100">CHF (Fr)</option>
                  </select>
                </div>
                <div className="space-y-3">
                  {acceptedItems.map(itemId => {
                    const pt = PACKAGE_TYPES.find(p => p.id === itemId);
                    const pricing = PACKAGE_PRICING[itemId];
                    if (!pt) return null;
                    const isComp = pt.isCompanion;
                    const spec = itemSpecs[itemId] || {};
                    const updateSpec = (field, val) => { setItemSpecs({ ...itemSpecs, [itemId]: { ...spec, [field]: val } }); };
                    return (
                      <div key={itemId} className={`rounded-xl p-4 border ${isComp ? 'bg-teal-500/[0.06] border-teal-400/20' : 'bg-white/[0.08] border-white/[0.10]'}`}>
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <span className="font-extrabold text-slate-100 text-sm flex items-center gap-2">
                            {isComp ? <HeartHandshake className="h-4 w-4 text-teal-300" /> : <Box className="h-4 w-4 text-violet-400" />} {pkgTypeLabel(lang, itemId, itemSpecs)}
                          </span>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-emerald-400" />
                            <input type="number" value={ratesPerType[itemId] || ''} onChange={(e) => setRatesPerType({ ...ratesPerType, [itemId]: e.target.value })} className={`w-20 py-1.5 px-2 rounded-lg text-sm ${glassInput}`} placeholder={pricing ? `~${pricing.defaultUSD}` : '0'} />
                            <span className="text-slate-500 text-[10px] font-bold">{pricing ? t(pricing.unit) : t('perItem')}</span>
                          </div>
                        </div>
                        {itemId === 'custom' && (
                          <div className="mb-2">
                            <label className="block text-slate-500 text-[10px] font-bold uppercase mb-1">{t('customItemLabel')}</label>
                            <input type="text" value={spec.label || ''} onChange={(e) => updateSpec('label', e.target.value)} placeholder={t('customItemPlaceholder')} className={`w-full py-2 px-2.5 rounded-lg text-sm ${glassInput}`} />
                          </div>
                        )}
                        {/* Companion has no weight; quantity-based items (e.g. full suitcase) show count then weight each; other items show optional weight only */}
                        {!isComp && pt.hasQuantity && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-slate-500 text-[10px] font-bold uppercase mb-1">{t('quantityOptional')}</label>
                              <input type="number" value={spec.quantity || ''} onChange={(e) => updateSpec('quantity', e.target.value)} placeholder="1" className={`w-full py-2 px-2.5 rounded-lg text-sm ${glassInput}`} />
                            </div>
                            <div>
                              <label className="block text-slate-500 text-[10px] font-bold uppercase mb-1">{t('weightEachOptional')}</label>
                              <input type="number" value={spec.weight || ''} onChange={(e) => updateSpec('weight', e.target.value)} placeholder="kg" className={`w-full py-2 px-2.5 rounded-lg text-sm ${glassInput}`} />
                            </div>
                          </div>
                        )}
                        {!isComp && !pt.hasQuantity && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-slate-500 text-[10px] font-bold uppercase mb-1">{t('weightOptional')}</label>
                              <input type="number" value={spec.weight || ''} onChange={(e) => updateSpec('weight', e.target.value)} placeholder="kg" className={`w-full py-2 px-2.5 rounded-lg text-sm ${glassInput}`} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4 — Contact + Social + Account Security */}
        {step === 4 && (
          <div className="space-y-5">
            <h3 className="text-xl font-extrabold text-white mb-2 flex items-center gap-2"><User className="h-5 w-5 text-violet-400" /> {t('contactInfo')}</h3>
            <div>
              <label className="block text-slate-500 text-sm font-bold mb-2">{t('fullName')}</label>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className={`w-full py-3 px-4 rounded-2xl text-slate-100 ${glassInput}`} placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-slate-500 text-sm font-bold mb-2">{t('yourPhone')}</label>
              <div className="relative">
                <Phone className="absolute top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" style={dir === 'rtl' ? { right: '1rem' } : { left: '1rem' }} />
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={`w-full py-3 ${dir === 'rtl' ? 'pr-12 pl-4' : 'pl-12 pr-4'} rounded-2xl text-slate-100 ${glassInput}`} placeholder="+1 555 123 4567" />
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer select-none p-4 rounded-2xl bg-white/[0.04] border border-white/[0.10] hover:bg-white/[0.06] transition-colors">
              <input type="checkbox" checked={phoneHidden} onChange={(e) => setPhoneHidden(e.target.checked)} className="mt-1 w-5 h-5 rounded accent-fuchsia-500 cursor-pointer flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2 text-slate-100 font-bold text-sm mb-1">
                  <EyeOff className="h-4 w-4 text-violet-400" />
                  {t('hidePhoneLabel')}
                </div>
                <p className="text-slate-500 text-xs leading-relaxed">{t('hidePhoneHint')}</p>
              </div>
            </label>

            {role === 'sender' && (
              <>
                <div>
                  <label className="block text-slate-500 text-sm font-bold mb-2">{t('recipientFullName')}</label>
                  <input type="text" value={recipientFullName} onChange={(e) => setRecipientFullName(e.target.value)} className={`w-full py-3 px-4 rounded-2xl text-slate-100 ${glassInput}`} placeholder="Recipient name" />
                </div>
                <div>
                  <label className="block text-slate-500 text-sm font-bold mb-2">{t('recipientPhone')}</label>
                  <input type="tel" value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} className={`w-full py-3 px-4 rounded-2xl text-slate-100 ${glassInput}`} placeholder="+1 555 123 4567" />
                </div>
              </>
            )}

            {/* Social Media Section */}
            <div className="rounded-2xl p-5 mt-6 border-2 border-violet-400/25 bg-violet-500/[0.06]">
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="h-5 w-5 text-violet-400" />
                <h3 className="text-slate-200 font-extrabold text-base">{t('socialMedia')}</h3>
              </div>
              <p className="text-slate-500 text-sm mb-4 leading-relaxed">{t('socialDesc')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-500 text-xs font-bold mb-1.5 flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5 text-emerald-400" /> {t('whatsapp')}</label>
                  <input type="text" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} placeholder="+1 555 123 4567" />
                </div>
                <div>
                  <label className="block text-slate-500 text-xs font-bold mb-1.5 flex items-center gap-1.5"><Send className="h-3.5 w-3.5 text-sky-400" /> {t('telegram')}</label>
                  <input type="text" value={telegram} onChange={(e) => setTelegram(e.target.value)} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} placeholder="@username" />
                </div>
                <div>
                  <label className="block text-slate-500 text-xs font-bold mb-1.5 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-indigo-400" /> {t('signal')}</label>
                  <input type="text" value={signal} onChange={(e) => setSignal(e.target.value)} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} placeholder="+1 555 123 4567" />
                </div>
                <div>
                  <label className="block text-slate-500 text-xs font-bold mb-1.5 flex items-center gap-1.5"><Instagram className="h-3.5 w-3.5 text-pink-400" /> {t('instagram')}</label>
                  <input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} placeholder="@username" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-500 text-xs font-bold mb-1.5 flex items-center gap-1.5"><Facebook className="h-3.5 w-3.5 text-blue-400" /> {t('facebook')}</label>
                  <input type="text" value={facebook} onChange={(e) => setFacebook(e.target.value)} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} placeholder="profile.handle" />
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-6 bg-rose-500/12 border border-rose-400/30 rounded-2xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <p className="text-rose-300 text-sm font-bold">{error}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-between mt-8 pt-6 border-t border-white/[0.08]">
          <div className="flex gap-3">
            {step > 1 && (
              <button onClick={handleBack} className="px-6 py-3 rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] text-slate-100 font-bold transition-all flex items-center gap-2">
                <ChevronRight className={`h-4 w-4 ${dir === 'rtl' ? '' : 'rotate-180'}`} /> {t('back')}
              </button>
            )}
            {onCancel && (
              <button onClick={onCancel} className="px-6 py-3 rounded-2xl bg-white/[0.04] hover:bg-rose-500/20 text-slate-500 hover:text-rose-300 font-bold transition-all">{t('cancel')}</button>
            )}
          </div>
          {step < totalSteps && (
            <button onClick={handleNext} disabled={!canProceed()} className={`${btnPrimary} px-8 py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-40`}>
              {t('next')} <ChevronRight className={`h-4 w-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} />
            </button>
          )}
          {step === totalSteps && (
            <button onClick={handleSave} disabled={saving || !canProceed()} className={`${btnPrimary} px-8 py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-40`}>
              {saving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><CheckCircle2 className="h-5 w-5" /> {t('saveProfile')}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Dashboard — matches with companion (as item) awareness
// Matching rules:
//  Sender searching travelers:
//    - if packageType === 'companion': traveler must accept the sender's
//      companionNeed (wheelchair OR walking) via companionAccept
//    - else: traveler must accept the packageType. Weight is now optional:
//      only filter by weight if BOTH sides provided a weight.
//    - date window applies to traveler.flightDate
//  Traveler searching senders:
//    - companion senders (packageType==='companion') require the traveler to
//      accept that companionNeed
//    - else regular item match; weight only filters if both provided
// ==========================================
function Dashboard({ user, profile, onEdit, onMessage, onChangePassword, onAccountSettings, t, dir, lang, onOpenVerification }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [searched, setSearched] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const [filterFrom, setFilterFrom] = useState(todayStr);
  const [filterTo, setFilterTo] = useState('');

  const [showAdminList, setShowAdminList] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [adminListLoading, setAdminListLoading] = useState(false);

  // Self-service listing status — pause hides the listing from other users'
  // matches without touching login/account data; delete clears the trip
  // fields entirely (role goes blank, so it also drops out of matching and
  // the app routes the user back to the create-profile screen).
  const listingPaused = profile.listingPaused === true;
  const [statusAction, setStatusAction] = useState(null); // 'pause' | 'resume' | 'delete' | null
  const [statusLoading, setStatusLoading] = useState(false);

  const handlePauseToggle = async () => {
    setStatusLoading(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { listingPaused: !listingPaused, updatedAt: serverTimestamp() });
    } catch (err) {
      alert(err?.message || t('saveFailed'));
    } finally {
      setStatusLoading(false);
      setStatusAction(null);
    }
  };

  const handleDeleteProfile = async () => {
    setStatusLoading(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        role: '',
        originCountry: '', originCity: '', destCountry: '', destCity: '',
        senderItems: [], packageType: '', weight: '', preferredDate: '',
        recipientPhone: '', recipientFullName: '', companionNeed: '',
        flightDate: '', flightTime: '', flightNumber: '', hasTicket: true, approxDate: '',
        acceptedItems: [], itemSpecs: {}, companionAccept: { wheelchair: false, walking: false },
        ratesPerType: {}, rateCurrency: 'USD',
        listingPaused: false,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      alert(err?.message || t('saveFailed'));
    } finally {
      setStatusLoading(false);
      setStatusAction(null);
    }
  };

  const openContactAdmin = async () => {
    setShowAdminList(true);
    setAdminListLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const admins = [];
      snap.forEach(d => {
        const data = d.data();
        const isMain = data.email === 'administrator@fly-carrier.com';
        const isSub = data.isAdmin === true;
        if ((isMain || isSub) && data.banned !== true) {
          admins.push({ uid: d.id, ...data, isMainAdmin: isMain });
        }
      });
      admins.sort((a, b) => (b.isMainAdmin ? 1 : 0) - (a.isMainAdmin ? 1 : 0));
      setAdminList(admins);
    } catch (err) { console.error('Failed to load admins:', err); }
    finally { setAdminListLoading(false); }
  };

  const handleAdminClick = (admin) => {
    setShowAdminList(false);
    onMessage({ id: admin.uid, uid: admin.uid, name: admin.fullName || admin.email, email: admin.email, verified: admin.verified });
  };

  const findMatches = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const oppositeRole = profile.role === 'sender' ? 'traveler' : 'sender';
      const q = query(
        collection(db, 'users'),
        where('role', '==', oppositeRole),
        where('originCountry', '==', profile.originCountry),
        where('destCountry', '==', profile.destCountry)
      );
      const snap = await getDocs(q);
      const list = [];

      const today = new Date(); today.setHours(0, 0, 0, 0);
      let fromBoundary = filterFrom ? new Date(filterFrom + 'T00:00:00') : today;
      if (fromBoundary < today) fromBoundary = today;
      const toBoundary = filterTo ? new Date(filterTo + 'T23:59:59') : null;

      const withinFlight = (data) => {
        if (data.flightDate) {
          const fd = new Date(data.flightDate + 'T00:00:00');
          return (fd >= fromBoundary) && (!toBoundary || fd <= toBoundary);
        }
        // No exact date yet (no ticket) — approxDate is "YYYY-MM"; match if the
        // filter window overlaps that whole month at all.
        if (data.approxDate) {
          const [y, m] = data.approxDate.split('-').map(Number);
          if (!y || !m) return false;
          const monthStart = new Date(y, m - 1, 1);
          const monthEnd = new Date(y, m, 0, 23, 59, 59);
          return (monthEnd >= fromBoundary) && (!toBoundary || monthStart <= toBoundary);
        }
        return false;
      };
      const withinPreferred = (data) => {
        if (!data.preferredDate) return true; // no preferred date = flexible
        const pd = new Date(data.preferredDate + 'T00:00:00');
        return (pd >= fromBoundary) && (!toBoundary || pd <= toBoundary);
      };

      // Effective sender item list: prefer new senderItems array, fall back to legacy packageType.
      const senderItemsOf = (x) => {
        if (Array.isArray(x.senderItems) && x.senderItems.length > 0) return x.senderItems;
        if (x.packageType) return [x.packageType];
        return [];
      };

      snap.forEach(d => {
        const data = d.data();
        if (data.banned === true || data.listingPaused === true) return;

        if (profile.role === 'sender') {
          // Sender is searching travelers.
          const myItems = senderItemsOf(profile);
          const myGoods = myItems.filter(id => id !== 'companion');
          const iNeedCompanion = myItems.includes('companion');

          // Companion leg: does this traveler accept my companion need?
          const companionOk = iNeedCompanion && companionMatches(data.companionAccept, profile.companionNeed);

          // Goods leg: traveler must accept AT LEAST ONE of my (non-companion) items.
          let goodsOk = false;
          if (myGoods.length > 0 && Array.isArray(data.acceptedItems)) {
            goodsOk = myGoods.some(itemId => {
              if (!data.acceptedItems.includes(itemId)) return false;
              // weight optional: only filter when both sides provided a weight for THIS item
              const spec = data.itemSpecs?.[itemId];
              const myWeight = profile.itemSpecs?.[itemId]?.weight;
              if (spec && spec.weight && myWeight) return Number(spec.weight) >= Number(myWeight);
              return true;
            });
          }

          if ((goodsOk || companionOk) && withinFlight(data)) {
            list.push({ uid: d.id, ...data, _companionMatch: companionOk && !goodsOk });
          }
        } else {
          // Traveler is searching senders.
          const theirItems = senderItemsOf(data);
          const theirGoods = theirItems.filter(id => id !== 'companion');
          const theyNeedCompanion = theirItems.includes('companion');

          const companionOk = theyNeedCompanion && companionMatches(profile.companionAccept, data.companionNeed);

          let goodsOk = false;
          if (theirGoods.length > 0 && Array.isArray(profile.acceptedItems)) {
            goodsOk = theirGoods.some(itemId => {
              if (!profile.acceptedItems.includes(itemId)) return false;
              const spec = profile.itemSpecs?.[itemId];
              const theirWeight = data.itemSpecs?.[itemId]?.weight;
              if (spec && spec.weight && theirWeight) return Number(spec.weight) >= Number(theirWeight);
              return true;
            });
          }

          if ((goodsOk || companionOk) && withinPreferred(data)) {
            list.push({ uid: d.id, ...data, _companionMatch: companionOk && !goodsOk });
          }
        }
      });

      list.sort((a, b) => {
        const da = a.flightDate || a.approxDate || a.preferredDate || '9999-12-31';
        const db_ = b.flightDate || b.approxDate || b.preferredDate || '9999-12-31';
        return da.localeCompare(db_);
      });

      setMatches(list);
    } catch (err) {
      console.error('Match search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildSocialLinks = (m) => {
    const out = [];
    if (m.whatsapp) {
      const digits = String(m.whatsapp).replace(/[^\d]/g, '');
      if (digits) out.push({ kind: 'whatsapp', label: 'WhatsApp', url: `https://wa.me/${digits}`, icon: MessageCircle, color: 'text-emerald-300', bg: 'bg-emerald-500/15 border-emerald-400/30 hover:bg-emerald-500/25' });
    }
    if (m.telegram) {
      const handle = String(m.telegram).replace(/^@/, '').trim();
      if (handle) out.push({ kind: 'telegram', label: 'Telegram', url: `https://t.me/${handle}`, icon: Send, color: 'text-sky-300', bg: 'bg-sky-500/15 border-sky-400/30 hover:bg-sky-500/25' });
    }
    if (m.signal) {
      out.push({ kind: 'signal', label: 'Signal', url: `https://signal.me/#p/${String(m.signal).replace(/[^\d+]/g, '')}`, icon: Lock, color: 'text-indigo-300', bg: 'bg-indigo-500/15 border-indigo-400/30 hover:bg-indigo-500/25' });
    }
    if (m.instagram) {
      const handle = String(m.instagram).replace(/^@/, '').trim();
      if (handle) out.push({ kind: 'instagram', label: 'Instagram', url: `https://instagram.com/${handle}`, icon: Instagram, color: 'text-pink-300', bg: 'bg-pink-500/15 border-pink-400/30 hover:bg-pink-500/25' });
    }
    if (m.facebook) {
      const handle = String(m.facebook).replace(/^@/, '').trim();
      if (handle) out.push({ kind: 'facebook', label: 'Facebook', url: `https://facebook.com/${handle}`, icon: Facebook, color: 'text-blue-300', bg: 'bg-blue-500/15 border-blue-400/30 hover:bg-blue-500/25' });
    }
    return out;
  };

  const currencySymbol = (c) => ({ USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', CHF: 'CHF ' }[c] || c + ' ');

  // Effective sender items (new array or legacy single) — used for price + display
  const effSenderItems = (x) => {
    if (Array.isArray(x.senderItems) && x.senderItems.length > 0) return x.senderItems;
    if (x.packageType) return [x.packageType];
    return [];
  };

  const getMatchPriceChip = (m) => {
    // Companion-only match: show companion rate (traveler side) if present
    if (m._companionMatch) {
      if (m.role === 'traveler' && m.ratesPerType?.companion != null) {
        return { value: m.ratesPerType.companion, currency: m.rateCurrency || 'USD', unitKey: 'perTrip' };
      }
      if (m.role === 'sender' && m.ratesPerType?.companion != null) {
        return { value: m.ratesPerType.companion, currency: m.rateCurrency || 'USD', unitKey: 'perTrip' };
      }
      return null;
    }
    // Find a shared (non-companion) item to price against.
    if (m.role === 'sender') {
      // I'm a traveler viewing a sender: price the first item I accept from their list.
      const theirGoods = effSenderItems(m).filter(id => id !== 'companion');
      const shared = theirGoods.find(id => Array.isArray(profile.acceptedItems) && profile.acceptedItems.includes(id));
      const pick = shared || theirGoods[0];
      if (pick && m.ratesPerType?.[pick] != null) {
        const pricing = PACKAGE_PRICING[pick];
        return { value: m.ratesPerType[pick], currency: m.rateCurrency || 'USD', unitKey: pricing?.unit || 'perItem' };
      }
      return null;
    }
    if (m.role === 'traveler' && m.ratesPerType) {
      // I'm a sender viewing a traveler: price the first of MY items the traveler accepts.
      const myGoods = effSenderItems(profile).filter(id => id !== 'companion');
      const shared = myGoods.find(id => Array.isArray(m.acceptedItems) && m.acceptedItems.includes(id) && m.ratesPerType[id] != null);
      const pick = shared || myGoods.find(id => m.ratesPerType[id] != null);
      if (pick && m.ratesPerType[pick] != null) {
        const pricing = PACKAGE_PRICING[pick];
        return { value: m.ratesPerType[pick], currency: m.rateCurrency || 'USD', unitKey: pricing?.unit || 'perItem' };
      }
    }
    return null;
  };

  // Companion chips on profile summary
  const myCompanionSummary = profile.role === 'traveler'
    ? companionAcceptSummary(lang, profile.companionAccept)
    : (profile.role === 'sender' && effSenderItems(profile).includes('companion') ? companionNeedLabel(lang, profile.companionNeed) : '');

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {profile.role === 'sender' && <SenderSafetyNotice t={t} dir={dir} />}

      {profile.role === 'traveler' && !profile.verified && (
        <div className={`rounded-3xl border-2 backdrop-blur-xl overflow-hidden ${profile.verificationStatus === 'pending' ? 'border-amber-400/30' : 'border-sky-400/30'}`}>
          <div className={`p-5 sm:p-6 ${profile.verificationStatus === 'pending' ? 'bg-amber-500/8' : 'bg-sky-500/8'}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${profile.verificationStatus === 'pending' ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-gradient-to-br from-sky-400 to-blue-500'} shadow-lg`}>
                <BadgeCheck className="h-7 w-7 text-white" fill="currentColor" stroke="#0c1330" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                {profile.verificationStatus === 'pending' ? (
                  <>
                    <h3 className="text-amber-200 font-extrabold text-base mb-1">{t('verificationPending')}</h3>
                    <p className="text-amber-100/75 text-sm leading-relaxed">{t('verificationPendingDesc')}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-sky-200 font-extrabold text-base">{t('verificationBadge')}</h3>
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider bg-sky-400/20 text-sky-300 border border-sky-400/30">{t('optional')}</span>
                    </div>
                    <p className="text-sky-100/75 text-sm leading-relaxed mb-1">{t('verificationOptional')}</p>
                  </>
                )}
              </div>
              {profile.verificationStatus !== 'pending' && (
                <button onClick={onOpenVerification} className={`${btnPrimary} px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 whitespace-nowrap flex-shrink-0`}>
                  <BadgeCheck className="h-4 w-4" /> {t('requestVerification')}
                </button>
              )}
            </div>
          </div>

        </div>
      )}

      {/* Profile summary card */}
      <div className={`${glass} rounded-3xl p-6 sm:p-8`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ${profile.role === 'sender' ? 'bg-gradient-to-br from-fuchsia-500 to-pink-500 shadow-fuchsia-500/30' : 'bg-gradient-to-br from-violet-500 to-indigo-500 shadow-violet-500/30'}`}>
              {profile.role === 'sender' ? <Package className="h-8 w-8 text-slate-100" /> : <Plane className="h-8 w-8 text-slate-100" />}
            </div>
            <div>
              <div className="text-2xl font-extrabold text-slate-100 flex items-center gap-2 flex-wrap">
                {profile.fullName || profile.email}
                {profile.verified === true && <VerifiedBadge size="lg" title={t('verifiedBadgeTooltip')} />}
                {profile.signupSource === 'telegram' && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/15 text-sky-300 border border-sky-400/30 flex items-center gap-1"><Send className="h-3 w-3" />{t('telegramBadge')}</span>}
                {listingPaused && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-400/30 flex items-center gap-1"><EyeOff className="h-3 w-3" />{t('listingPausedBadge')}</span>}
              </div>
              <div className="text-slate-500 text-sm font-bold uppercase tracking-wider">{profile.role === 'sender' ? t('roleSender') : t('roleTraveler')}</div>
              <div className="text-slate-500 text-sm mt-1 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-violet-400" /> {profile.originCity}, {profile.originCountry} → {profile.destCity}, {profile.destCountry}</div>
              {myCompanionSummary && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-teal-500/15 text-teal-300 border border-teal-400/30">
                    <HeartHandshake className="h-3 w-3" /> {myCompanionSummary}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onEdit} className="px-4 py-2.5 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] text-slate-100 font-bold text-sm flex items-center gap-2 transition-all">
              <UserCircle2 className="h-4 w-4" /> {t('editProfile')}
            </button>
            <button onClick={() => setStatusAction(listingPaused ? 'resume' : 'pause')} className={`px-4 py-2.5 rounded-xl border font-bold text-sm flex items-center gap-2 transition-all ${listingPaused ? 'bg-emerald-500/12 hover:bg-emerald-500/20 border-emerald-400/30 text-emerald-300' : 'bg-white/[0.08] hover:bg-white/[0.12] border-white/[0.14] text-slate-100'}`} title={t('pauseListingHint')}>
              {listingPaused ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} {listingPaused ? t('resumeListing') : t('pauseListing')}
            </button>
            <button onClick={onAccountSettings} className="px-4 py-2.5 rounded-xl bg-violet-500/12 hover:bg-violet-500/20 border border-violet-400/30 text-violet-300 font-bold text-sm flex items-center gap-2 transition-all">
              <Settings className="h-4 w-4" /> {t('accountSettingsBtn')}
            </button>
            <button onClick={openContactAdmin} className="px-4 py-2.5 rounded-xl bg-amber-500/12 hover:bg-amber-500/20 border border-amber-400/30 text-amber-300 font-bold text-sm flex items-center gap-2 transition-all">
              <ShieldCheck className="h-4 w-4" /> {t('contactAdmin')}
            </button>
            <button onClick={() => setStatusAction('delete')} className="px-4 py-2.5 rounded-xl bg-rose-500/12 hover:bg-rose-500/20 border border-rose-400/30 text-rose-300 font-bold text-sm flex items-center gap-2 transition-all" title={t('deleteProfileAction')}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        {listingPaused && (
          <div className="mt-4 rounded-xl bg-amber-500/10 border border-amber-400/25 p-3 flex items-start gap-2">
            <EyeOff className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5" />
            <p className="text-amber-100/80 text-xs leading-relaxed">{t('listingPausedBanner')}</p>
          </div>
        )}
      </div>

      {statusAction && (() => {
        const isDelete = statusAction === 'delete';
        const isResume = statusAction === 'resume';
        const accent = isDelete
          ? { border: 'border-rose-400/40', bg: 'bg-rose-500/20', text: 'text-rose-400', icon: Trash2, btn: 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600' }
          : isResume
          ? { border: 'border-emerald-400/40', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: Eye, btn: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600' }
          : { border: 'border-amber-400/40', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: EyeOff, btn: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' };
        const title = isDelete ? t('confirmDeleteProfileTitle') : isResume ? t('confirmResumeTitle') : t('confirmPauseTitle');
        const desc = isDelete ? t('confirmDeleteProfileDesc') : isResume ? t('confirmResumeDesc') : t('confirmPauseDesc');
        const btnLabel = isDelete ? t('deleteProfileAction') : isResume ? t('resumeListing') : t('pauseListing');
        const action = isDelete ? handleDeleteProfile : handlePauseToggle;
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`${glass} rounded-3xl p-8 max-w-md w-full border-2 ${accent.border}`}>
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 ${accent.bg}`}>
                <accent.icon className={`h-7 w-7 ${accent.text}`} />
              </div>
              <h3 className="text-xl font-extrabold text-white text-center mb-2">{title}</h3>
              <p className="text-slate-500 text-sm text-center mb-6 leading-relaxed">{desc}</p>
              <div className="flex gap-3">
                <button onClick={() => setStatusAction(null)} className="flex-1 py-3 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] text-slate-100 font-bold transition-all">{t('cancel')}</button>
                <button onClick={action} disabled={statusLoading} className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-white ${accent.btn}`}>
                  {statusLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><accent.icon className="h-4 w-4" /> {btnLabel}</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Find matches + date range */}
      <div className={`${glassCard} rounded-3xl p-6 sm:p-8`}>
        <div className="text-center mb-6">
          <h3 className="text-2xl font-extrabold text-white mb-2 flex items-center justify-center gap-2"><Search className="h-6 w-6 text-violet-500" /> {t('findMatches')}</h3>
        </div>

        <div className="rounded-2xl bg-white/[0.04] border border-white/[0.10] p-4 mb-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 text-sky-200 font-bold text-sm">
              <Calendar className="h-4 w-4" /> {t('dateFilter')}
            </div>
            {(filterFrom !== todayStr || filterTo) && (
              <button type="button" onClick={() => { setFilterFrom(todayStr); setFilterTo(''); }} className="text-xs text-slate-500 hover:text-slate-100 font-bold underline-offset-2 hover:underline transition-colors">
                {t('clearDates')}
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-500 text-[11px] font-bold uppercase tracking-wider mb-1.5">{t('fromDate')}</label>
              <input type="date" value={filterFrom} min={todayStr} onChange={(e) => setFilterFrom(e.target.value)} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} />
            </div>
            <div>
              <label className="block text-slate-500 text-[11px] font-bold uppercase tracking-wider mb-1.5">{t('toDate')} <span className="font-normal text-slate-500">({t('optional')})</span></label>
              <input type="date" value={filterTo} min={filterFrom || todayStr} onChange={(e) => setFilterTo(e.target.value)} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} />
            </div>
          </div>
          <p className="text-slate-500 text-[11px] mt-3 flex items-center gap-1.5">
            <Info className="h-3 w-3" /> {t('pastDatesExcluded')}
          </p>
        </div>

        <div className="text-center">
          <button onClick={findMatches} disabled={loading} className={`${btnPrimary} px-8 py-3.5 rounded-2xl inline-flex items-center gap-2 text-lg`}>
            {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Search className="h-5 w-5" /> {t('searchBtn')}</>}
          </button>
        </div>
      </div>

      {searched && !loading && matches.length === 0 && (
        <div className={`${glass} rounded-3xl p-10 text-center`}>
          <Search className="h-12 w-12 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-500 font-bold">{t('noMatches')}</p>
        </div>
      )}

      {matches.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {matches.map(m => {
            const isRevealed = !!revealed[m.uid];
            const chip = getMatchPriceChip(m);
            const socials = buildSocialLinks(m);
            const isCompanion = !!m._companionMatch;
            // Companion detail text
            const compDetail = m.role === 'traveler'
              ? companionAcceptSummary(lang, m.companionAccept)
              : companionNeedLabel(lang, m.companionNeed);
            return (
              <div key={m.uid} className={`${glass} rounded-3xl p-6 transition-all hover:scale-[1.01] hover:shadow-2xl hover:shadow-violet-500/10 ${isCompanion ? 'ring-1 ring-teal-400/30' : ''}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isCompanion ? 'bg-teal-500/20' : m.role === 'sender' ? 'bg-fuchsia-500/20' : 'bg-violet-500/20'}`}>
                      {isCompanion ? <HeartHandshake className="h-6 w-6 text-teal-300" /> : m.role === 'sender' ? <Package className="h-6 w-6 text-violet-500" /> : <Plane className="h-6 w-6 text-violet-400" />}
                    </div>
                    <div>
                      <div className="text-slate-100 font-extrabold flex items-center gap-1.5">
                        {m.fullName || 'User'}
                        {m.verified === true && <VerifiedBadge size="md" title={t('verifiedBadgeTooltip')} />}
                        {m.signupSource === 'telegram' && <Send className="h-3.5 w-3.5 text-sky-400" title={t('viaTelegram')} />}
                      </div>
                      <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">{m.role === 'sender' ? t('roleSender') : t('roleTraveler')}</div>
                    </div>
                  </div>
                  {chip && (
                    <div className="px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center gap-1 text-emerald-200 text-sm font-extrabold">
                      <DollarSign className="h-3.5 w-3.5" />
                      <span>{currencySymbol(chip.currency)}{chip.value}</span>
                      <span className="text-emerald-300/70 text-[10px] font-bold ml-0.5">{t(chip.unitKey)}</span>
                    </div>
                  )}
                </div>

                {isCompanion && (
                  <div className="mb-3 rounded-xl bg-teal-500/10 border border-teal-400/25 px-3 py-2 flex items-center gap-2">
                    <Accessibility className="h-4 w-4 text-teal-300 flex-shrink-0" />
                    <span className="text-teal-100 text-xs font-bold leading-snug">{compDetail}</span>
                  </div>
                )}

                <div className="space-y-2 text-sm mb-4">
                  <InfoItem icon={MapPin} label={t('detailsRoute')} value={`${m.originCity}, ${m.originCountry} → ${m.destCity}, ${m.destCountry}`} />
                  {m.role === 'sender' && !isCompanion && (
                    <>
                      {(() => {
                        const goods = effSenderItems(m).filter(id => id !== 'companion');
                        if (goods.length === 0) return null;
                        return (
                          <div className="py-1.5">
                            <div className="flex items-center gap-1.5 text-slate-500 text-xs font-bold mb-1.5"><Box className="h-3.5 w-3.5" /> {t('whatSending')}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {goods.map(id => {
                                const w = m.itemSpecs?.[id]?.weight;
                                const q = m.itemSpecs?.[id]?.quantity;
                                return (
                                  <span key={id} className="px-2 py-1 rounded-lg bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200 text-xs font-bold flex items-center gap-1">
                                    {pkgTypeLabel(lang, id, m.itemSpecs)}{w ? <span className="text-fuchsia-300/70">· {q ? `${q} × ` : ''}{w}kg</span> : null}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                      {m.preferredDate && <InfoItem icon={Calendar} label={t('preferredDate')} value={m.preferredDate} />}
                    </>
                  )}
                  {m.role === 'sender' && isCompanion && m.preferredDate && (
                    <InfoItem icon={Calendar} label={t('preferredDate')} value={m.preferredDate} />
                  )}
                  {m.role === 'traveler' && (
                    <>
                      {m.flightDate && <InfoItem icon={Calendar} label={t('flightDate')} value={`${m.flightDate}${m.flightTime ? ' • ' + m.flightTime : ''}`} />}
                      {!m.flightDate && m.approxDate && <InfoItem icon={Calendar} label={t('approxDate')} value={m.approxDate} />}
                      {m.flightNumber && <InfoItem icon={Plane} label={t('flightNumber')} value={m.flightNumber} />}
                      {profile.role === 'sender' && (() => {
                        // Show traveler's max weight for items I want to send that they accept
                        const myGoods = effSenderItems(profile).filter(id => id !== 'companion');
                        const shown = myGoods.filter(id => Array.isArray(m.acceptedItems) && m.acceptedItems.includes(id) && m.itemSpecs?.[id]?.weight);
                        if (shown.length === 0) return null;
                        return (
                          <div className="py-1.5">
                            <div className="flex items-center gap-1.5 text-slate-500 text-xs font-bold mb-1.5"><Weight className="h-3.5 w-3.5" /> {t('weight')}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {shown.map(id => {
                                const q = m.itemSpecs[id].quantity;
                                return (
                                  <span key={id} className="px-2 py-1 rounded-lg bg-white/[0.06] border border-white/[0.10] text-slate-300 text-xs font-bold">
                                    {pkgTypeLabel(lang, id, m.itemSpecs)} · {q ? `${q} × ` : ''}{m.itemSpecs[id].weight}kg max
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>

                {!isRevealed ? (
                  m.phoneHidden || !m.phone ? (
                    <button onClick={() => onMessage(m)} className="w-full py-3 rounded-2xl bg-violet-500/15 hover:bg-violet-500/25 border border-violet-400/30 text-slate-200 font-bold flex items-center justify-center gap-2 transition-all">
                      <EyeOff className="h-4 w-4" /> {t('contactViaMessage')}
                    </button>
                  ) : (
                    <button onClick={() => setRevealed({ ...revealed, [m.uid]: true })} className="w-full py-3 rounded-2xl bg-fuchsia-500/15 hover:bg-fuchsia-500/25 border border-fuchsia-400/30 text-slate-200 font-bold flex items-center justify-center gap-2 transition-all">
                      <Eye className="h-4 w-4" /> {t('showPhone')}
                    </button>
                  )
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-2xl bg-white/[0.06] border border-white/[0.10] p-4">
                      <div className="flex items-center gap-2 text-slate-100 font-bold">
                        <Phone className="h-4 w-4 text-emerald-300" />
                        <a href={`tel:${m.phone}`} className="hover:text-emerald-300 transition-colors break-all">{m.phone}</a>
                      </div>
                    </div>
                    {socials.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {socials.map(s => (
                          <a key={s.kind} href={s.url} target="_blank" rel="noopener noreferrer" className={`px-3 py-2 rounded-xl border text-xs font-bold flex items-center gap-1.5 transition-all ${s.bg} ${s.color}`}>
                            <s.icon className="h-3.5 w-3.5" /> {s.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <button onClick={() => onMessage(m)} className="w-full mt-3 py-3 rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] text-slate-100 font-bold text-sm flex items-center justify-center gap-2 transition-all">
                  <MessageSquare className="h-4 w-4" /> {t('sendMessage')}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Contact Admin modal */}
      {showAdminList && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className={`${glass} rounded-3xl p-8 max-w-md w-full border-2 border-amber-400/30 max-h-[80vh] overflow-hidden flex flex-col`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
                <ShieldCheck className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-white">{t('contactAdmin')}</h3>
                <p className="text-slate-500 text-xs">{t('contactAdminDesc')}</p>
              </div>
            </div>
            <div className="border-t border-white/[0.10] my-4"></div>
            {adminListLoading ? (
              <div className="py-12 flex flex-col items-center text-slate-500">
                <div className="w-8 h-8 border-2 border-amber-400/40 border-t-amber-500 rounded-full animate-spin mb-3"></div>
                <span className="text-sm">{t('searching')}</span>
              </div>
            ) : adminList.length === 0 ? (
              <p className="py-12 text-center text-slate-500 text-sm">{t('noAdminsAvailable')}</p>
            ) : (
              <div className="space-y-2 overflow-y-auto flex-1">
                {adminList.map(admin => (
                  <button key={admin.uid} onClick={() => handleAdminClick(admin)} className="w-full p-4 rounded-2xl bg-white/[0.08] hover:bg-amber-500/12 border border-white/[0.10] hover:border-amber-400/40 transition-all flex items-center gap-3 text-left group">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${admin.isMainAdmin ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md' : 'bg-amber-500/20 text-amber-300'}`}>
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-slate-100 text-sm truncate">{admin.fullName || admin.email}</span>
                        {admin.verified && <VerifiedBadge size="sm" title={t('verifiedBadgeTooltip')} />}
                        {admin.isMainAdmin
                          ? <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-orange-500 text-white">{t('mainAdminBadge')}</span>
                          : <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/40">{t('subAdminBadge')}</span>
                        }
                      </div>
                      <div className="text-slate-500 text-xs truncate mt-0.5">{admin.email}</div>
                    </div>
                    <MessageSquare className="h-5 w-5 text-slate-500 group-hover:text-amber-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowAdminList(false)} className="mt-4 w-full py-3 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] text-slate-100 font-bold transition-all">
              {t('cancel')}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// ==========================================
// AuthScreen — Login, Sign Up, Forgot Password
// ==========================================
function AuthScreen({ t, dir, requireEmailVerification }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const isLogin = mode === 'login';
  const isSignup = mode === 'signup';
  const isForgot = mode === 'forgot';

  const switchMode = (newMode) => {
    setMode(newMode); setError(''); setSuccessMsg(''); setShowTermsError(false); setPassword('');
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(''); setSuccessMsg(''); setShowTermsError(false);

    if (isForgot) {
      if (!email) { setError(t('authFailed')); return; }
      setLoading(true);
      try {
        await sendPasswordResetEmail(auth, email.trim());
        setSuccessMsg(t('resetSent'));
        setEmail('');
      } catch (err) { setSuccessMsg(t('resetSent')); }
      finally { setLoading(false); }
      return;
    }

    if (isSignup && !termsAccepted) {
      setShowTermsError(true);
      setError(t('termsRequired'));
      return;
    }
    if (!email || !password) { setError(t('authFailed')); return; }

    setLoading(true);
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        try {
          await setDoc(doc(db, 'users', cred.user.uid), {
            email: cred.user.email,
            createdAt: serverTimestamp(),
            termsAcceptedAt: serverTimestamp(),
            signupSource: 'web'
          }, { merge: true });
        } catch (err) { console.warn('Could not write initial user doc:', err); }
        if (requireEmailVerification) {
          try { await sendEmailVerification(cred.user); } catch (err) { /* ignore */ }
        }
      }
    } catch (err) { setError(t('authFailed')); }
    finally { setLoading(false); }
  };

  const termsItems = t('termsList');

  return (
    <div className="max-w-xl mx-auto space-y-6 animate-in zoom-in-95 duration-500" dir={dir}>
      <div className={`${glass} p-8 sm:p-10 rounded-3xl`}>
        <div className="text-center mb-8">
          <div className={`w-16 h-16 ${isForgot ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500' : 'bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500'} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-fuchsia-500/30`}>
            {isLogin ? <LogIn className="h-8 w-8 text-slate-100" /> : isSignup ? <UserCircle2 className="h-8 w-8 text-slate-100" /> : <KeyRound className="h-8 w-8 text-slate-100" />}
          </div>
          <h2 className="text-3xl font-extrabold text-white mb-2">
            {isLogin ? t('welcome') : isSignup ? t('signup') : t('forgotTitle')}
          </h2>
          <p className="text-slate-500">
            {isLogin ? t('welcomeSub') : isSignup ? t('joinSub') : t('forgotDesc')}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-slate-500 text-sm font-bold mb-2">{t('email')}</label>
            <div className="relative">
              <Mail className="absolute top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" style={dir === 'rtl' ? { right: '1rem' } : { left: '1rem' }} />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={`w-full py-3.5 ${dir === 'rtl' ? 'pr-12 pl-4' : 'pl-12 pr-4'} rounded-2xl text-slate-100 placeholder-slate-500 ${glassInput}`} autoComplete="email" />
            </div>
          </div>

          {!isForgot && (
            <div>
              <label className="block text-slate-500 text-sm font-bold mb-2">{t('password')}</label>
              <div className="relative">
                <Lock className="absolute top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" style={dir === 'rtl' ? { right: '1rem' } : { left: '1rem' }} />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={`w-full py-3.5 ${dir === 'rtl' ? 'pr-12 pl-4' : 'pl-12 pr-4'} rounded-2xl text-slate-100 placeholder-slate-500 ${glassInput}`} autoComplete={isLogin ? 'current-password' : 'new-password'} />
              </div>
              {isLogin && (
                <div className={`mt-2 ${dir === 'rtl' ? 'text-left' : 'text-right'}`}>
                  <button type="button" onClick={() => switchMode('forgot')} className="text-amber-300 text-xs font-bold hover:text-amber-200 hover:underline transition-colors">
                    {t('forgotPassword')}
                  </button>
                </div>
              )}
            </div>
          )}

          {isSignup && (
            <div className={`rounded-2xl p-5 mt-5 ${showTermsError ? 'border-rose-400/40 bg-rose-500/10' : 'border-amber-400/25 bg-amber-500/[0.06]'} border-2`}>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-amber-300" />
                <h3 className="text-amber-200 font-extrabold text-base">{t('termsTitle')}</h3>
              </div>
              <p className="text-amber-100/80 text-sm leading-relaxed mb-3">{t('termsIntro')}</p>
              <div className="max-h-56 overflow-y-auto rounded-xl bg-black/20 p-4 border border-white/[0.10] mb-4 space-y-2.5">
                {Array.isArray(termsItems) && termsItems.map((item, i) => (
                  <div key={i} className="flex gap-2.5 items-start text-[13px] leading-relaxed text-amber-50/85">
                    <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-amber-400/20 text-amber-300 text-[11px] font-extrabold flex items-center justify-center">{i + 1}</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none group">
                <input type="checkbox" checked={termsAccepted} onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setShowTermsError(false); }} className="mt-1 w-5 h-5 rounded accent-fuchsia-500 cursor-pointer flex-shrink-0" />
                <span className={`text-sm font-bold leading-snug ${termsAccepted ? 'text-emerald-300' : 'text-amber-100/90'} group-hover:text-slate-100 transition-colors`}>
                  {t('termsAccept')}
                </span>
              </label>
            </div>
          )}

          {error && (
            <div className="bg-rose-500/15 border border-rose-400/30 rounded-2xl p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-rose-300 flex-shrink-0 mt-0.5" />
              <p className="text-rose-200 text-sm font-bold">{error}</p>
            </div>
          )}
          {successMsg && (
            <div className="bg-emerald-500/15 border border-emerald-400/30 rounded-2xl p-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-300 flex-shrink-0 mt-0.5" />
              <p className="text-emerald-200 text-sm font-bold">{successMsg}</p>
            </div>
          )}

          <button onClick={handleSubmit} disabled={loading} className={`w-full ${btnPrimary} py-4 rounded-2xl flex items-center justify-center gap-2 text-lg mt-2`}>
            {loading
              ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : isForgot
                ? <><Send className="h-5 w-5" /> {t('sendResetLink')}</>
                : isLogin
                  ? <><LogIn className="h-5 w-5" /> {t('login')}</>
                  : <><UserCircle2 className="h-5 w-5" /> {t('signup')}</>
            }
          </button>

          <div className="text-center pt-3 space-y-2">
            {isForgot ? (
              <button type="button" onClick={() => switchMode('login')} className="text-violet-400 text-sm font-bold hover:text-slate-200 hover:underline transition-colors flex items-center justify-center gap-1.5 mx-auto">
                <ChevronRight className={`h-4 w-4 ${dir === 'rtl' ? '' : 'rotate-180'}`} /> {t('backToLogin')}
              </button>
            ) : (
              <button type="button" onClick={() => switchMode(isLogin ? 'signup' : 'login')} className="text-violet-400 text-sm font-bold hover:text-slate-200 hover:underline transition-colors">
                {isLogin ? t('noAccount') + ' ' + t('signup') : t('haveAccount') + ' ' + t('login')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// AdminUserDetails — expandable profile breakdown (dimensions removed, companion-as-item)
// ==========================================
function AdminUserDetails({ u, t, lang }) {
  const curSym = (c) => ({ USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', CHF: 'CHF ' }[c] || (c ? c + ' ' : ''));
  const hasProfile = !!u.role;

  const Row = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-2 py-1.5">
      {Icon && <Icon className="h-3.5 w-3.5 text-slate-500 flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0 text-[13px] leading-relaxed">
        <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px] mr-1.5">{label}:</span>
        <span className="text-slate-100/90 break-words">{children}</span>
      </div>
    </div>
  );

  const socials = [
    u.whatsapp && { label: 'WhatsApp', val: u.whatsapp },
    u.telegram && { label: 'Telegram', val: u.telegram },
    u.signal && { label: 'Signal', val: u.signal },
    u.instagram && { label: 'Instagram', val: u.instagram },
    u.facebook && { label: 'Facebook', val: u.facebook },
  ].filter(Boolean);

  const acceptedItems = Array.isArray(u.acceptedItems) ? u.acceptedItems : [];
  const rates = u.ratesPerType || {};
  const specs = u.itemSpecs || {};

  return (
    <div className="mt-3 pt-3 border-t border-white/[0.10] animate-in fade-in slide-in-from-top-1 duration-200">
      {!hasProfile ? (
        <p className="text-slate-500 text-xs italic py-2">{t('detailsNoProfile')}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
          <div className="lg:col-span-2 mb-1">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold ${u.role === 'sender' ? 'bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-400/30' : 'bg-violet-500/15 text-violet-300 border border-violet-400/30'}`}>
              {u.role === 'sender' ? <Package className="h-3 w-3" /> : <Plane className="h-3 w-3" />}
              {t('detailsProfileType')}: {u.role === 'sender' ? t('roleSender') : t('roleTraveler')}
            </span>
          </div>

          {(u.originCity || u.destCity) && (
            <Row icon={MapPin} label={t('detailsRoute')}>
              {(u.originCity || '?')}, {(u.originCountry || '?')} → {(u.destCity || '?')}, {(u.destCountry || '?')}
            </Row>
          )}

          <Row icon={Phone} label={t('detailsContact')}>
            {u.phone || t('detailsNone')}
            {u.phoneHidden && <span className="ml-2 text-[10px] text-amber-300/80 font-bold">({t('detailsPhoneHidden')})</span>}
          </Row>

          {/* SENDER */}
          {u.role === 'sender' && (() => {
            const sItems = (Array.isArray(u.senderItems) && u.senderItems.length > 0) ? u.senderItems : (u.packageType ? [u.packageType] : []);
            const sGoods = sItems.filter(id => id !== 'companion');
            const hasCompanion = sItems.includes('companion');
            return (
              <>
                {sGoods.length > 0 && (
                  <div className="lg:col-span-2">
                    <Row icon={Box} label={t('detailsPackage')}>
                      <span className="flex flex-wrap gap-1.5 mt-1">
                        {sGoods.map(id => {
                          const rate = rates[id];
                          const s = specs[id] || {};
                          const wt = s.weight ? (s.quantity ? `${s.quantity} × ${s.weight}kg` : `${s.weight}kg`) : null;
                          return (
                            <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.06] border border-white/[0.10] text-[11px]">
                              <span className="font-bold text-slate-200">{pkgTypeLabel(lang, id, specs)}</span>
                              {wt && <span className="text-slate-400">· {wt}</span>}
                              {rate != null && <span className="text-emerald-300 font-bold">· {curSym(u.rateCurrency)}{rate}</span>}
                            </span>
                          );
                        })}
                      </span>
                    </Row>
                  </div>
                )}
                {u.preferredDate && <Row icon={Calendar} label={t('detailsPreferredDate')}>{u.preferredDate}</Row>}
                {u.recipientFullName && (
                  <Row icon={User} label={t('detailsRecipient')}>
                    {u.recipientFullName}{u.recipientPhone ? ` · ${u.recipientPhone}` : ''}
                  </Row>
                )}
                {hasCompanion && u.companionNeed && (
                  <Row icon={Accessibility} label={t('detailsCompanionNeed')}>
                    <span className="text-teal-300 font-bold">{companionNeedLabel(lang, u.companionNeed)}</span>
                  </Row>
                )}
              </>
            );
          })()}

          {/* TRAVELER */}
          {u.role === 'traveler' && (
            <>
              {(u.flightDate || u.flightTime) && (
                <Row icon={Calendar} label={t('detailsFlight')}>
                  {u.flightDate || '—'}{u.flightTime ? ` · ${u.flightTime}` : ''}
                </Row>
              )}
              {!u.flightDate && u.approxDate && (
                <Row icon={Calendar} label={t('approxDate')}>{u.approxDate}</Row>
              )}
              {u.flightNumber && <Row icon={Plane} label={t('detailsFlightNumber')}>{u.flightNumber}</Row>}
              {acceptedItems.length > 0 && (
                <div className="lg:col-span-2">
                  <Row icon={Box} label={t('detailsAcceptedItems')}>
                    <span className="flex flex-wrap gap-1.5 mt-1">
                      {acceptedItems.map(id => {
                        const rate = rates[id];
                        const s = specs[id] || {};
                        const wt = s.weight ? (s.quantity ? `${s.quantity} × ${s.weight}kg` : `${s.weight}kg`) : null;
                        return (
                          <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.06] border border-white/[0.10] text-[11px]">
                            <span className="font-bold text-slate-100">{pkgTypeLabel(lang, id, specs)}</span>
                            {rate != null && rate !== '' && <span className="text-emerald-300 font-bold">{curSym(u.rateCurrency)}{rate}</span>}
                            {wt && <span className="text-slate-500">{wt}</span>}
                          </span>
                        );
                      })}
                    </span>
                  </Row>
                </div>
              )}
              {u.companionAccept && (u.companionAccept.wheelchair || u.companionAccept.walking) && (
                <Row icon={HeartHandshake} label={t('detailsCompanionOffer')}>
                  <span className="text-teal-300 font-bold">{companionAcceptSummary(lang, u.companionAccept)}</span>
                  {u.ratesPerType?.companion != null && (
                    <span className="ml-2 text-emerald-300 font-bold">{curSym(u.rateCurrency)}{u.ratesPerType.companion} · {t('perTrip')}</span>
                  )}
                </Row>
              )}
            </>
          )}

          {socials.length > 0 && (
            <div className="lg:col-span-2">
              <Row icon={MessageCircle} label={t('detailsSocial')}>
                <span className="flex flex-wrap gap-1.5 mt-1">
                  {socials.map(s => (
                    <span key={s.label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.06] border border-white/[0.10] text-[11px]">
                      <span className="text-slate-500 font-bold">{s.label}</span>
                      <span className="text-slate-100 font-mono">{s.val}</span>
                    </span>
                  ))}
                </span>
              </Row>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================
// AdminStatsPanel — visits & members with publish-to-footer
// ==========================================
function AdminStatsPanel({ t, users, loadingUsers, isMainAdmin }) {
  const [visits, setVisits] = useState({ today: 0, week: 0, month: 0, total: 0 });
  const [loadingVisits, setLoadingVisits] = useState(true);
  const [pubSettings, setPubSettings] = useState({ showVisits: false, showMembers: false });
  const [publishing, setPublishing] = useState(null);
  const [flash, setFlash] = useState('');

  const dateKey = (d) => `d_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const computeVisits = (data) => {
    if (!data) return { today: 0, week: 0, month: 0, total: 0 };
    const now = new Date();
    const todayK = dateKey(now);
    let today = 0, week = 0, month = 0;
    const weekKeys = new Set(), monthKeys = new Set();
    for (let i = 0; i < 30; i++) {
      const d = new Date(now); d.setDate(now.getDate() - i);
      const k = dateKey(d);
      if (i < 7) weekKeys.add(k);
      monthKeys.add(k);
    }
    Object.keys(data).forEach(k => {
      if (!k.startsWith('d_')) return;
      const v = Number(data[k]) || 0;
      if (k === todayK) today += v;
      if (weekKeys.has(k)) week += v;
      if (monthKeys.has(k)) month += v;
    });
    return { today, week, month, total: Number(data.total) || 0 };
  };

  const loadVisits = async () => {
    setLoadingVisits(true);
    try {
      const snap = await getDoc(doc(db, 'stats', 'visits'));
      setVisits(computeVisits(snap.exists() ? snap.data() : null));
    } catch (e) { console.warn('Failed to load visit stats:', e?.code || e); }
    finally { setLoadingVisits(false); }
  };

  useEffect(() => {
    loadVisits();
    let unsub = () => {};
    try {
      unsub = onSnapshot(doc(db, 'stats', 'publicSettings'), (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setPubSettings({ showVisits: d.showVisits === true, showMembers: d.showMembers === true });
        }
      }, (err) => console.warn('publicSettings listen error:', err?.code || err));
    } catch (e) { /* ignore */ }
    return () => unsub();
  }, []);

  const memberCounts = (() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
    const monthAgo = new Date(now); monthAgo.setDate(now.getDate() - 30);
    const ms = (u) => {
      if (!u.createdAt) return null;
      if (u.createdAt.toMillis) return u.createdAt.toMillis();
      if (u.createdAt.seconds != null) return u.createdAt.seconds * 1000;
      const d = new Date(u.createdAt); return isNaN(d.getTime()) ? null : d.getTime();
    };
    let today = 0, week = 0, month = 0;
    users.forEach(u => {
      const m = ms(u);
      if (m == null) return;
      if (m >= startOfToday.getTime()) today++;
      if (m >= weekAgo.getTime()) week++;
      if (m >= monthAgo.getTime()) month++;
    });
    return { today, week, month, total: users.length };
  })();

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 3500); };

  const togglePublish = async (kind) => {
    if (!isMainAdmin) return;
    const field = kind === 'visits' ? 'showVisits' : 'showMembers';
    const next = !pubSettings[field];
    setPublishing(kind);
    try {
      const payload = { [field]: next, updatedAt: serverTimestamp() };
      if (kind === 'visits') payload.visitsSnapshot = next ? visits : null;
      else payload.membersSnapshot = next ? { total: memberCounts.total, today: memberCounts.today, week: memberCounts.week, month: memberCounts.month } : null;
      await setDoc(doc(db, 'stats', 'publicSettings'), payload, { merge: true });
      showFlash(next ? t('statsPublishedNote') : t('statsUnpublishedNote'));
    } catch (e) { alert(e?.message || 'Failed to update publish setting.'); }
    finally { setPublishing(null); }
  };

  const republishIfLive = async (kind) => {
    if (!isMainAdmin) return;
    const field = kind === 'visits' ? 'showVisits' : 'showMembers';
    if (!pubSettings[field]) return;
    try {
      const payload = { updatedAt: serverTimestamp() };
      if (kind === 'visits') payload.visitsSnapshot = visits;
      else payload.membersSnapshot = { total: memberCounts.total, today: memberCounts.today, week: memberCounts.week, month: memberCounts.month };
      await setDoc(doc(db, 'stats', 'publicSettings'), payload, { merge: true });
    } catch (e) { /* non-critical */ }
  };

  const StatGrid = ({ counts, loading }) => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
      {[
        { key: 'today', label: t('statToday'), val: counts.today },
        { key: 'week', label: t('statWeek'), val: counts.week },
        { key: 'month', label: t('statMonth'), val: counts.month },
        { key: 'total', label: t('statTotal'), val: counts.total },
      ].map(s => (
        <div key={s.key} className="rounded-xl bg-white/[0.05] border border-white/[0.10] px-3 py-3 text-center">
          <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1">{s.label}</div>
          <div className="text-2xl font-black text-white">
            {loading ? <span className="inline-block w-6 h-6 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin align-middle" /> : (s.val ?? 0).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );

  const PublishButton = ({ kind, isLive, hint }) => {
    if (!isMainAdmin) {
      return isLive ? (
        <div className="inline-flex items-center gap-1.5 text-emerald-300 text-xs font-bold px-3 py-2">
          <CheckCircle2 className="h-3.5 w-3.5" /> {t('publishedToFooter')}
        </div>
      ) : null;
    }
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-3">
        <button
          onClick={() => togglePublish(kind)}
          disabled={publishing === kind}
          className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all disabled:opacity-50 ${isLive
            ? 'bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/40 text-emerald-300'
            : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white shadow-sm'}`}
        >
          {publishing === kind
            ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : isLive ? <><EyeOff className="h-3.5 w-3.5" /> {t('unpublish')}</> : <><Globe className="h-3.5 w-3.5" /> {t('publishToFooter')}</>}
        </button>
        {isLive
          ? <span className="text-emerald-300/80 text-[11px] font-bold flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {t('publishedToFooter')}</span>
          : <span className="text-slate-500 text-[11px] leading-snug">{hint}</span>}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {flash && (
        <div className="rounded-xl bg-emerald-500/12 border border-emerald-400/30 px-4 py-2.5 flex items-center gap-2 animate-in fade-in slide-in-from-top-1">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
          <span className="text-emerald-200 text-sm font-bold">{flash}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`${glass} rounded-2xl p-5`}>
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="font-bold text-base text-white flex items-center gap-2">
              <Eye className="h-5 w-5 text-sky-400" /> {t('visitStatsTitle')}
              {pubSettings.showVisits && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-400/40">LIVE</span>}
            </h3>
            <button onClick={() => { loadVisits().then(() => republishIfLive('visits')); }} className="text-slate-500 hover:text-sky-300 transition-colors flex items-center gap-1 text-xs font-bold" title={t('refreshStats')}>
              <RefreshCw className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t('refreshStats')}</span>
            </button>
          </div>
          <StatGrid counts={visits} loading={loadingVisits} />
          <PublishButton kind="visits" isLive={pubSettings.showVisits} hint={t('publishVisitsHint')} />
          {!isMainAdmin && <p className="text-slate-500 text-[10px] mt-2 italic">{t('mainAdminOnly')}</p>}
        </div>

        <div className={`${glass} rounded-2xl p-5`}>
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="font-bold text-base text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-violet-400" /> {t('memberStatsTitle')}
              {pubSettings.showMembers && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-400/40">LIVE</span>}
            </h3>
          </div>
          <StatGrid counts={memberCounts} loading={loadingUsers} />
          <PublishButton kind="members" isLive={pubSettings.showMembers} hint={t('publishMembersHint')} />
          {!isMainAdmin && <p className="text-slate-500 text-[10px] mt-2 italic">{t('mainAdminOnly')}</p>}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// TelegramMembersTab — separate view for users who joined via the Telegram bot
// Shows whether each has created a profile yet.
// ==========================================
function TelegramMembersTab({ t, lang, users, loading, onMessage, onRefresh, adminPermissions, isMainAdmin, actionLoading, onVerify, onUnverify, onBan, onUnban, onDelete }) {
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');

  const tgUsers = users.filter(u => u.signupSource === 'telegram');
  const filtered = tgUsers.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (u.fullName || '').toLowerCase().includes(q)
      || String(u.telegramId || '').toLowerCase().includes(q)
      || (u.telegramUsername || '').toLowerCase().includes(q);
  });

  const withProfile = tgUsers.filter(u => !!u.role).length;
  const withoutProfile = tgUsers.length - withProfile;

  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  };

  return (
    <div className={`${glass} rounded-2xl p-5`}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div>
          <h3 className="font-bold text-base text-white flex items-center gap-2">
            <Send className="h-5 w-5 text-sky-400" /> {t('telegramMembersTitle')}
            <span className="text-slate-500 text-sm font-normal">({tgUsers.length})</span>
          </h3>
          <p className="text-slate-500 text-xs mt-1">{t('telegramMembersDesc')}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2.5 py-1 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 font-bold flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3" /> {t('hasProfile')}: {withProfile}
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-400/30 font-bold flex items-center gap-1.5">
              <AlertCircle className="h-3 w-3" /> {t('noProfile')}: {withoutProfile}
            </span>
          </div>
          <button onClick={onRefresh} className="text-slate-500 hover:text-sky-300 transition-colors p-1.5" title={t('refreshStats')}>
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`${t('telegramId')} / ${t('telegramUsername')} / ${t('fullName')}`} className={`w-full pl-10 pr-3 py-2.5 ${glassInput} rounded-xl text-slate-100 text-sm placeholder-slate-500`} />
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">
          <div className="w-8 h-8 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin mx-auto mb-3"></div>
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-slate-500">{t('noTelegramMembers')}</div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map(u => {
            const hasProfile = !!u.role;
            const banned = u.banned === true;
            const verified = u.verified === true;
            return (
              <div key={u.id} className={`rounded-xl border p-4 transition-colors ${banned ? 'bg-rose-500/12 border-rose-400/30' : hasProfile ? 'bg-sky-500/8 border-sky-400/20' : 'bg-amber-500/8 border-amber-400/20'}`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-sky-500/20 text-sky-300">
                      <Send className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-slate-100 font-bold text-sm truncate">{u.fullName || (u.telegramUsername ? '@' + u.telegramUsername : `ID ${u.telegramId}`)}</span>
                        {verified && <VerifiedBadge size="sm" title={t('verifiedBadgeTooltip')} />}
                        {hasProfile
                          ? <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 flex items-center gap-1"><CheckCircle2 className="h-2.5 w-2.5" />{t('hasProfile')}</span>
                          : <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/40 flex items-center gap-1"><AlertCircle className="h-2.5 w-2.5" />{t('noProfile')}</span>
                        }
                        {hasProfile && <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{u.role === 'sender' ? t('roleSender') : t('roleTraveler')}</span>}
                        {banned && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-rose-500/20 text-rose-300 border border-rose-400/40">{t('statusBanned')}</span>}
                      </div>
                      <div className="text-slate-500 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="font-mono">{t('telegramId')}: {u.telegramId || '—'}</span>
                        {u.telegramUsername && <><span>•</span><span>@{u.telegramUsername}</span></>}
                        <span>•</span>
                        <span>{t('joined')}: {fmtDate(u.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
                    <button onClick={() => onMessage({ id: u.id, uid: u.id, name: u.fullName || (u.telegramUsername ? '@' + u.telegramUsername : `ID ${u.telegramId}`), verified: u.verified })} className="px-3 py-2 rounded-lg text-xs font-bold bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.14] text-slate-200 flex items-center gap-1.5 transition-all">
                      <MessageSquare className="h-3.5 w-3.5" /> {t('sendMessage')}
                    </button>
                    {(isMainAdmin || adminPermissions?.canVerify) && (verified ? (
                      <button onClick={() => onUnverify(u)} className="px-3 py-2 rounded-lg text-xs font-bold bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.14] text-slate-200 flex items-center gap-1.5 transition-all" title={t('unverifyAction')}>
                        <BadgeCheck className="h-3.5 w-3.5 text-sky-500" fill="currentColor" stroke="#fff" strokeWidth={2.5} />
                        <span className="hidden sm:inline">{t('unverifyAction')}</span>
                      </button>
                    ) : (
                      <button onClick={() => onVerify(u)} className="px-3 py-2 rounded-lg text-xs font-bold bg-sky-500/12 hover:bg-sky-500/20 border border-sky-400/40 text-sky-300 flex items-center gap-1.5 transition-all">
                        <BadgeCheck className="h-3.5 w-3.5" /> {t('verifyAction')}
                      </button>
                    ))}
                    {(isMainAdmin || adminPermissions?.canBan) && (banned ? (
                      <button onClick={() => onUnban(u)} disabled={actionLoading === u.id + ':unban'} className="px-3 py-2 rounded-lg text-xs font-bold bg-emerald-500/12 hover:bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 flex items-center gap-1.5 transition-all disabled:opacity-50">
                        {actionLoading === u.id + ':unban' ? <div className="w-3 h-3 border border-emerald-500/50 border-t-emerald-500 rounded-full animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        {t('unbanAction')}
                      </button>
                    ) : (
                      <button onClick={() => onBan(u)} className="px-3 py-2 rounded-lg text-xs font-bold bg-amber-500/12 hover:bg-amber-500/20 border border-amber-400/40 text-amber-300 flex items-center gap-1.5 transition-all">
                        <Ban className="h-3.5 w-3.5" /> {t('banAction')}
                      </button>
                    ))}
                    {(isMainAdmin || adminPermissions?.canDelete) && (
                      <button onClick={() => onDelete(u)} className="px-3 py-2 rounded-lg text-xs font-bold bg-rose-500/12 hover:bg-rose-500/20 border border-rose-400/40 text-rose-300 flex items-center gap-1.5 transition-all">
                        <Trash2 className="h-3.5 w-3.5" /> {t('deleteAction')}
                      </button>
                    )}
                  </div>
                </div>
                {hasProfile && (
                  <div className="mt-2">
                    <button onClick={() => setExpanded(expanded === u.id ? null : u.id)} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-sky-300 transition-colors">
                      {expanded === u.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {expanded === u.id ? t('hideDetails') : t('viewDetails')}
                    </button>
                    {expanded === u.id && <AdminUserDetails u={u} t={t} lang={lang} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==========================================
// AdminEmailVerificationSetting — main-admin-only toggle for the signup
// email verification gate. Reads/writes settings/app.requireEmailVerification.
// ==========================================
function AdminEmailVerificationSetting({ t }) {
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'app'), (snap) => {
      setEnabled(snap.exists() && snap.data().requireEmailVerification === true);
      setLoaded(true);
    }, () => setLoaded(true));
    return () => unsubscribe();
  }, []);

  const toggle = async () => {
    if (saving || !loaded) return;
    const next = !enabled;
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'app'), { requireEmailVerification: next }, { merge: true });
    } catch (err) {
      alert('Failed to update setting.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${glass} p-5 rounded-2xl`}>
      <h3 className="font-bold text-base text-white mb-1 flex items-center gap-2"><Mail className="h-4 w-4 text-slate-500" /> {t('emailVerifySettingTitle')}</h3>
      <p className="text-slate-500 text-sm mb-4 leading-relaxed">{t('emailVerifySettingDesc')}</p>
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" disabled={saving || !loaded} onClick={toggle} className={`relative w-14 h-8 rounded-full transition-all flex-shrink-0 ${enabled ? 'bg-emerald-500' : 'bg-white/[0.15]'} disabled:opacity-50`}>
          <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-md transition-all ${enabled ? 'left-7' : 'left-1'}`} />
        </button>
        <span className={`text-sm font-bold ${enabled ? 'text-emerald-300' : 'text-slate-400'}`}>
          {enabled ? t('emailVerifyStatusOn') : t('emailVerifyStatusOff')}
        </span>
      </div>
    </div>
  );
}

// ==========================================
// AdminPanel — with All Users / Telegram Members tabs
// ==========================================
function AdminPanel({ t, lang, currentUserEmail, isMainAdmin, adminPermissions, onMessage }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [permsDialog, setPermsDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [stats, setStats] = useState({ total: 0, online: 0, banned: 0, dateCount: 0, admins: 0 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [expandedUser, setExpandedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'telegram'

  const formatFirebaseError = (err, defaultMsg) => {
    if (!err) return defaultMsg;
    if (err.code === 'permission-denied') return 'Permission denied by Firestore Security Rules.';
    if (err.code === 'not-found') return 'This user no longer exists in the database.';
    if (err.code === 'unavailable' || err.code === 'deadline-exceeded') return 'Network error. Please try again.';
    return `${defaultMsg}\n\nFirebase error: ${err.code || 'unknown'}`;
  };

  const refreshUsers = async () => {
    setLoadingUsers(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const allUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const createdMs = (u) => {
        if (!u.createdAt) return null;
        if (u.createdAt.toMillis) return u.createdAt.toMillis();
        if (u.createdAt.seconds != null) return u.createdAt.seconds * 1000;
        const d = new Date(u.createdAt);
        return isNaN(d.getTime()) ? null : d.getTime();
      };
      allUsers.sort((a, b) => {
        const ta = createdMs(a), tb = createdMs(b);
        if (ta == null && tb == null) return 0;
        if (ta == null) return 1;
        if (tb == null) return -1;
        return tb - ta;
      });
      setUsers(allUsers);

      const fifteenMinsAgo = new Date(Date.now() - 15 * 60 * 1000);
      const onlineCount = allUsers.filter(u => {
        if (!u.lastSeen) return false;
        const lastSeenDate = u.lastSeen.toDate ? u.lastSeen.toDate() : new Date(u.lastSeen);
        return lastSeenDate > fifteenMinsAgo;
      }).length;
      const bannedCount = allUsers.filter(u => u.banned === true).length;
      const adminCount = allUsers.filter(u => u.isAdmin === true || u.email === 'administrator@fly-carrier.com').length;
      setStats(prev => ({ ...prev, total: allUsers.length, online: onlineCount, banned: bannedCount, admins: adminCount }));
    } catch (err) { console.error('Failed to load users:', err); }
    finally { setLoadingUsers(false); }
  };

  useEffect(() => { refreshUsers(); }, []);

  const handleFilter = () => {
    if (!startDate || !endDate) return;
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59);
    const filtered = users.filter(u => {
      const dateToCheck = u.createdAt ? (u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt)) : null;
      if (!dateToCheck) return false;
      return dateToCheck >= start && dateToCheck <= end;
    });
    setStats(prev => ({ ...prev, dateCount: filtered.length }));
  };

  const filteredUsers = (() => {
    const base = users.filter(u => {
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();
      return (u.email || '').toLowerCase().includes(q) || (u.fullName || '').toLowerCase().includes(q);
    });
    if (sortOrder === 'oldest') {
      const withDate = base.filter(u => u.createdAt);
      const noDate = base.filter(u => !u.createdAt);
      return [...withDate.reverse(), ...noDate];
    }
    return base;
  })();

  const doBan = async (user) => {
    setActionLoading(user.id + ':ban');
    try {
      await updateDoc(doc(db, 'users', user.id), { banned: true, bannedAt: serverTimestamp(), bannedBy: currentUserEmail || 'admin' });
      await refreshUsers();
    } catch (err) { alert(formatFirebaseError(err, 'Failed to ban user.')); }
    finally { setActionLoading(null); setConfirmDialog(null); }
  };
  const doUnban = async (user) => {
    setActionLoading(user.id + ':unban');
    try {
      await updateDoc(doc(db, 'users', user.id), { banned: false, bannedAt: null, bannedBy: null });
      await refreshUsers();
    } catch (err) { alert(formatFirebaseError(err, 'Failed to unban user.')); }
    finally { setActionLoading(null); }
  };
  const doDelete = async (user) => {
    setActionLoading(user.id + ':delete');
    try {
      await deleteDoc(doc(db, 'users', user.id));
      await refreshUsers();
    } catch (err) { alert(formatFirebaseError(err, 'Failed to delete user profile.')); }
    finally { setActionLoading(null); setConfirmDialog(null); }
  };
  const doVerify = async (user) => {
    setActionLoading(user.id + ':verify');
    try {
      await updateDoc(doc(db, 'users', user.id), { verified: true, verifiedAt: serverTimestamp(), verifiedBy: currentUserEmail || 'admin' });
      await refreshUsers();
    } catch (err) { alert(formatFirebaseError(err, 'Failed to verify user.')); }
    finally { setActionLoading(null); setConfirmDialog(null); }
  };
  const doUnverify = async (user) => {
    setActionLoading(user.id + ':unverify');
    try {
      await updateDoc(doc(db, 'users', user.id), { verified: false, verifiedAt: null, verifiedBy: null });
      await refreshUsers();
    } catch (err) { alert(formatFirebaseError(err, 'Failed to remove verification.')); }
    finally { setActionLoading(null); setConfirmDialog(null); }
  };
  const doPromote = async (user, perms) => {
    if (!isMainAdmin) { alert(t('notAuthorized')); return; }
    setActionLoading(user.id + ':promote');
    try {
      await updateDoc(doc(db, 'users', user.id), { isAdmin: true, adminPermissions: perms || { canBan: true, canDelete: false, canVerify: true }, adminPromotedAt: serverTimestamp(), adminPromotedBy: currentUserEmail });
      await refreshUsers();
    } catch (err) { alert(formatFirebaseError(err, 'Failed to promote user.')); }
    finally { setActionLoading(null); setConfirmDialog(null); setPermsDialog(null); }
  };
  const doRevokeAdmin = async (user) => {
    if (!isMainAdmin) { alert(t('notAuthorized')); return; }
    setActionLoading(user.id + ':revoke');
    try {
      await updateDoc(doc(db, 'users', user.id), { isAdmin: false, adminPermissions: null, adminRevokedAt: serverTimestamp(), adminRevokedBy: currentUserEmail });
      await refreshUsers();
    } catch (err) { alert(formatFirebaseError(err, 'Failed to revoke admin access.')); }
    finally { setActionLoading(null); setConfirmDialog(null); }
  };
  const doSavePerms = async (user, perms) => {
    if (!isMainAdmin) { alert(t('notAuthorized')); return; }
    setActionLoading(user.id + ':perms');
    try {
      await updateDoc(doc(db, 'users', user.id), { adminPermissions: perms, adminPermsUpdatedAt: serverTimestamp(), adminPermsUpdatedBy: currentUserEmail });
      await refreshUsers();
    } catch (err) { alert(formatFirebaseError(err, 'Failed to save permissions.')); }
    finally { setActionLoading(null); setPermsDialog(null); }
  };

  const isOnline = (u) => {
    if (!u.lastSeen) return false;
    const d = u.lastSeen.toDate ? u.lastSeen.toDate() : new Date(u.lastSeen);
    return d > new Date(Date.now() - 15 * 60 * 1000);
  };
  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
      <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
        <ShieldCheck className="h-8 w-8 text-amber-400" /> {t('adminPanel')}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className={`${glass} p-5 rounded-2xl flex items-center justify-between`}>
          <div>
            <p className="text-slate-500 font-medium text-sm mb-1">{t('totalUsers')}</p>
            <h3 className="text-3xl font-black text-white">{stats.total}</h3>
          </div>
          <div className="p-2.5 bg-violet-400/15 text-violet-400 rounded-xl"><Users className="h-6 w-6" /></div>
        </div>
        <div className={`${glass} p-5 rounded-2xl flex items-center justify-between`}>
          <div>
            <p className="text-slate-500 font-medium text-sm mb-1">{t('onlineUsers')}</p>
            <h3 className="text-3xl font-black text-emerald-400">{stats.online}</h3>
          </div>
          <div className="p-2.5 bg-emerald-400/15 text-emerald-300 rounded-xl"><Globe className="h-6 w-6" /></div>
        </div>
        <div className={`${glass} p-5 rounded-2xl flex items-center justify-between`}>
          <div>
            <p className="text-slate-500 font-medium text-sm mb-1">{t('statusBanned')}</p>
            <h3 className="text-3xl font-black text-rose-400">{stats.banned}</h3>
          </div>
          <div className="p-2.5 bg-rose-400/15 text-rose-300 rounded-xl"><Ban className="h-6 w-6" /></div>
        </div>
      </div>

      <AdminStatsPanel t={t} users={users} loadingUsers={loadingUsers} isMainAdmin={isMainAdmin} />

      {isMainAdmin && <AdminEmailVerificationSetting t={t} />}

      <div className={`${glass} p-5 rounded-2xl`}>
        <h3 className="font-bold text-base text-white mb-3 flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-500" /> {t('newSignups')}</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('startDate')}</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={`p-2.5 ${glassInput} rounded-lg text-slate-100 text-sm`} />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('endDate')}</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={`p-2.5 ${glassInput} rounded-lg text-slate-100 text-sm`} />
          </div>
          <button onClick={handleFilter} className={`${btnPrimary} px-5 py-2.5 rounded-lg text-sm`}>{t('apply')}</button>
          {stats.dateCount > 0 && (
            <div className={`p-2.5 ${glassCard} rounded-xl`}>
              <span className="text-slate-500 mr-2 text-xs">Signups:</span>
              <span className="text-xl font-black text-amber-400">{stats.dateCount}</span>
            </div>
          )}
        </div>
      </div>

      <AdminVerificationRequests t={t} currentUserEmail={currentUserEmail} adminPermissions={adminPermissions} onMessage={onMessage} />

      {/* Tabs: All Users / Telegram Members */}
      <div className="flex items-center gap-2 p-1 rounded-2xl bg-white/[0.04] border border-white/[0.10] w-fit">
        <button onClick={() => setActiveTab('all')} className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'all' ? 'bg-violet-500/25 text-violet-200 border border-violet-400/40 shadow-sm' : 'text-slate-500 hover:text-slate-200'}`}>
          <Users className="h-4 w-4" /> {t('manageUsers')}
        </button>
        <button onClick={() => setActiveTab('telegram')} className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'telegram' ? 'bg-sky-500/25 text-sky-200 border border-sky-400/40 shadow-sm' : 'text-slate-500 hover:text-slate-200'}`}>
          <Send className="h-4 w-4" /> {t('telegramTab')}
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-black bg-sky-500/20 text-sky-300">{users.filter(u => u.signupSource === 'telegram').length}</span>
        </button>
      </div>

      {activeTab === 'telegram' ? (
        <TelegramMembersTab t={t} lang={lang} users={users} loading={loadingUsers} onMessage={onMessage} onRefresh={refreshUsers}
          adminPermissions={adminPermissions} isMainAdmin={isMainAdmin} actionLoading={actionLoading}
          onVerify={(u) => setConfirmDialog({ type: 'verify', user: u })}
          onUnverify={(u) => setConfirmDialog({ type: 'unverify', user: u })}
          onBan={(u) => setConfirmDialog({ type: 'ban', user: u })}
          onUnban={doUnban}
          onDelete={(u) => setConfirmDialog({ type: 'delete', user: u })} />
      ) : (
      <div className={`${glass} rounded-2xl p-5`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <h3 className="font-bold text-base text-white flex items-center gap-2">
            <Users className="h-5 w-5 text-violet-500" /> {t('manageUsers')}
            <span className="text-slate-500 text-sm font-normal">({filteredUsers.length})</span>
          </h3>
          <div className="flex flex-col sm:flex-row gap-3 lg:items-center">
            <div className="flex items-center gap-1 p-1 rounded-xl bg-white/[0.05] border border-white/[0.10] flex-shrink-0">
              <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider px-2 hidden sm:inline">{t('sortLabel')}</span>
              <button onClick={() => setSortOrder('newest')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${sortOrder === 'newest' ? 'bg-violet-500/25 text-violet-200 border border-violet-400/40' : 'text-slate-500 hover:text-slate-200'}`}>
                <ChevronDown className="h-3 w-3" /> {t('sortNewest')}
              </button>
              <button onClick={() => setSortOrder('oldest')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${sortOrder === 'oldest' ? 'bg-violet-500/25 text-violet-200 border border-violet-400/40' : 'text-slate-500 hover:text-slate-200'}`}>
                <ChevronUp className="h-3 w-3" /> {t('sortOldest')}
              </button>
            </div>
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={t('searchUsersPlaceholder')} className={`w-full pl-10 pr-3 py-2.5 ${glassInput} rounded-xl text-slate-100 text-sm placeholder-slate-500`} />
            </div>
          </div>
        </div>

        {loadingUsers ? (
          <div className="text-center py-10 text-slate-500">
            <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin mx-auto mb-3"></div>
            Loading...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-10 text-slate-500">{t('noUsersFound')}</div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {filteredUsers.map(u => {
              const banned = u.banned === true;
              const verified = u.verified === true;
              const online = isOnline(u);
              const isSelf = u.email === currentUserEmail;
              const isMain = u.email === 'administrator@fly-carrier.com';
              const isSub = u.isAdmin === true && !isMain;
              const protectedRow = isSelf || isMain;
              const isTg = u.signupSource === 'telegram';
              const compOffer = u.companionAccept && (u.companionAccept.wheelchair || u.companionAccept.walking);
              const uSenderItems = (Array.isArray(u.senderItems) && u.senderItems.length > 0) ? u.senderItems : (u.packageType ? [u.packageType] : []);
              const compNeed = uSenderItems.includes('companion');
              return (
                <div key={u.id} className={`rounded-xl border p-4 transition-colors ${banned ? 'bg-rose-500/12 border-rose-400/30' : verified ? 'bg-sky-500/10 border-sky-400/25' : isSub ? 'bg-amber-500/10 border-amber-400/25' : 'bg-white/[0.06] border-white/[0.10] hover:bg-white/[0.08]'}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${banned ? 'bg-rose-500/20 text-rose-400' : isSub ? 'bg-amber-500/20 text-amber-300' : isMain ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : u.role === 'sender' ? 'bg-violet-500/20 text-violet-400' : u.role === 'traveler' ? 'bg-blue-500/20 text-blue-400' : 'bg-white/[0.08] text-slate-500'}`}>
                        {banned ? <Ban className="h-5 w-5" /> : (isMain || isSub) ? <ShieldCheck className="h-5 w-5" /> : u.role === 'sender' ? <Package className="h-5 w-5" /> : u.role === 'traveler' ? <Plane className="h-5 w-5" /> : <User className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-slate-100 font-bold text-sm truncate">{u.fullName || u.email || (isTg ? `TG ${u.telegramId}` : '—')}</span>
                          {verified && <VerifiedBadge size="sm" title={t('verifiedBadgeTooltip')} />}
                          {isTg && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-sky-500/20 text-sky-300 border border-sky-400/40 flex items-center gap-1"><Send className="h-2.5 w-2.5" />{t('telegramBadge')}</span>}
                          {isMain && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-orange-500 text-white">{t('mainAdminBadge')}</span>}
                          {isSub && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/40">{t('subAdminBadge')}</span>}
                          {compOffer && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-teal-500/20 text-teal-300 border border-teal-400/40 flex items-center gap-1"><HeartHandshake className="h-2.5 w-2.5" />{t('companionService')}</span>}
                          {compNeed && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-teal-500/20 text-teal-300 border border-teal-400/40 flex items-center gap-1"><Accessibility className="h-2.5 w-2.5" />{t('companionNeeded')}</span>}
                          {online && !banned && <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>{t('statusOnline')}</span>}
                          {banned && <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-rose-500/20 text-rose-300 border border-rose-400/40">{t('statusBanned')}</span>}
                        </div>
                        <div className="text-slate-500 text-xs truncate mt-0.5">{u.email || (isTg && u.telegramUsername ? '@' + u.telegramUsername : (isTg ? t('viaTelegram') : ''))}</div>
                        <div className="text-slate-500 text-[11px] mt-0.5 flex items-center gap-2 flex-wrap">
                          {u.role && <span className="font-bold uppercase tracking-wider">{u.role === 'sender' ? t('roleSender') : t('roleTraveler')}</span>}
                          <span>•</span>
                          <span>{t('joined')}: {fmtDate(u.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    {!protectedRow && (
                      <div className="flex flex-wrap gap-2 flex-shrink-0">
                        {adminPermissions.canVerify && (verified ? (
                          <button onClick={() => setConfirmDialog({ type: 'unverify', user: u })} className="px-3 py-2 rounded-lg text-xs font-bold bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.14] text-slate-200 flex items-center gap-1.5 transition-all" title={t('unverifyAction')}>
                            <BadgeCheck className="h-3.5 w-3.5 text-sky-500" fill="currentColor" stroke="#fff" strokeWidth={2.5} />
                            <span className="hidden sm:inline">{t('unverifyAction')}</span>
                          </button>
                        ) : (
                          <button onClick={() => setConfirmDialog({ type: 'verify', user: u })} className="px-3 py-2 rounded-lg text-xs font-bold bg-sky-500/12 hover:bg-sky-500/20 border border-sky-400/40 text-sky-300 flex items-center gap-1.5 transition-all">
                            <BadgeCheck className="h-3.5 w-3.5" /> {t('verifyAction')}
                          </button>
                        ))}
                        {adminPermissions.canBan && (banned ? (
                          <button onClick={() => doUnban(u)} disabled={actionLoading === u.id + ':unban'} className="px-3 py-2 rounded-lg text-xs font-bold bg-emerald-500/12 hover:bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 flex items-center gap-1.5 transition-all disabled:opacity-50">
                            {actionLoading === u.id + ':unban' ? <div className="w-3 h-3 border border-emerald-500/50 border-t-emerald-500 rounded-full animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                            {t('unbanAction')}
                          </button>
                        ) : (
                          <button onClick={() => setConfirmDialog({ type: 'ban', user: u })} className="px-3 py-2 rounded-lg text-xs font-bold bg-amber-500/12 hover:bg-amber-500/20 border border-amber-400/40 text-amber-300 flex items-center gap-1.5 transition-all">
                            <Ban className="h-3.5 w-3.5" /> {t('banAction')}
                          </button>
                        ))}
                        {adminPermissions.canDelete && (
                          <button onClick={() => setConfirmDialog({ type: 'delete', user: u })} className="px-3 py-2 rounded-lg text-xs font-bold bg-rose-500/12 hover:bg-rose-500/20 border border-rose-400/40 text-rose-300 flex items-center gap-1.5 transition-all">
                            <Trash2 className="h-3.5 w-3.5" /> {t('deleteAction')}
                          </button>
                        )}
                        {isMainAdmin && !isSub && (
                          <button onClick={() => setPermsDialog({ user: u, mode: 'promote', perms: { canBan: true, canDelete: false, canVerify: true } })} className="px-3 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white flex items-center gap-1.5 transition-all shadow-sm">
                            <ShieldCheck className="h-3.5 w-3.5" /> {t('promoteToAdmin')}
                          </button>
                        )}
                        {isMainAdmin && isSub && (
                          <>
                            <button onClick={() => setPermsDialog({ user: u, mode: 'edit', perms: u.adminPermissions || { canBan: false, canDelete: false, canVerify: false } })} className="px-3 py-2 rounded-lg text-xs font-bold bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-300 flex items-center gap-1.5 transition-all">
                              <ShieldAlert className="h-3.5 w-3.5" /> {t('managePermissions')}
                            </button>
                            <button onClick={() => setConfirmDialog({ type: 'revoke', user: u })} className="px-3 py-2 rounded-lg text-xs font-bold bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.14] text-slate-200 flex items-center gap-1.5 transition-all">
                              <UserX className="h-3.5 w-3.5" /> {t('revokeAdmin')}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {protectedRow && (
                      <span className="text-slate-500 text-xs italic">— protected —</span>
                    )}
                  </div>

                  <div className="mt-2">
                    <button
                      onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-violet-300 transition-colors"
                    >
                      {expandedUser === u.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {expandedUser === u.id ? t('hideDetails') : t('viewDetails')}
                    </button>
                    {expandedUser === u.id && <AdminUserDetails u={u} t={t} lang={lang} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {confirmDialog && (() => {
        const t_ = confirmDialog.type;
        const accent = t_ === 'delete' ? { border: 'border-rose-400/40', bg: 'bg-rose-500/20', text: 'text-rose-400', icon: UserX, btn: 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600' }
          : t_ === 'ban' ? { border: 'border-amber-400/40', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: ShieldAlert, btn: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' }
          : t_ === 'verify' ? { border: 'border-sky-400/40', bg: 'bg-sky-500/20', text: 'text-sky-400', icon: BadgeCheck, btn: 'bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600' }
          : t_ === 'revoke' ? { border: 'border-white/[0.14]', bg: 'bg-white/[0.08]', text: 'text-slate-500', icon: UserX, btn: 'bg-gradient-to-r from-slate-500 to-slate-700 hover:from-slate-600 hover:to-slate-800' }
          : { border: 'border-white/[0.14]', bg: 'bg-white/[0.08]', text: 'text-slate-500', icon: ShieldAlert, btn: 'bg-gradient-to-r from-violet-500 to-blue-500 hover:from-violet-600 hover:to-blue-600' };
        const title = t_ === 'delete' ? t('confirmDeleteTitle') : t_ === 'ban' ? t('confirmBanTitle') : t_ === 'verify' ? t('confirmVerifyTitle') : t_ === 'unverify' ? t('confirmUnverifyTitle') : t_ === 'revoke' ? t('confirmRevokeTitle') : '';
        const desc = t_ === 'delete' ? t('confirmDeleteDesc') : t_ === 'ban' ? t('confirmBanDesc') : t_ === 'verify' ? t('confirmVerifyDesc') : t_ === 'unverify' ? t('confirmUnverifyDesc') : t_ === 'revoke' ? t('confirmRevokeDesc') : '';
        const btnLabel = t_ === 'delete' ? t('deleteAction') : t_ === 'ban' ? t('banAction') : t_ === 'verify' ? t('verifyAction') : t_ === 'unverify' ? t('unverifyAction') : t_ === 'revoke' ? t('revokeAdmin') : '';
        const BtnIcon = t_ === 'delete' ? Trash2 : t_ === 'ban' ? Ban : t_ === 'revoke' ? UserX : BadgeCheck;
        const action = () => {
          if (t_ === 'delete') return doDelete(confirmDialog.user);
          if (t_ === 'ban') return doBan(confirmDialog.user);
          if (t_ === 'verify') return doVerify(confirmDialog.user);
          if (t_ === 'unverify') return doUnverify(confirmDialog.user);
          if (t_ === 'revoke') return doRevokeAdmin(confirmDialog.user);
        };
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`${glass} rounded-3xl p-8 max-w-md w-full border-2 ${accent.border}`}>
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 ${accent.bg}`}>
                <accent.icon className={`h-7 w-7 ${accent.text}`} />
              </div>
              <h3 className="text-xl font-extrabold text-white text-center mb-2">{title}</h3>
              <p className="text-slate-500 text-sm text-center mb-2 leading-relaxed">{desc}</p>
              <p className="text-slate-500 text-xs text-center mb-6 break-all font-mono">{confirmDialog.user.email || confirmDialog.user.telegramId || confirmDialog.user.id}</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDialog(null)} className="flex-1 py-3 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] text-slate-100 font-bold transition-all">{t('cancel')}</button>
                <button onClick={action} disabled={!!actionLoading} className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-white ${accent.btn}`}>
                  {actionLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><BtnIcon className="h-4 w-4" /> {btnLabel}</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {permsDialog && (
        <PermissionsDialog user={permsDialog.user} mode={permsDialog.mode} initialPerms={permsDialog.perms} actionLoading={actionLoading} t={t} onCancel={() => setPermsDialog(null)} onSave={(perms) => { if (permsDialog.mode === 'promote') doPromote(permsDialog.user, perms); else doSavePerms(permsDialog.user, perms); }} />
      )}
    </div>
  );
}

// ==========================================
// PermissionsDialog — grant/edit sub-admin permissions
// ==========================================
function PermissionsDialog({ user, mode, initialPerms, actionLoading, t, onCancel, onSave }) {
  const [perms, setPerms] = useState(initialPerms || { canBan: false, canDelete: false, canVerify: false });
  const toggle = (key) => setPerms(p => ({ ...p, [key]: !p[key] }));
  const busy = actionLoading === user.id + ':promote' || actionLoading === user.id + ':perms';

  const rows = [
    { key: 'canBan', label: t('permCanBan'), icon: Ban, color: 'text-amber-300' },
    { key: 'canDelete', label: t('permCanDelete'), icon: Trash2, color: 'text-rose-300' },
    { key: 'canVerify', label: t('permCanVerify'), icon: BadgeCheck, color: 'text-sky-300' },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className={`${glass} rounded-3xl p-8 max-w-md w-full border-2 border-amber-400/30`}>
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 bg-gradient-to-br from-amber-400 to-orange-500 shadow-md">
          <ShieldCheck className="h-7 w-7 text-white" />
        </div>
        <h3 className="text-xl font-extrabold text-white text-center mb-1">{t('permsHeading')}</h3>
        <p className="text-slate-500 text-sm text-center mb-2 leading-relaxed">{t('permsDescription')}</p>
        <p className="text-slate-500 text-xs text-center mb-6 break-all font-mono">{user.email || user.fullName}</p>

        <div className="space-y-2.5 mb-6">
          {rows.map(r => (
            <label key={r.key} className={`flex items-center gap-3 cursor-pointer select-none p-4 rounded-2xl border-2 transition-all ${perms[r.key] ? 'border-amber-400/50 bg-amber-500/12' : 'border-white/[0.10] bg-white/[0.05] hover:bg-white/[0.08]'}`}>
              <input type="checkbox" checked={!!perms[r.key]} onChange={() => toggle(r.key)} className="w-5 h-5 rounded accent-amber-500 cursor-pointer flex-shrink-0" />
              <r.icon className={`h-5 w-5 flex-shrink-0 ${perms[r.key] ? r.color : 'text-slate-500'}`} />
              <span className={`text-sm font-bold ${perms[r.key] ? 'text-slate-100' : 'text-slate-400'}`}>{r.label}</span>
            </label>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-3 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] text-slate-100 font-bold transition-all">{t('cancel')}</button>
          <button onClick={() => onSave(perms)} disabled={busy} className="flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-white bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 shadow-sm">
            {busy ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> {mode === 'promote' ? t('promoteToAdmin') : t('savePermissions')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// SenderSafetyNotice — dismissible warning for senders
// ==========================================
function SenderSafetyNotice({ t, dir }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className="relative overflow-hidden rounded-3xl border-2 border-amber-400/30" style={{ background: 'rgba(251,191,36,0.08)' }}>
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400"></div>
      <div className="p-5 sm:p-6 flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-amber-400/20 rounded-2xl flex items-center justify-center mt-0.5">
          <ShieldAlert className="h-7 w-7 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h4 className="text-amber-200 font-extrabold text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {t('senderSafetyTitle')}
            </h4>
            <button onClick={() => setDismissed(true)} className="text-amber-300/60 hover:text-amber-200 flex-shrink-0 transition-colors" title={t('dismiss')}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-amber-100/85 leading-relaxed text-[14px]">{t('senderSafetyBody')}</p>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-500/15 border border-sky-400/30">
            <BadgeCheck className="h-4 w-4 text-sky-300" fill="currentColor" stroke="#0c1330" strokeWidth={2.5} />
            <span className="text-sky-200 text-xs font-bold">{t('verified')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// VerificationRequestForm — traveler submits identity verification request
// ==========================================
function VerificationRequestForm({ user, profile, onClose, onSubmitted, t, dir, lang }) {
  const STEP_COUNT = 5;
  const [step, setStep] = useState(1);
  const [passportPhotoFile, setPassportPhotoFile] = useState(null);
  const [passportVideoFile, setPassportVideoFile] = useState(null);
  const [selfieVideoFile, setSelfieVideoFile] = useState(null);
  const [phone, setPhone] = useState(profile?.phone || '');
  const [fullName, setFullName] = useState(profile?.fullName || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const photoInputRef = useRef(null);
  const passportVideoInputRef = useRef(null);
  const selfieVideoInputRef = useRef(null);

  const today = new Date().toISOString().slice(0, 10);
  const tutorialImageUrl = lang === 'fa'
    ? 'https://www.fly-carrier.com/clips/LEARN.jpg'
    : 'https://www.fly-carrier.com/clips/LEARNEN.png';

  const pickFile = (setter, kind) => (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (kind === 'photo' && !file.type.startsWith('image/')) { setError(t('verificationInvalidPhoto')); return; }
    if (kind === 'video' && !file.type.startsWith('video/')) { setError(t('verificationInvalidVideo')); return; }
    setError('');
    setter(file);
  };

  const goNext = () => {
    if (step === 1 && !passportPhotoFile) { setError(t('verificationFileRequired')); return; }
    if (step === 2 && !passportVideoFile) { setError(t('verificationFileRequired')); return; }
    if (step === 3 && !selfieVideoFile) { setError(t('verificationFileRequired')); return; }
    if (step === 4 && !phone.trim()) { setError(t('verificationFileRequired')); return; }
    setError('');
    setStep((s) => Math.min(STEP_COUNT, s + 1));
  };

  const goBack = () => { setError(''); setStep((s) => Math.max(1, s - 1)); };

  const handleSubmit = async () => {
    if (!fullName.trim()) { setError(t('verificationFileRequired')); return; }
    setUploading(true);
    setError('');
    try {
      const folder = `DADEHA/${sanitizeForStoragePath(fullName)}-${user.uid.slice(0, 6)}`;

      const uploadOne = async (file, name) => {
        const fileRef = storageRef(storage, `${folder}/${name}-${Date.now()}-${file.name}`);
        await uploadBytes(fileRef, file);
        return getDownloadURL(fileRef);
      };

      const [passportPhotoUrl, passportVideoUrl, selfieVideoUrl] = await Promise.all([
        uploadOne(passportPhotoFile, 'passport-photo'),
        uploadOne(passportVideoFile, 'passport-video'),
        uploadOne(selfieVideoFile, 'selfie-video'),
      ]);

      await setDoc(doc(db, 'users', user.uid), {
        verificationStatus: 'pending',
        verificationRequestedAt: serverTimestamp(),
        fullName: fullName.trim(),
        phone: phone.trim(),
      }, { merge: true });

      await addDoc(collection(db, 'verificationRequests'), {
        userId: user.uid,
        email: user.email || '',
        fullName: fullName.trim(),
        phone: phone.trim(),
        passportPhotoUrl,
        passportVideoUrl,
        selfieVideoUrl,
        status: 'pending',
        createdAt: serverTimestamp(),
        signupSource: 'web',
      });

      onSubmitted && onSubmitted();
    } catch (err) {
      console.error('Verification request failed:', err);
      setError(err?.message || 'Could not submit request.');
    } finally {
      setUploading(false);
    }
  };

  const FilePickerButton = ({ file, onClick, chooseLabel }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full py-3 px-3 rounded-xl border-2 border-dashed text-sm font-bold flex items-center justify-center gap-2 transition-all ${file ? 'border-emerald-400/50 bg-emerald-500/10 text-emerald-300' : 'border-sky-400/30 bg-white/[0.03] text-sky-200'}`}
    >
      {file ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> : <FileText className="h-4 w-4 flex-shrink-0" />}
      <span className="truncate">{file ? `${t('verificationChangeFile')} — ${file.name}` : chooseLabel}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" dir={dir}>
      <div className={`${glass} rounded-3xl max-w-lg w-full border-2 border-sky-400/30 my-4 max-h-[92vh] flex flex-col overflow-hidden`}>
        <div className="overflow-y-auto p-8 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shadow-md">
              <BadgeCheck className="h-6 w-6 text-white" fill="currentColor" stroke="#0c1330" strokeWidth={2.5} />
            </div>
            <h3 className="text-xl font-extrabold text-white">{t('verificationFormTitle')}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100 transition-colors"><X className="h-6 w-6" /></button>
        </div>
        <p className="text-slate-500 text-sm mb-4 leading-relaxed">{t('verificationFormDesc')}</p>

        {/* Step progress */}
        <div className="flex items-center gap-1.5 mb-2">
          {Array.from({ length: STEP_COUNT }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i < step ? 'bg-sky-400' : 'bg-white/10'}`} />
          ))}
        </div>
        <p className="text-sky-300 text-[11px] font-bold mb-4">{t('verificationStepLabel').replace('{n}', step)}</p>

        <div className="space-y-3 min-h-[220px]">
          {step === 1 && (
            <div>
              <h4 className="text-sky-200 font-extrabold text-sm mb-1.5">{t('verificationStep1Title')}</h4>
              <p className="text-slate-500 text-[13px] leading-relaxed mb-3">{t('verificationStep1Desc')}</p>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={pickFile(setPassportPhotoFile, 'photo')} />
              <FilePickerButton file={passportPhotoFile} onClick={() => photoInputRef.current?.click()} chooseLabel={t('verificationChoosePhoto')} />
            </div>
          )}

          {step === 2 && (
            <div>
              <h4 className="text-sky-200 font-extrabold text-sm mb-1.5">{t('verificationStep2Title')}</h4>
              <p className="text-slate-500 text-[13px] leading-relaxed mb-3">{t('verificationStep2Desc')}</p>
              <input ref={passportVideoInputRef} type="file" accept="video/*" className="hidden" onChange={pickFile(setPassportVideoFile, 'video')} />
              <FilePickerButton file={passportVideoFile} onClick={() => passportVideoInputRef.current?.click()} chooseLabel={t('verificationChooseVideo')} />
            </div>
          )}

          {step === 3 && (
            <div>
              <h4 className="text-sky-200 font-extrabold text-sm mb-1.5">{t('verificationStep3Title')}</h4>
              <p className="text-slate-500 text-[13px] leading-relaxed mb-3">{t('verificationStep3Desc')}</p>
              <div className="rounded-xl overflow-hidden border border-sky-400/30 mb-3">
                <img src={tutorialImageUrl} alt="" className="w-full h-auto block" />
              </div>
              <div className="rounded-xl bg-sky-500/10 border border-sky-400/30 p-3 mb-3">
                <p className="text-sky-100/90 text-[13px] leading-relaxed font-medium">{t('verificationSelfieSampleText').replace('{date}', today)}</p>
              </div>
              <input ref={selfieVideoInputRef} type="file" accept="video/*" className="hidden" onChange={pickFile(setSelfieVideoFile, 'video')} />
              <FilePickerButton file={selfieVideoFile} onClick={() => selfieVideoInputRef.current?.click()} chooseLabel={t('verificationChooseVideo')} />
            </div>
          )}

          {step === 4 && (
            <div>
              <h4 className="text-sky-200 font-extrabold text-sm mb-1.5">{t('verificationStep4Title')}</h4>
              <p className="text-slate-500 text-[13px] leading-relaxed mb-3">{t('verificationStep4Desc')}</p>
              <input type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setError(''); }} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} placeholder="+1 555 123 4567" />
            </div>
          )}

          {step === 5 && (
            <div>
              <h4 className="text-sky-200 font-extrabold text-sm mb-1.5">{t('verificationStep5Title')}</h4>
              <p className="text-slate-500 text-[13px] leading-relaxed mb-3">{t('verificationStep5Desc')}</p>
              <label className="block text-slate-500 text-xs font-bold mb-1.5">{t('verificationFullName')}</label>
              <input type="text" value={fullName} onChange={(e) => { setFullName(e.target.value); setError(''); }} className={`w-full py-2.5 px-3 rounded-xl text-slate-100 text-sm ${glassInput}`} placeholder="John Doe" />
            </div>
          )}

          {error && (
            <div className="bg-rose-500/12 border border-rose-400/30 rounded-xl p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-rose-400 flex-shrink-0 mt-0.5" />
              <p className="text-rose-300 text-xs font-bold">{error}</p>
            </div>
          )}

          {uploading && (
            <p className="text-sky-300 text-xs font-bold flex items-center gap-2">
              <div className="w-3.5 h-3.5 border-2 border-sky-300/30 border-t-sky-300 rounded-full animate-spin" /> {t('verificationUploading')}
            </p>
          )}
        </div>
        </div>

        <div className="flex gap-3 px-8 py-4 border-t border-white/[0.10] flex-shrink-0">
          <button onClick={step === 1 ? onClose : goBack} disabled={uploading} className="flex-1 py-3 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] text-slate-100 font-bold transition-all disabled:opacity-40">
            {step === 1 ? t('cancel') : t('back')}
          </button>
          {step < STEP_COUNT ? (
            <button onClick={goNext} className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-white ${btnPrimary}`}>
              {t('next')} <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={uploading || !fullName.trim()} className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-white ${btnPrimary} disabled:opacity-40`}>
              {uploading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><BadgeCheck className="h-4 w-4" /> {t('submitVerification')}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// AdminVerificationRequests — admins review & approve/reject verification requests
// ==========================================
function AdminVerificationRequests({ t, currentUserEmail, adminPermissions, onMessage }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [fileActionLoading, setFileActionLoading] = useState(null);
  const [loadError, setLoadError] = useState('');

  const refresh = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const snap = await getDocs(collection(db, 'verificationRequests'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ms = (x) => x.createdAt?.toMillis ? x.createdAt.toMillis() : (x.createdAt?.seconds ? x.createdAt.seconds * 1000 : 0);
        return ms(b) - ms(a);
      });
      setRequests(list);
    } catch (err) {
      console.error('Failed to load verification requests:', err);
      setLoadError(err?.message || 'Failed to load verification requests.');
    }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const approve = async (req) => {
    if (!adminPermissions.canVerify) { alert(t('notAuthorized')); return; }
    setActionLoading(req.id + ':approve');
    try {
      await updateDoc(doc(db, 'users', req.userId), { verified: true, verificationStatus: 'approved', verifiedAt: serverTimestamp(), verifiedBy: currentUserEmail || 'admin' });
      await updateDoc(doc(db, 'verificationRequests', req.id), { status: 'approved', reviewedAt: serverTimestamp(), reviewedBy: currentUserEmail || 'admin' });
      await refresh();
    } catch (err) { alert(err?.message || 'Failed to approve.'); }
    finally { setActionLoading(null); }
  };
  const reject = async (req) => {
    if (!adminPermissions.canVerify) { alert(t('notAuthorized')); return; }
    setActionLoading(req.id + ':reject');
    try {
      await updateDoc(doc(db, 'users', req.userId), { verificationStatus: 'rejected' });
      await updateDoc(doc(db, 'verificationRequests', req.id), { status: 'rejected', reviewedAt: serverTimestamp(), reviewedBy: currentUserEmail || 'admin' });
      await refresh();
    } catch (err) { alert(err?.message || 'Failed to reject.'); }
    finally { setActionLoading(null); }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    } catch (e) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const deleteFile = async (req, field) => {
    if (!adminPermissions.canDelete) { alert(t('notAuthorized')); return; }
    if (!window.confirm(t('confirmDeleteVerificationFile'))) return;
    const key = req.id + ':' + field;
    setFileActionLoading(key);
    try {
      await deleteObject(storageRef(storage, req[field]));
      await updateDoc(doc(db, 'verificationRequests', req.id), { [field]: null });
      await refresh();
    } catch (err) { alert(err?.message || 'Failed to delete file.'); }
    finally { setFileActionLoading(null); }
  };

  const pending = requests.filter(r => r.status === 'pending');
  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  };

  const statusChip = (status) => {
    if (status === 'approved') return <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-400/40">{t('statusApproved')}</span>;
    if (status === 'rejected') return <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider bg-rose-500/20 text-rose-300 border border-rose-400/40">{t('statusRejected')}</span>;
    return <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/40">{t('statusPending')}</span>;
  };

  return (
    <div className={`${glass} rounded-2xl p-5`}>
      <div className="w-full flex items-center justify-between gap-2 mb-1">
        <h3 className="font-bold text-base text-white flex items-center gap-2">
          <BadgeCheck className="h-5 w-5 text-sky-400" /> {t('verificationRequestsTab')}
          {pending.length > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-500/20 text-amber-300 border border-amber-400/40">{pending.length}</span>}
        </h3>
      </div>

      <div className="mt-4">
          {loading ? (
            <div className="text-center py-8 text-slate-500">
              <div className="w-7 h-7 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin mx-auto mb-2"></div>
            </div>
          ) : loadError ? (
            <div className="text-center py-8">
              <p className="text-rose-300 text-sm mb-3">{loadError}</p>
              <button onClick={refresh} className="px-4 py-2 rounded-lg text-xs font-bold bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.14] text-slate-200 transition-all">{t('retry')}</button>
            </div>
          ) : requests.length === 0 ? (
            <p className="text-center py-8 text-slate-500 text-sm">{t('noVerificationRequests')}</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
              {requests.map(req => (
                <div key={req.id} className={`rounded-xl border p-4 ${req.status === 'pending' ? 'bg-amber-500/8 border-amber-400/25' : req.status === 'approved' ? 'bg-emerald-500/8 border-emerald-400/20' : 'bg-white/[0.05] border-white/[0.10]'}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-slate-100 font-bold text-sm">{req.fullName || '—'}</span>
                        {statusChip(req.status)}
                        {req.signupSource === 'telegram' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-sky-500/20 text-sky-300 border border-sky-400/40 flex items-center gap-1">
                            <Send className="h-2.5 w-2.5" />{t('telegramBadge')}
                          </span>
                        )}
                      </div>
                      <div className="text-slate-500 text-xs space-y-0.5">
                        {req.email && <div>{req.email}</div>}
                        {req.phone && <div>📱 {req.phone}</div>}
                        {(req.telegramId || req.country || req.passport) && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {req.telegramId && (<><Send className="h-3 w-3 text-sky-400" /><span className="font-mono text-sky-300">{req.telegramId}</span></>)}
                            {req.country && <><span>•</span><span>{req.country}</span></>}
                            {req.passport && <><span>•</span><span>{req.passport}</span></>}
                          </div>
                        )}
                        {req.additional && <div className="text-slate-500 italic mt-1">{req.additional}</div>}
                        {req.signupSource === 'telegram' && (
                          <div className="text-sky-300/80 mt-1 flex items-center gap-1.5">
                            <Send className="h-3 w-3" /> عکس پاسپورت، ویدیوی پاسپورت و ویدیوی سلفی این کاربر برای شما در تلگرام ارسال شده — همان‌جا بررسی کنید.
                          </div>
                        )}
                        {(req.passportPhotoUrl || req.passportVideoUrl || req.selfieVideoUrl) && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {[
                              { field: 'passportPhotoUrl', label: t('verificationStep1Title'), isImage: true },
                              { field: 'passportVideoUrl', label: t('verificationStep2Title'), isImage: false },
                              { field: 'selfieVideoUrl', label: t('verificationStep3Title'), isImage: false },
                            ].filter(f => req[f.field]).map(f => {
                              const url = req[f.field];
                              const busyKey = req.id + ':' + f.field;
                              const busy = fileActionLoading === busyKey;
                              return (
                                <div key={f.field} className="rounded-lg border border-white/10 bg-white/[0.04] p-2 flex items-center gap-2">
                                  <a href={url} target="_blank" rel="noopener noreferrer" title={f.label} className="flex-shrink-0">
                                    {f.isImage ? (
                                      <img src={url} alt={f.label} className="h-12 w-12 object-cover rounded-md border border-white/10 hover:border-sky-400/50 transition-all" />
                                    ) : (
                                      <div className="h-12 w-12 rounded-md border border-white/10 hover:border-sky-400/50 bg-white/[0.05] flex items-center justify-center text-sky-300 transition-all">
                                        <Video className="h-5 w-5" />
                                      </div>
                                    )}
                                  </a>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[10px] font-bold text-slate-400 max-w-[90px] truncate">{f.label}</span>
                                    <div className="flex items-center gap-1">
                                      <button type="button" onClick={() => downloadFile(url, `${f.field}-${req.id}`)} title={t('downloadFile')} className="p-1.5 rounded-md bg-sky-500/12 hover:bg-sky-500/22 border border-sky-400/30 text-sky-300 transition-all">
                                        <Download className="h-3 w-3" />
                                      </button>
                                      {adminPermissions.canDelete && (
                                        <button type="button" onClick={() => deleteFile(req, f.field)} disabled={busy} title={t('deleteFile')} className="p-1.5 rounded-md bg-rose-500/12 hover:bg-rose-500/22 border border-rose-400/30 text-rose-300 transition-all disabled:opacity-40">
                                          {busy ? <div className="w-3 h-3 border border-rose-400/50 border-t-rose-400 rounded-full animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="text-slate-500 mt-1">{t('joined')}: {fmtDate(req.createdAt)}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                      <button onClick={() => onMessage({ id: req.userId, uid: req.userId, name: req.fullName || req.email, email: req.email })} className="px-3 py-2 rounded-lg text-xs font-bold bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.14] text-slate-200 flex items-center gap-1.5 transition-all">
                        <MessageSquare className="h-3.5 w-3.5" /> {t('messageApplicant')}
                      </button>
                      {req.status === 'pending' && adminPermissions.canVerify && (
                        <>
                          <button onClick={() => approve(req)} disabled={actionLoading === req.id + ':approve'} className="px-3 py-2 rounded-lg text-xs font-bold bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/40 text-emerald-300 flex items-center gap-1.5 transition-all disabled:opacity-50">
                            {actionLoading === req.id + ':approve' ? <div className="w-3 h-3 border border-emerald-500/50 border-t-emerald-500 rounded-full animate-spin" /> : <BadgeCheck className="h-3.5 w-3.5" />}
                            {t('approveRequest')}
                          </button>
                          <button onClick={() => reject(req)} disabled={actionLoading === req.id + ':reject'} className="px-3 py-2 rounded-lg text-xs font-bold bg-rose-500/12 hover:bg-rose-500/20 border border-rose-400/40 text-rose-300 flex items-center gap-1.5 transition-all disabled:opacity-50">
                            {actionLoading === req.id + ':reject' ? <div className="w-3 h-3 border border-rose-500/50 border-t-rose-500 rounded-full animate-spin" /> : <X className="h-3.5 w-3.5" />}
                            {t('rejectRequest')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}